/*
 * GENERATED FILE — do not edit.
 *
 * Written by `npm run api:types` from docs/api/openapi.json, and checked by
 * gates/api-client-lint.ts, which regenerates it and fails on any difference. Editing it by hand
 * has the same effect as editing the OpenAPI document by hand: the gate reverts your reasoning
 * and nobody sees why.
 *
 * Source contract version: "1.0.0"
 */

/** Sets the agency's display conventions. Today: the order a date is written in. */
export interface AccessAgencyProfileUpdateResponse {
  readonly tenant: {
    readonly id: string;
    readonly date_format: "dd_mm_yyyy" | "mm_dd_yyyy" | "iso";
  };
}

export type AccessAgencyProfileUpdateRequest = {
  readonly dateFormat: "dd_mm_yyyy" | "mm_dd_yyyy" | "iso";
};

/** Creates a team, optionally underneath another. */
export interface AccessGroupCreateResponse {
  readonly groupId: string;
}

export type AccessGroupCreateRequest = {
  readonly name: string;
  readonly parentId?: string;
};

/** Every team in the agency, with who manages it and who is on it. */
export interface AccessGroupListResponse {
  readonly groups: readonly ({
    readonly id: string;
    readonly name: string;
    readonly parent_id: string | null;
    readonly parent_name: string | null;
    readonly status: "active" | "archived";
    readonly member_count: number;
    readonly managed_by_me: boolean;
    readonly mine: boolean;
    readonly created_at: string;
  })[];
}

export type AccessGroupListRequest = Record<string, never>;

/** One team and the people on it. */
export interface AccessGroupReadResponse {
  readonly group: {
    readonly id: string;
    readonly name: string;
    readonly parent_id: string | null;
    readonly parent_name: string | null;
    readonly status: "active" | "archived";
    readonly member_count: number;
    readonly managed_by_me: boolean;
    readonly mine: boolean;
    readonly created_at: string;
  };
  readonly members: readonly ({
    readonly membership_id: string;
    readonly email: string;
    readonly role_key: string;
    readonly group_role: "member" | "manager";
    readonly is_self: boolean;
  })[];
  readonly children: readonly {
    readonly id: string;
    readonly name: string;
  }[];
}

export type AccessGroupReadRequest = Record<string, never>;

/** Takes somebody off a team. */
export interface AccessGroupRemoveMemberResponse {
  readonly groupId: string;
  readonly membershipId: string;
}

export type AccessGroupRemoveMemberRequest = Record<string, never>;

/** Puts somebody on a team, or changes whether they manage it. */
export interface AccessGroupSetMemberResponse {
  readonly groupId: string;
  readonly membershipId: string;
  readonly role: "member" | "manager";
}

export type AccessGroupSetMemberRequest = {
  readonly role?: "member" | "manager";
};

/** Renames a team, moves it in the tree, or archives it. */
export interface AccessGroupUpdateResponse {
  readonly groupId: string;
}

export type AccessGroupUpdateRequest = {
  readonly name?: string;
  readonly parentId?: string | null;
  readonly status?: "active" | "archived";
};

/** The authenticated principal, their membership in this tenant, and its entitlements. */
export interface AccessMeReadResponse {
  readonly tenant: {
    readonly id: string;
    readonly name: string;
    readonly slug: string;
    readonly kind: string;
    readonly home_region: string;
    readonly default_locale: string;
    readonly default_currency: string;
    readonly default_timezone: string;
    readonly date_format: "dd_mm_yyyy" | "mm_dd_yyyy" | "iso";
  };
  readonly membership: {
    readonly id: string;
    readonly user_account_id: string;
    readonly role_key: string;
    readonly record_scope: "own" | "team" | "hiring_unit" | "all";
    readonly scope_membership_ids: readonly string[];
  };
  readonly realm: "workforce" | "partner" | "candidate";
  readonly entitlements: readonly string[];
}

export type AccessMeReadRequest = Record<string, never>;

/** Revokes a member’s access to this tenant. */
export interface AccessMemberDeactivateResponse {
  readonly membershipId: string;
  readonly status: string;
}

export type AccessMemberDeactivateRequest = Record<string, never>;

/** Invites somebody into this tenant with a role. */
export interface AccessMemberInviteResponse {
  readonly membershipId: string;
  readonly userAccountId: string;
}

export type AccessMemberInviteRequest = {
  readonly email: string;
  readonly fullName?: string;
  readonly roleKey: "admin" | "manager" | "recruiter" | "coordinator" | "sourcer";
  readonly scope?: "own" | "team" | "hiring_unit" | "all";
};

/** Everyone with a membership in this tenant. */
export interface AccessMemberListResponse {
  readonly members: readonly ({
    readonly id: string;
    readonly user_account_id: string;
    readonly email: string;
    readonly full_name: string | null;
    readonly role_key: string;
    readonly scope: "own" | "team" | "hiring_unit" | "all";
    readonly status: string;
    readonly invited_at: string;
    readonly activated_at: string | null;
    readonly is_self: boolean;
  })[];
}

export type AccessMemberListRequest = Record<string, never>;

/** Restores a revoked member’s access, at the role they held. */
export interface AccessMemberReactivateResponse {
  readonly membershipId: string;
  readonly status: string;
}

export type AccessMemberReactivateRequest = Record<string, never>;

/** Changes a member's role, and optionally their record scope. */
export interface AccessMemberUpdateRoleResponse {
  readonly membershipId: string;
  readonly roleKey: string;
  readonly scope: string;
}

export type AccessMemberUpdateRoleRequest = {
  readonly roleKey: "admin" | "manager" | "recruiter" | "coordinator" | "sourcer";
  readonly scope?: "own" | "team" | "hiring_unit" | "all";
};

/** The authenticated operator and what their role permits. */
export interface AccessPlatformOperatorReadResponse {
  readonly userAccountId: string;
  readonly email: string;
  readonly roleKey: string;
  readonly canProvision: boolean;
}

export type AccessPlatformOperatorReadRequest = Record<string, never>;

/** End an agency. Sign-in stops and the identity organisation is removed; no data is deleted. */
export interface AccessPlatformTenantCloseResponse {
  readonly tenantId: string;
  readonly status: string;
  readonly changed: boolean;
}

export type AccessPlatformTenantCloseRequest = {
  readonly reasonCode: "contract_ended" | "migrated" | "abuse" | "other";
  readonly confirmSlug: string;
};

/** Provision a new agency tenant, its entitlements, its quotas and its first admin. Send `packs`; `modules` is the previous shape, accepted for one release and then removed. */
export interface AccessPlatformTenantCreateResponse {
  readonly tenantId: string;
  readonly slug: string;
  readonly adminUserAccountId: string;
  readonly membershipId: string;
  readonly modules: readonly string[];
}

export type AccessPlatformTenantCreateRequest = {
  readonly name: string;
  readonly slug: string;
  readonly defaultLocale: string;
  readonly defaultCurrency: string;
  readonly defaultTimezone: string;
  readonly packs?: readonly string[];
  readonly modules?: readonly string[];
  readonly quotas: Readonly<Record<string, number | null>>;
  readonly adminEmail: string;
  readonly adminName?: string;
};

/** Every tenant on the platform, with its module and member counts. */
export interface AccessPlatformTenantListResponse {
  readonly tenants: readonly ({
    readonly id: string;
    readonly name: string;
    readonly slug: string;
    readonly kind: string;
    readonly status: string;
    readonly home_region: string;
    readonly default_locale: string;
    readonly default_currency: string;
    readonly default_timezone: string;
    readonly created_at: string;
    readonly term_ends_at: string | null;
    readonly is_test_data: boolean;
    readonly module_count: number;
    readonly member_count: number;
  })[];
}

export type AccessPlatformTenantListRequest = Record<string, never>;

/** Let a suspended agency back in. Refused for a closed one. */
export interface AccessPlatformTenantReactivateResponse {
  readonly tenantId: string;
  readonly status: string;
  readonly changed: boolean;
}

export type AccessPlatformTenantReactivateRequest = Record<string, never>;

/** One tenant, with its entitlements, quotas and members. */
export interface AccessPlatformTenantReadResponse {
  readonly tenant: {
    readonly id: string;
    readonly name: string;
    readonly slug: string;
    readonly kind: string;
    readonly status: string;
    readonly home_region: string;
    readonly default_locale: string;
    readonly default_currency: string;
    readonly default_timezone: string;
    readonly created_at: string;
    readonly term_ends_at: string | null;
    readonly is_test_data: boolean;
    readonly module_count: number;
    readonly member_count: number;
  };
  readonly entitlements: readonly {
    readonly module_key: string;
    readonly enabled: boolean;
  }[];
  readonly quotas: readonly ({
    readonly quota_key: string;
    readonly limit_value: number | null;
    readonly period: string;
  })[];
  readonly members: readonly {
    readonly id: string;
    readonly email: string;
    readonly role_key: string;
    readonly scope: string;
    readonly status: string;
  }[];
}

export type AccessPlatformTenantReadRequest = Record<string, never>;

/** Set the capability packs an agency has. Idempotent — send the full set, not a change to it. */
export interface AccessPlatformTenantSetEntitlementsResponse {
  readonly entitlements: readonly {
    readonly module_key: string;
    readonly enabled: boolean;
  }[];
  readonly changed: readonly {
    readonly module_key: string;
    readonly enabled: boolean;
    readonly previously_enabled: boolean;
  }[];
}

export type AccessPlatformTenantSetEntitlementsRequest = {
  readonly packs?: readonly string[];
  readonly modules?: readonly string[];
};

/** Sets an agency’s term — trial or active, and when it ends. */
export interface AccessPlatformTenantSetTermResponse {
  readonly tenantId: string;
  readonly status: string;
  readonly changed: boolean;
  readonly term_ends_at: string | null;
}

export type AccessPlatformTenantSetTermRequest = {
  readonly status: "trial" | "active";
  readonly endsAt: string | null;
};

/** Stop an agency signing in, reversibly. Nothing is deleted. */
export interface AccessPlatformTenantSuspendResponse {
  readonly tenantId: string;
  readonly status: string;
  readonly changed: boolean;
}

export type AccessPlatformTenantSuspendRequest = {
  readonly reasonCode: "billing_lapsed" | "contract_paused" | "abuse" | "other";
};

/** Forgets one preference, returning that screen to its default. */
export interface AccessPreferenceClearResponse {
  readonly key: string;
  readonly cleared: boolean;
}

export type AccessPreferenceClearRequest = Record<string, never>;

/** This person’s saved screen preferences. Never another person’s. */
export interface AccessPreferenceListResponse {
  readonly preferences: Readonly<Record<string, unknown>>;
  readonly keys: readonly string[];
}

export type AccessPreferenceListRequest = Record<string, never>;

/** Saves one of this person’s screen preferences. The key decides the shape. */
export interface AccessPreferenceSaveResponse {
  readonly key: string;
  readonly value: unknown;
}

export type AccessPreferenceSaveRequest = {
  readonly value: unknown;
};

/** Removes one of this person’s saved views. */
export interface AccessSavedViewDeleteResponse {
  readonly savedViewId: string;
}

export type AccessSavedViewDeleteRequest = Record<string, never>;

/** The views this person saved, optionally for one screen. */
export interface AccessSavedViewListResponse {
  readonly views: readonly ({
    readonly id: string;
    readonly surface: "interviews" | "jobs" | "applications" | "candidates" | "clients";
    readonly name: string;
    readonly params: Readonly<Record<string, unknown>>;
    readonly created_at: string;
    readonly updated_at: string;
  })[];
}

export type AccessSavedViewListRequest = Record<string, never>;

/** Saves a screen’s current filters under a name, replacing one of the same name. */
export interface AccessSavedViewSaveResponse {
  readonly savedViewId: string;
  readonly surface: "interviews" | "jobs" | "applications" | "candidates" | "clients";
  readonly name: string;
}

export type AccessSavedViewSaveRequest = {
  readonly surface: "interviews" | "jobs" | "applications" | "candidates" | "clients";
  readonly name: string;
  readonly params: Readonly<Record<string, string | number | boolean | readonly (string | number)[]>>;
};

/** Adds a candidate, resolving them against the shared identity index by email. */
export interface CandidatesCandidateCreateResponse {
  readonly candidateId: string;
}

export type CandidatesCandidateCreateRequest = {
  readonly fullName: string;
  readonly email?: string;
  readonly phone?: string;
  readonly headline?: string;
  readonly totalExperienceMonths?: number;
  readonly noticePeriodDays?: number;
  readonly city?: string;
  readonly stateCode?: string;
  readonly locality?: string;
  readonly currentCtc?: number;
  readonly expectedCtc?: number;
  readonly source?: "self" | "recruiter" | "application" | "referral" | "import" | "api";
};

/** What a re-parse replaced on this profile, newest first. */
export interface CandidatesCandidateHistoryResponse {
  readonly entries: readonly ({
    readonly id: string;
    readonly satellite: string;
    readonly superseded: Readonly<Record<string, unknown>>;
    readonly replaced_by_resume_id: string | null;
    readonly replaced_at: string;
  })[];
}

export type CandidatesCandidateHistoryRequest = Record<string, never>;

/** Every candidate this tenant knows, newest first. */
export interface CandidatesCandidateListResponse {
  readonly candidates: readonly ({
    readonly id: string;
    readonly full_name: string;
    readonly email: string | null;
    readonly phone: string | null;
    readonly headline: string | null;
    readonly total_experience_months: number | null;
    readonly unreachable_since: string | null;
    readonly notice_period_days: number | null;
    readonly city_name: string | null;
    readonly locality: string | null;
    readonly status: "active" | "do_not_contact" | "blacklisted";
    readonly source: "self" | "recruiter" | "application" | "referral" | "import" | "api";
    readonly rating: number | null;
    readonly owner_membership_id: string | null;
    readonly owned_by_me: boolean;
    readonly created_at: string;
    readonly last_activity_at: string | null;
    readonly top_skills: readonly string[];
    readonly skill_count: number;
  })[];
  readonly scope: "own" | "team" | "all";
  readonly nextCursor: string | null;
}

export type CandidatesCandidateListRequest = Record<string, never>;

/** The five figures the candidate home opens with. */
export interface CandidatesCandidateOverviewResponse {
  readonly total: number;
  readonly uploaded: number;
  readonly applied: number;
  readonly byHand: number;
  readonly reviewQueue: number;
  readonly reviewOldestDays: number | null;
  readonly mergeReview: number;
  readonly unreachable: number;
  readonly fresh: number;
}

export type CandidatesCandidateOverviewRequest = Record<string, never>;

/** One candidate, as this tenant knows them. */
export interface CandidatesCandidateReadResponse {
  readonly candidate: {
    readonly id: string;
    readonly full_name: string;
    readonly email: string | null;
    readonly phone: string | null;
    readonly headline: string | null;
    readonly total_experience_months: number | null;
    readonly unreachable_since: string | null;
    readonly notice_period_days: number | null;
    readonly city_name: string | null;
    readonly locality: string | null;
    readonly status: "active" | "do_not_contact" | "blacklisted";
    readonly source: "self" | "recruiter" | "application" | "referral" | "import" | "api";
    readonly rating: number | null;
    readonly owner_membership_id: string | null;
    readonly owned_by_me: boolean;
    readonly created_at: string;
    readonly last_activity_at: string | null;
    readonly tags: readonly string[];
    readonly sourced_at: string;
    readonly first_contacted_at: string | null;
    readonly updated_at: string;
    readonly current_ctc_amount: string | null;
    readonly expected_ctc_amount: string | null;
    readonly expected_ctc_max_amount: string | null;
    readonly current_ctc_fixed_amount: string | null;
    readonly current_ctc_variable_amount: string | null;
    readonly current_ctc_benefits: string | null;
    readonly ctc_currency: string | null;
    readonly available_from: string | null;
    readonly notice_negotiable_days: number | null;
    readonly last_working_day: string | null;
    readonly employment_type: string | null;
    readonly work_mode: string | null;
    readonly open_to_relocate: boolean | null;
    readonly verified_at: string | null;
  };
  readonly locationPreferences: readonly {
    readonly cityKey: string;
    readonly cityName: string;
    readonly rank: number;
  }[];
  readonly photoUrl: string | null;
  readonly signals: readonly ({
    readonly kind: string;
    readonly severity: "high" | "medium" | "low";
    readonly evidence: Readonly<Record<string, string | number>>;
  })[];
  readonly shape: {
    readonly employers: number;
    readonly averageStayMonths: number | null;
    readonly datedRoles: number;
  };
  readonly skills: readonly ({
    readonly evidence_projects: number | null;
    readonly last_used_year: number | null;
    readonly skill: string;
    readonly months: number | null;
    readonly source: string;
    readonly confidence: number | null;
  })[];
  readonly experience: readonly ({
    readonly id: string;
    readonly company: string;
    readonly title: string | null;
    readonly starts_on: string | null;
    readonly ends_on: string | null;
    readonly is_current: boolean;
    readonly source: string;
    readonly confidence: number | null;
    readonly kind: "employment" | "project";
    readonly location: string | null;
  })[];
  readonly languages: readonly ({
    readonly id: string;
    readonly name: string;
    readonly proficiency: string | null;
    readonly source: string;
    readonly confidence: number | null;
  })[];
  readonly education: readonly ({
    readonly id: string;
    readonly institution: string;
    readonly qualification: string | null;
    readonly field_of_study: string | null;
    readonly completed_year: number | null;
    readonly source: string;
    readonly confidence: number | null;
  })[];
}

export type CandidatesCandidateReadRequest = Record<string, never>;

/** Structured candidate search with facet counts. */
export interface CandidatesCandidateSearchResponse {
  readonly candidates: readonly ({
    readonly id: string;
    readonly full_name: string;
    readonly email: string | null;
    readonly headline: string | null;
    readonly total_experience_months: number | null;
    readonly notice_period_days: number | null;
    readonly status: "active" | "do_not_contact" | "blacklisted";
    readonly owner_membership_id: string | null;
    readonly owned_by_me: boolean;
    readonly skills: readonly string[];
    readonly skill_depth: readonly ({
      readonly skill: string;
      readonly months: number | null;
      readonly evidence_projects: number | null;
      readonly last_used_year: number | null;
    })[];
    readonly current_company: string | null;
    readonly current_title: string | null;
    readonly city_name: string | null;
    readonly locality: string | null;
    readonly current_ctc_amount: number | null;
    readonly expected_ctc_amount: number | null;
    readonly expected_ctc_max_amount: number | null;
    readonly phone: string | null;
    readonly verified_at: string | null;
    readonly ctc_currency: string | null;
    readonly employer_changes: number | null;
    readonly average_stay_months: number | null;
    readonly rating: number | null;
    readonly available_from: string | null;
    readonly source: string | null;
    readonly created_at: string;
    readonly score: number;
    readonly explanation: {
      readonly matched: readonly string[];
      readonly missed: readonly string[];
      readonly avoided: readonly string[];
    };
  })[];
  readonly facets: {
    readonly skills: readonly {
      readonly value: string;
      readonly count: number;
    }[];
    readonly companies: readonly {
      readonly value: string;
      readonly count: number;
    }[];
    readonly institutions: readonly {
      readonly value: string;
      readonly count: number;
    }[];
    readonly notice: readonly {
      readonly key: string;
      readonly count: number;
    }[];
    readonly experience: readonly {
      readonly key: string;
      readonly count: number;
    }[];
    readonly freshness: readonly {
      readonly key: string;
      readonly count: number;
    }[];
    readonly cities: readonly {
      readonly value: string;
      readonly count: number;
    }[];
    readonly employment: readonly {
      readonly value: string;
      readonly count: number;
    }[];
    readonly qualities: readonly {
      readonly key: string;
      readonly count: number;
    }[];
  };
  readonly cityResolved: {
    readonly asked: string;
    readonly resolved: boolean;
    readonly cityName: string | null;
    readonly stateName: string | null;
  } | null;
  readonly total: number | null;
  readonly matchedQuery: number | null;
  readonly relaxations: readonly ({
    readonly key: string;
    readonly label: string;
    readonly kind: "query" | "filter";
    readonly wouldReturn: number;
  })[];
  readonly didYouMean: readonly ({
    readonly field: "skills" | "q";
    readonly asked: string;
    readonly suggest: string;
    readonly wouldReturn: number;
  })[];
  readonly searchId: string | null;
  readonly nextCursor: string | null;
}

export type CandidatesCandidateSearchRequest = Record<string, never>;

/** Stores a photograph of a candidate. Base64 through the API, never a presigned PUT. */
export interface CandidatesCandidateSetPhotoResponse {
  readonly photoUrl: string;
  readonly byteSize: number;
}

export type CandidatesCandidateSetPhotoRequest = {
  readonly mimeType: "image/jpeg" | "image/png" | "image/webp";
  readonly content: string;
};

/** Records that a person has stopped answering, or that they are answering again. */
export interface CandidatesCandidateSetReachabilityResponse {
  readonly candidateId: string;
  readonly unreachable_since: string | null;
}

export type CandidatesCandidateSetReachabilityRequest = {
  readonly unreachable: boolean;
};

/** Records what a candidate said about their own money, notice, reach and preferences — §4.3a’s capture requirement. Corrects the person; never touches identity resolution or history. */
export interface CandidatesCandidateUpdateResponse {
  readonly candidateId: string;
  readonly preferredCities: readonly string[];
}

export type CandidatesCandidateUpdateRequest = {
  readonly currentCtc?: number | null;
  readonly expectedCtc?: number | null;
  readonly expectedCtcMax?: number | null;
  readonly noticePeriodDays?: number | null;
  readonly availableFrom?: string | null;
  readonly currentCtcFixed?: number | null;
  readonly currentCtcVariable?: number | null;
  readonly currentCtcBenefits?: string | null;
  readonly noticeNegotiableDays?: number | null;
  readonly lastWorkingDay?: string | null;
  readonly employmentType?: "full_time" | "part_time" | "contract" | "contract_to_hire" | "freelance" | "internship" | null;
  readonly workMode?: "onsite" | "hybrid" | "remote" | null;
  readonly openToRelocate?: boolean | null;
  readonly preferredCities?: readonly string[];
  readonly email?: string | null;
  readonly phone?: string | null;
  readonly location?: {
    readonly city: string;
    readonly stateCode?: string | null;
    readonly locality?: string | null;
  } | null;
};

/** Moves a candidate between active, do-not-contact and blacklisted. */
export interface CandidatesCandidateUpdateStatusResponse {
  readonly candidateId: string;
  readonly status: "active" | "do_not_contact" | "blacklisted";
}

export type CandidatesCandidateUpdateStatusRequest = {
  readonly status: "active" | "do_not_contact" | "blacklisted";
};

/** Answers a question about one candidate, from what is recorded against them. */
export interface CandidatesCopilotAskResponse {
  readonly decisionId: string;
  readonly reply: string;
  readonly citations: readonly string[];
  readonly refused: boolean;
  readonly ai_sample: boolean;
  readonly context: {
    readonly kind: "job" | "candidate" | "application" | "desk";
    readonly label: string;
    readonly facts: readonly {
      readonly label: string;
      readonly value: string;
    }[];
  };
}

export type CandidatesCopilotAskRequest = {
  readonly question: string;
};

/** What this agency has earned by contributing people who confirmed their own profile. */
export interface CandidatesCreditBalanceResponse {
  readonly balance: number;
  readonly contributions: number;
}

export type CandidatesCreditBalanceRequest = Record<string, never>;

/** The identity numbers held for a candidate, masked. */
export interface CandidatesIdentityNumberReadResponse {
  readonly numbers: readonly ({
    readonly kind: "pan" | "aadhaar" | "uan" | "passport" | "driving_licence";
    readonly masked: string;
    readonly source: "candidate_supplied" | "recruiter_entered";
    readonly collected_at: string;
  })[];
  readonly may_reveal: boolean;
}

export type CandidatesIdentityNumberReadRequest = Record<string, never>;

/** Reveals a sealed identity number, recording who looked and why. */
export interface CandidatesIdentityNumberRevealResponse {
  readonly kind: "pan" | "aadhaar" | "uan" | "passport" | "driving_licence";
  readonly value: string;
  readonly masked: string;
}

export type CandidatesIdentityNumberRevealRequest = {
  readonly reason: string;
};

/** Stores a candidate identity number, sealed, and returns only its masked form. */
export interface CandidatesIdentityNumberSaveResponse {
  readonly number: {
    readonly kind: "pan" | "aadhaar" | "uan" | "passport" | "driving_licence";
    readonly masked: string;
    readonly source: "candidate_supplied" | "recruiter_entered";
    readonly collected_at: string;
  };
}

export type CandidatesIdentityNumberSaveRequest = {
  readonly value: string;
  readonly source?: "candidate_supplied" | "recruiter_entered";
};

/** Who revealed this candidate’s identity numbers, and the reason they gave. */
export interface CandidatesIdentityRevealReadResponse {
  readonly reveals: readonly ({
    readonly kind: "pan" | "aadhaar" | "uan" | "passport" | "driving_licence";
    readonly membership_id: string;
    readonly reason: string;
    readonly revealed_at: string;
  })[];
}

export type CandidatesIdentityRevealReadRequest = Record<string, never>;

/** Asks the assistant to write about this candidate. Returns immediately; the run polls. */
export interface CandidatesIntelligenceGenerateResponse {
  readonly artifacts: readonly ({
    readonly kind: "summary" | "role_fit";
    readonly state: "absent" | "current" | "stale";
    readonly content: unknown | null;
    readonly generated_by_sample: boolean | null;
    readonly ai_decision_id: string | null;
    readonly generated_at: string | null;
    readonly run: {
      readonly state: string;
      readonly failure_code: string | null;
      readonly failure_message: string | null;
    } | null;
  })[];
}

export type CandidatesIntelligenceGenerateRequest = Record<string, never>;

/** What a model has written about this candidate, and whether it is still current. */
export interface CandidatesIntelligenceReadResponse {
  readonly artifacts: readonly ({
    readonly kind: "summary" | "role_fit";
    readonly state: "absent" | "current" | "stale";
    readonly content: unknown | null;
    readonly generated_by_sample: boolean | null;
    readonly ai_decision_id: string | null;
    readonly generated_at: string | null;
    readonly run: {
      readonly state: string;
      readonly failure_code: string | null;
      readonly failure_message: string | null;
    } | null;
  })[];
}

export type CandidatesIntelligenceReadRequest = Record<string, never>;

/** Applies a pass over the queue — each pair judged on its own, all applied together. */
export interface CandidatesMergeReviewDecideManyResponse {
  readonly merged: number;
  readonly separated: number;
}

export type CandidatesMergeReviewDecideManyRequest = {
  readonly decisions: readonly ({
    readonly reviewId: string;
    readonly answer: "same" | "different";
  })[];
};

/** Pairs of records this agency’s documents suggested might be one person. */
export interface CandidatesMergeReviewListResponse {
  readonly pairs: readonly ({
    readonly id: string;
    readonly leftIdentityId: string;
    readonly rightIdentityId: string;
    readonly leftName: string | null;
    readonly rightName: string | null;
    readonly score: number;
    readonly evidence: Readonly<Record<string, unknown>>;
    readonly leftDocument: {
      readonly id: string;
      readonly filename: string;
      readonly door: string;
      readonly uploadedAt: string;
    } | null;
    readonly rightDocument: {
      readonly id: string;
      readonly filename: string;
      readonly door: string;
      readonly uploadedAt: string;
    } | null;
    readonly createdAt: string;
  })[];
}

export type CandidatesMergeReviewListRequest = Record<string, never>;

/** Confirms two records are one person, and folds one into the other. */
export interface CandidatesMergeReviewMergeResponse {
  readonly survivingIdentityId: string;
  readonly repointedProfiles: number;
  readonly retiredProfiles: number;
}

export type CandidatesMergeReviewMergeRequest = Record<string, never>;

/** Records that two records are two different people. */
export interface CandidatesMergeReviewRejectResponse {
  readonly reviewId: string;
}

export type CandidatesMergeReviewRejectRequest = Record<string, never>;

/** Writes a note against a person, or against one pursuit of them. */
export interface CandidatesNoteAddResponse {
  readonly noteId: string;
  readonly visibility: "internal" | "hiring_unit";
}

export type CandidatesNoteAddRequest = {
  readonly applicationId?: string;
  readonly note_type?: "general" | "screening" | "submission" | "interview" | "reference" | "internal" | "call" | "email" | "client_feedback";
  readonly body_md: string;
  readonly visibility?: "internal" | "hiring_unit";
  readonly mentions?: readonly string[];
};

/** Removes the author’s own note from every screen. */
export interface CandidatesNoteDeleteResponse {
  readonly noteId: string;
}

export type CandidatesNoteDeleteRequest = Record<string, never>;

/** What this agency has written about a person. */
export interface CandidatesNoteListResponse {
  readonly notes: readonly ({
    readonly id: string;
    readonly candidate_profile_id: string;
    readonly application_id: string | null;
    readonly author_membership_id: string;
    readonly note_type: "general" | "screening" | "submission" | "interview" | "reference" | "internal" | "call" | "email" | "client_feedback";
    readonly body_md: string;
    readonly visibility: "internal" | "hiring_unit";
    readonly created_at: string;
    readonly updated_at: string;
    readonly mentions: readonly string[];
  })[];
}

export type CandidatesNoteListRequest = Record<string, never>;

/** Edits the author’s own note, changes who may see it, or moves it to the person. */
export interface CandidatesNoteUpdateResponse {
  readonly noteId: string;
  readonly visibility: "internal" | "hiring_unit";
}

export type CandidatesNoteUpdateRequest = {
  readonly body_md?: string;
  readonly visibility?: "internal" | "hiring_unit";
  readonly promote?: true;
};

/** Parse-review’s save — turns a reviewed document into a candidate. */
export interface CandidatesResumeAcceptResponse {
  readonly candidateId: string;
  readonly isMaster: boolean;
  readonly added: {
    readonly skills: number;
    readonly experience: number;
    readonly education: number;
    readonly projects: number;
  };
}

export type CandidatesResumeAcceptRequest = {
  readonly fullName: string;
  readonly email?: string;
  readonly phone?: string;
  readonly headline?: string;
  readonly totalExperienceMonths?: number;
  readonly noticePeriodDays?: number;
  readonly city?: string;
  readonly stateCode?: string;
  readonly locality?: string;
  readonly source?: "self" | "recruiter" | "application" | "referral" | "import" | "api";
  readonly skills?: readonly ({
    readonly skill: string;
    readonly months?: number | null;
    readonly skillType?: "specialized" | "common" | "certification" | null;
    readonly domain?: string | null;
    readonly confidence?: number | null;
    readonly source?: "resume" | "recruiter" | "self" | "import";
  })[];
  readonly experience?: readonly ({
    readonly company: string;
    readonly title?: string | null;
    readonly startsOn?: string | null;
    readonly endsOn?: string | null;
    readonly isCurrent?: boolean;
    readonly confidence?: number | null;
    readonly source?: "resume" | "recruiter" | "self" | "import";
    readonly kind?: "employment" | "project";
    readonly client?: string | null;
    readonly projectName?: string | null;
    readonly location?: string | null;
  })[];
  readonly education?: readonly ({
    readonly institution: string;
    readonly qualification?: string | null;
    readonly fieldOfStudy?: string | null;
    readonly grade?: string | null;
    readonly completedYear?: number | null;
    readonly confidence?: number | null;
    readonly source?: "resume" | "recruiter" | "self" | "import";
  })[];
  readonly languages?: readonly ({
    readonly name: string;
    readonly proficiency?: string | null;
    readonly confidence?: number | null;
    readonly source?: "resume" | "recruiter" | "self" | "import";
  })[];
};

/** Whether this agency already holds the person a document is about. */
export interface CandidatesResumeCheckDuplicateResponse {
  readonly matches: readonly {
    readonly candidateId: string;
    readonly fullName: string;
    readonly sourcedAt: string;
  }[];
}

export type CandidatesResumeCheckDuplicateRequest = Record<string, never>;

/** Which of these documents this agency already holds, by content hash. */
export interface CandidatesResumeCheckHeldResponse {
  readonly held: readonly string[];
  readonly holders: readonly ({
    readonly hash: string;
    readonly parseStatus: "pending" | "parsed" | "failed" | "discarded";
    readonly candidate: {
      readonly id: string;
      readonly fullName: string;
    } | null;
  })[];
}

export type CandidatesResumeCheckHeldRequest = Record<string, never>;

/** Takes a resume out of the review queue without creating a candidate. */
export interface CandidatesResumeDiscardResponse {
  readonly resumeId: string;
  readonly parseStatus: "pending" | "parsed" | "failed" | "discarded";
}

export type CandidatesResumeDiscardRequest = Record<string, never>;

/** The upload batch — what has parsed, what failed, and what is still waiting. */
export interface CandidatesResumeListResponse {
  readonly resumes: readonly ({
    readonly id: string;
    readonly filename: string;
    readonly mime_type: string;
    readonly byte_size: number;
    readonly parse_status: "pending" | "parsed" | "failed" | "discarded";
    readonly identity_id: string | null;
    readonly accepted_at: string | null;
    readonly uploaded_by_membership_id: string | null;
    readonly uploaded_by_me: boolean;
    readonly created_at: string;
  })[];
  readonly scope: "own" | "team" | "all";
  readonly nextCursor: string | null;
}

export type CandidatesResumeListRequest = Record<string, never>;

/** One resume, with a short-lived URL for rendering the original document. */
export interface CandidatesResumeReadResponse {
  readonly resume: {
    readonly id: string;
    readonly filename: string;
    readonly mime_type: string;
    readonly byte_size: number;
    readonly parse_status: "pending" | "parsed" | "failed" | "discarded";
    readonly identity_id: string | null;
    readonly accepted_at: string | null;
    readonly uploaded_by_membership_id: string | null;
    readonly uploaded_by_me: boolean;
    readonly created_at: string;
  };
  readonly documentUrl: string;
  readonly documentMetadata: Readonly<Record<string, unknown>>;
  readonly provenance: {
    readonly door: string;
    readonly consentBasis: string | null;
  };
}

export type CandidatesResumeReadRequest = Record<string, never>;

/** Stores one resume and queues it for parsing. */
export interface CandidatesResumeUploadResponse {
  readonly resumeId: string;
  readonly byteSize: number;
  readonly parseStatus: "pending" | "parsed" | "failed" | "discarded";
  readonly outcome: "stored" | "already_held";
  readonly candidate: {
    readonly id: string;
    readonly fullName: string;
  } | null;
}

export type CandidatesResumeUploadRequest = {
  readonly filename: string;
  readonly mimeType: "application/pdf" | "application/vnd.openxmlformats-officedocument.wordprocessingml.document" | "application/msword" | "text/plain";
  readonly content: string;
  readonly batchId?: string;
};

/** Removes one of this recruiter’s saved searches. */
export interface CandidatesSavedSearchDeleteResponse {
  readonly savedSearchId: string;
}

export type CandidatesSavedSearchDeleteRequest = Record<string, never>;

/** The searches this recruiter saved. */
export interface CandidatesSavedSearchListResponse {
  readonly searches: readonly {
    readonly id: string;
    readonly name: string;
    readonly filters: Readonly<Record<string, unknown>>;
    readonly created_at: string;
    readonly updated_at: string;
  }[];
}

export type CandidatesSavedSearchListRequest = Record<string, never>;

/** Saves a set of search filters under a name, replacing one of the same name. */
export interface CandidatesSavedSearchSaveResponse {
  readonly savedSearchId: string;
  readonly name: string;
}

export type CandidatesSavedSearchSaveRequest = {
  readonly name: string;
  readonly filters: {
    readonly q?: string;
    readonly skills?: string;
    readonly should?: string;
    readonly skillMatch?: "all" | "any";
    readonly minExperienceYears?: number;
    readonly maxExperienceYears?: number;
    readonly companies?: string;
    readonly institutions?: string;
    readonly noticeMaxDays?: number;
    readonly city?: string;
    readonly excludeCompanies?: string;
    readonly excludeWords?: string;
    readonly exclusionMode?: "remove" | "demote";
    readonly currentCtcMin?: number;
    readonly currentCtcMax?: number;
    readonly expectedCtcMax?: number;
    readonly maxEmployerChanges?: number;
    readonly minAverageStayMonths?: number;
    readonly employmentType?: "full_time" | "part_time" | "contract" | "contract_to_hire" | "freelance" | "internship";
    readonly mobileOnly?: boolean;
    readonly evidencedOnly?: boolean;
    readonly usedRecently?: boolean;
    readonly status?: "active" | "do_not_contact" | "blacklisted";
  };
};

/** Record which candidates a search actually put in front of a recruiter. */
export interface CandidatesSearchLogImpressionsResponse {
  readonly recorded: number;
}

export type CandidatesSearchLogImpressionsRequest = {
  readonly searchId: string;
  readonly results: readonly {
    readonly candidateId: string;
    readonly position: number;
  }[];
};

/** Filter values matching what somebody is typing, from the catalogue and their own data. */
export interface CandidatesSearchSuggestResponse {
  readonly suggestions: readonly ({
    readonly value: string;
    readonly matched: string | null;
  })[];
}

export type CandidatesSearchSuggestRequest = Record<string, never>;

/** Puts a candidate in a pool, or updates why they are in it. */
export interface CandidatesTalentPoolAddMemberResponse {
  readonly added: boolean;
}

export type CandidatesTalentPoolAddMemberRequest = {
  readonly note?: string;
};

/** Archives a pool, or restores an archived one. */
export interface CandidatesTalentPoolArchiveResponse {
  readonly archived: boolean;
}

export type CandidatesTalentPoolArchiveRequest = {
  readonly archived: boolean;
};

/** Creates a talent pool. */
export interface CandidatesTalentPoolCreateResponse {
  readonly poolId: string;
  readonly name: string;
}

export type CandidatesTalentPoolCreateRequest = {
  readonly name: string;
  readonly description?: string;
};

/** The agency’s talent pools, newest first, with how many people are in each. */
export interface CandidatesTalentPoolListResponse {
  readonly pools: readonly ({
    readonly id: string;
    readonly name: string;
    readonly description: string | null;
    readonly archived: boolean;
    readonly memberCount: number;
    readonly visibility: "private" | "shared" | "agency";
    readonly ownerMembershipId: string | null;
    readonly mine: boolean;
    readonly mayEdit: boolean;
    readonly sharedWith: number;
    readonly sharedWithMemberships: readonly string[];
    readonly created_at: string;
  })[];
}

export type CandidatesTalentPoolListRequest = Record<string, never>;

/** One talent pool and the people on it. */
export interface CandidatesTalentPoolReadResponse {
  readonly pool: {
    readonly id: string;
    readonly name: string;
    readonly description: string | null;
    readonly archived: boolean;
    readonly memberCount: number;
    readonly visibility: "private" | "shared" | "agency";
    readonly ownerMembershipId: string | null;
    readonly mine: boolean;
    readonly mayEdit: boolean;
    readonly sharedWith: number;
    readonly sharedWithMemberships: readonly string[];
    readonly created_at: string;
  };
  readonly members: readonly ({
    readonly candidateProfileId: string;
    readonly fullName: string;
    readonly headline: string | null;
    readonly totalExperienceMonths: number | null;
    readonly status: string;
    readonly note: string | null;
    readonly addedAt: string;
    readonly addedByMembershipId: string | null;
  })[];
}

export type CandidatesTalentPoolReadRequest = Record<string, never>;

/** Takes a candidate off a pool. */
export interface CandidatesTalentPoolRemoveMemberResponse {
  readonly removed: boolean;
}

export type CandidatesTalentPoolRemoveMemberRequest = Record<string, never>;

/** Sets who a pool is shared with, replacing what was there. */
export interface CandidatesTalentPoolShareResponse {
  readonly visibility: "private" | "shared" | "agency";
  readonly sharedWith: number;
}

export type CandidatesTalentPoolShareRequest = {
  readonly visibility: "private" | "shared" | "agency";
  readonly grants?: readonly {
    readonly membershipId: string;
    readonly mayEdit: boolean;
  }[];
};

/** Who this pool is shared with. */
export interface CandidatesTalentPoolSharingResponse {
  readonly visibility: "private" | "shared" | "agency";
  readonly grants: readonly {
    readonly membershipId: string;
    readonly mayEdit: boolean;
  }[];
}

export type CandidatesTalentPoolSharingRequest = Record<string, never>;

/** Puts a team member on a client, or changes what they do on it. */
export interface ClientsClientAssignResponse {
  readonly clientId: string;
  readonly membershipId: string;
  readonly assignmentRole: "lead" | "recruiter" | "coordinator" | "sourcer";
}

export type ClientsClientAssignRequest = {
  readonly membershipId: string;
  readonly assignmentRole?: "lead" | "recruiter" | "coordinator" | "sourcer";
};

/** Adds a client, optionally with its first contact. */
export interface ClientsClientCreateResponse {
  readonly clientId: string;
  readonly contactId: string | null;
}

export type ClientsClientCreateRequest = {
  readonly name: string;
  readonly status?: "prospect" | "active" | "dormant" | "closed";
  readonly ownerMembershipId?: string;
  readonly primaryContact?: {
    readonly fullName: string;
    readonly email?: string;
    readonly phone?: string;
    readonly phoneCountry?: string;
    readonly title?: string;
    readonly contactRole?: "hiring_manager" | "hr" | "finance" | "panel" | "executive" | "accounts" | "escalation" | "signatory";
  };
};

/** Every client in this tenant, newest first, with a contact count. */
export interface ClientsClientListResponse {
  readonly clients: readonly ({
    readonly id: string;
    readonly name: string;
    readonly status: "prospect" | "active" | "dormant" | "closed";
    readonly owner_membership_id: string | null;
    readonly owned_by_me: boolean;
    readonly contact_count: number;
    readonly created_at: string;
    readonly updated_at: string;
  })[];
  readonly nextCursor: string | null;
}

export type ClientsClientListRequest = Record<string, never>;

/** The four figures the client home opens with. */
export interface ClientsClientOverviewResponse {
  readonly total: number;
  readonly active: number;
  readonly prospect: number;
  readonly dormant: number;
  readonly closed: number;
  readonly endingSoon: number;
  readonly endingSoonNearestDays: number | null;
  readonly noLiveWork: number;
  readonly added: number;
}

export type ClientsClientOverviewRequest = Record<string, never>;

/** One client, with its contacts. */
export interface ClientsClientReadResponse {
  readonly client: {
    readonly id: string;
    readonly name: string;
    readonly status: "prospect" | "active" | "dormant" | "closed";
    readonly owner_membership_id: string | null;
    readonly owned_by_me: boolean;
    readonly contact_count: number;
    readonly created_at: string;
    readonly updated_at: string;
    readonly kind: string;
    readonly default_locale: string;
    readonly company_id: string | null;
    readonly preferred_meeting_service: "google_meet" | "teams" | "other" | null;
  };
  readonly contacts: readonly ({
    readonly id: string;
    readonly full_name: string;
    readonly email: string | null;
    readonly phone: string | null;
    readonly phone_country: string | null;
    readonly title: string | null;
    readonly contact_role: "hiring_manager" | "hr" | "finance" | "panel" | "executive" | "accounts" | "escalation" | "signatory";
    readonly is_primary: boolean;
  })[];
  readonly assignments: readonly ({
    readonly membership_id: string;
    readonly assignment_role: "lead" | "recruiter" | "coordinator" | "sourcer";
    readonly assigned_at: string;
  })[];
}

export type ClientsClientReadRequest = Record<string, never>;

/** Sets the meeting service offered by default when scheduling for this client. */
export interface ClientsClientSetMeetingServiceResponse {
  readonly clientId: string;
  readonly preferredMeetingService: "google_meet" | "teams" | "other" | null;
}

export type ClientsClientSetMeetingServiceRequest = {
  readonly preferredMeetingService: "google_meet" | "teams" | "other" | null;
};

/** Hands the client relationship to a member of the team. */
export interface ClientsClientSetOwnerResponse {
  readonly clientId: string;
  readonly ownerMembershipId: string;
}

export type ClientsClientSetOwnerRequest = {
  readonly ownerMembershipId: string;
};

/** Takes a team member off a client. */
export interface ClientsClientUnassignResponse {
  readonly clientId: string;
  readonly membershipId: string;
  readonly removed: boolean;
}

export type ClientsClientUnassignRequest = Record<string, never>;

/** Moves a client between prospect, active, dormant and closed. */
export interface ClientsClientUpdateStatusResponse {
  readonly clientId: string;
  readonly status: "prospect" | "active" | "dormant" | "closed";
}

export type ClientsClientUpdateStatusRequest = {
  readonly status: "prospect" | "active" | "dormant" | "closed";
};

/** Adds one of the agency’s words over a core closure reason. */
export interface ClientsClosureLabelCreateResponse {
  readonly id: string;
  readonly coreReason: "filled" | "cancelled_by_client" | "lost" | "expired";
  readonly label: string;
  readonly displayOrder: number;
  readonly active: boolean;
}

export type ClientsClosureLabelCreateRequest = {
  readonly coreReason: "filled" | "cancelled_by_client" | "lost" | "expired";
  readonly label: string;
  readonly displayOrder?: number;
};

/** The agency’s words for the four core closure reasons. */
export interface ClientsClosureLabelReadResponse {
  readonly labels: readonly ({
    readonly id: string;
    readonly coreReason: "filled" | "cancelled_by_client" | "lost" | "expired";
    readonly label: string;
    readonly displayOrder: number;
    readonly active: boolean;
  })[];
}

export type ClientsClosureLabelReadRequest = Record<string, never>;

/** Renames, reorders or retires one of the agency’s closure labels. */
export interface ClientsClosureLabelUpdateResponse {
  readonly id: string;
  readonly coreReason: "filled" | "cancelled_by_client" | "lost" | "expired";
  readonly label: string;
  readonly displayOrder: number;
  readonly active: boolean;
}

export type ClientsClosureLabelUpdateRequest = {
  readonly label?: string;
  readonly displayOrder?: number;
  readonly active?: boolean;
};

/** The engagement types this client has enabled, and the rate cards under each. */
export interface ClientsCommercialsReadResponse {
  readonly terms: readonly ({
    readonly employment_type: "full_time" | "contract" | "contract_to_hire" | "internship" | "consultant";
    readonly enabled: boolean;
    readonly payment_terms_days: number | null;
    readonly guarantee_days: number | null;
    readonly card_count: number;
  })[];
  readonly cards: readonly ({
    readonly id: string;
    readonly hiring_unit_id: string;
    readonly employment_type: "full_time" | "contract" | "contract_to_hire" | "internship" | "consultant";
    readonly name: string;
    readonly fee_percent: string | null;
    readonly fee_amount: string | null;
    readonly bill_rate: string | null;
    readonly rate_unit: "hourly" | "daily" | "monthly" | "annual" | null;
    readonly margin_floor_percent: string | null;
    readonly currency: string;
    readonly effective_from: string;
    readonly effective_to: string | null;
    readonly active: boolean;
    readonly amendments: readonly ({
      readonly id: string;
      readonly kind: "created" | "corrected" | "retired";
      readonly field: string;
      readonly from_value: string | null;
      readonly to_value: string | null;
      readonly note: string | null;
      readonly actor_membership_id: string | null;
      readonly occurred_at: string;
    })[];
    readonly created_at: string;
  })[];
  readonly is_self: boolean;
}

export type ClientsCommercialsReadRequest = Record<string, never>;

/** Adds a hiring manager, panellist or escalation contact to a client. */
export interface ClientsContactCreateResponse {
  readonly contactId: string;
}

export type ClientsContactCreateRequest = {
  readonly fullName: string;
  readonly email?: string;
  readonly phone?: string;
  readonly phoneCountry?: string;
  readonly title?: string;
  readonly contactRole: "hiring_manager" | "hr" | "finance" | "panel" | "executive" | "accounts" | "escalation" | "signatory";
};

/** Takes a delivery contact off a client. */
export interface ClientsContactRemoveResponse {
  readonly contactId: string;
  readonly removed: boolean;
}

export type ClientsContactRemoveRequest = Record<string, never>;

/** Names the one contact who speaks for this client. */
export interface ClientsContactSetPrimaryResponse {
  readonly clientId: string;
  readonly contactId: string;
}

export type ClientsContactSetPrimaryRequest = Record<string, never>;

/** Corrects a delivery contact’s name, title or details. */
export interface ClientsContactUpdateResponse {
  readonly contactId: string;
}

export type ClientsContactUpdateRequest = {
  readonly fullName: string;
  readonly email?: string;
  readonly phone?: string;
  readonly phoneCountry?: string;
  readonly title?: string;
  readonly contactRole: "hiring_manager" | "hr" | "finance" | "panel" | "executive" | "accounts" | "escalation" | "signatory";
};

/** Corrects a figure typed wrong, keeping a record of what it used to say. */
export interface ClientsContractCorrectResponse {
  readonly contractId: string;
  readonly corrected: number;
}

export type ClientsContractCorrectRequest = {
  readonly fee_basis?: "percentage" | "flat" | "hourly_markup";
  readonly fee_value?: string;
  readonly currency?: string;
  readonly payment_terms_days?: number | null;
  readonly guarantee_period_days?: number | null;
  readonly candidate_ownership_days?: number;
  readonly starts_on?: string | null;
  readonly ends_on?: string | null;
  readonly duration_value?: number | null;
  readonly duration_unit?: "months" | "years" | null;
  readonly notes?: string | null;
  readonly reason?: string;
};

/** Records the terms agreed with a client, closing the previous terms the day before. */
export interface ClientsContractCreateResponse {
  readonly contractId: string;
}

export type ClientsContractCreateRequest = {
  readonly fee_basis: "percentage" | "flat" | "hourly_markup";
  readonly fee_value: string;
  readonly currency: string;
  readonly payment_terms_days?: number;
  readonly guarantee_period_days?: number;
  readonly candidate_ownership_days?: number;
  readonly starts_on?: string;
  readonly ends_on?: string;
  readonly duration_value?: number;
  readonly duration_unit?: "months" | "years";
  readonly notes?: string;
};

/** The commercial terms agreed with this client, newest first. */
export interface ClientsContractListResponse {
  readonly contracts: readonly ({
    readonly id: string;
    readonly hiring_unit_id: string;
    readonly fee_basis: "percentage" | "flat" | "hourly_markup";
    readonly fee_value: string;
    readonly currency: string;
    readonly payment_terms_days: number | null;
    readonly guarantee_period_days: number | null;
    readonly candidate_ownership_days: number;
    readonly starts_on: string | null;
    readonly ends_on: string | null;
    readonly duration_value: number | null;
    readonly duration_unit: "months" | "years" | null;
    readonly ends_on_source: "calculated" | "manual" | "superseded";
    readonly notes: string | null;
    readonly is_current: boolean;
    readonly status: "scheduled" | "in_force" | "superseded" | "expired";
    readonly amendments: readonly ({
      readonly id: string;
      readonly kind: "created" | "corrected" | "superseded";
      readonly field: string;
      readonly from_value: string | null;
      readonly to_value: string | null;
      readonly successor_contract_id: string | null;
      readonly note: string | null;
      readonly actor_membership_id: string | null;
      readonly occurred_at: string;
    })[];
    readonly created_at: string;
  })[];
  readonly slas: readonly ({
    readonly id: string;
    readonly hiring_unit_id: string;
    readonly sla_type: "submission" | "feedback" | "scheduling" | "offer";
    readonly target_hours: number;
    readonly active: boolean;
  })[];
}

export type ClientsContractListRequest = Record<string, never>;

/** Answers a question about one requisition, from what is recorded against it. */
export interface ClientsCopilotAskResponse {
  readonly decisionId: string;
  readonly reply: string;
  readonly citations: readonly string[];
  readonly refused: boolean;
  readonly ai_sample: boolean;
  readonly context: {
    readonly kind: "job" | "candidate" | "application" | "desk";
    readonly label: string;
    readonly facts: readonly {
      readonly label: string;
      readonly value: string;
    }[];
  };
}

export type ClientsCopilotAskRequest = {
  readonly question: string;
};

/** What it costs this agency to keep one consultant on a client’s site. */
export interface ClientsCostTemplateListResponse {
  readonly templates: readonly ({
    readonly id: string;
    readonly name: string;
    readonly monthly_operating_cost: string;
    readonly capex_amount: string;
    readonly operating_cost_percent: string;
    readonly margin_floor_percent: string | null;
    readonly currency: string;
    readonly is_default: boolean;
    readonly components: readonly ({
      readonly id: string;
      readonly label: string;
      readonly basis: "absolute" | "percent_of_pay";
      readonly amount: string | null;
      readonly percent: string | null;
      readonly recurrence: "monthly" | "one_off";
      readonly display_order: number;
      readonly active: boolean;
    })[];
  })[];
}

export type ClientsCostTemplateListRequest = Record<string, never>;

/** Records or amends a cost template, and which one is the default. */
export interface ClientsCostTemplateSaveResponse {
  readonly id: string;
}

export type ClientsCostTemplateSaveRequest = {
  readonly id?: string;
  readonly name: string;
  readonly currency: string;
  readonly isDefault?: boolean;
  readonly marginFloorPercent?: string | null;
  readonly components?: readonly ({
    readonly label: string;
    readonly basis: "absolute" | "percent_of_pay";
    readonly amount?: string;
    readonly percent?: string;
    readonly recurrence: "monthly" | "one_off";
    readonly active?: boolean;
  })[];
};

/** Adds an HR, finance, accounts or signatory contact to a client. */
export interface ClientsEngagementContactCreateResponse {
  readonly contactId: string;
}

export type ClientsEngagementContactCreateRequest = {
  readonly fullName: string;
  readonly email?: string;
  readonly phone?: string;
  readonly phoneCountry?: string;
  readonly title?: string;
  readonly contactRole: "hiring_manager" | "hr" | "finance" | "panel" | "executive" | "accounts" | "escalation" | "signatory";
};

/** The commercial counterparts at this client — HR, finance, signatory. */
export interface ClientsEngagementContactReadResponse {
  readonly contacts: readonly ({
    readonly id: string;
    readonly full_name: string;
    readonly email: string | null;
    readonly phone: string | null;
    readonly phone_country: string | null;
    readonly title: string | null;
    readonly contact_role: "hiring_manager" | "hr" | "finance" | "panel" | "executive" | "accounts" | "escalation" | "signatory";
    readonly is_primary: boolean;
  })[];
  readonly contact_count: number;
}

export type ClientsEngagementContactReadRequest = Record<string, never>;

/** Takes an engagement contact off a client. */
export interface ClientsEngagementContactRemoveResponse {
  readonly contactId: string;
  readonly removed: boolean;
}

export type ClientsEngagementContactRemoveRequest = Record<string, never>;

/** Corrects an engagement contact’s name, title or details. */
export interface ClientsEngagementContactUpdateResponse {
  readonly contactId: string;
}

export type ClientsEngagementContactUpdateRequest = {
  readonly fullName: string;
  readonly email?: string;
  readonly phone?: string;
  readonly phoneCountry?: string;
  readonly title?: string;
  readonly contactRole: "hiring_manager" | "hr" | "finance" | "panel" | "executive" | "accounts" | "escalation" | "signatory";
};

/** Enables or disables an engagement type for a client, with optional term overrides. */
export interface ClientsEngagementTermsSetResponse {
  readonly employment_type: "full_time" | "contract" | "contract_to_hire" | "internship" | "consultant";
  readonly enabled: boolean;
  readonly open_jobs: number;
}

export type ClientsEngagementTermsSetRequest = {
  readonly enabled: boolean;
  readonly payment_terms_days?: number | null;
  readonly guarantee_days?: number | null;
};

/** Which engagement types this client is hired for. No rates. */
export interface ClientsEngagementTypesListResponse {
  readonly enabled: readonly ("full_time" | "contract" | "contract_to_hire" | "internship" | "consultant")[];
  readonly is_self: boolean;
  readonly unconfigured: boolean;
}

export type ClientsEngagementTypesListRequest = Record<string, never>;

/** The markets the platform operates in, and their states. */
export interface ClientsGeographyReadResponse {
  readonly countries: readonly {
    readonly iso2: string;
    readonly name: string;
    readonly states: readonly {
      readonly code: string;
      readonly name: string;
    }[];
  }[];
}

export type ClientsGeographyReadRequest = Record<string, never>;

/** Cities matching what somebody is typing, best match first. */
export interface ClientsGeographySearchResponse {
  readonly cities: readonly ({
    readonly country_iso2: string;
    readonly state_code: string | null;
    readonly state_name: string;
    readonly city_name: string;
    readonly latitude: number | null;
    readonly longitude: number | null;
  })[];
}

export type ClientsGeographySearchRequest = Record<string, never>;

/** Takes somebody off a requisition. */
export interface ClientsJobAssignmentRemoveResponse {
  readonly jobId: string;
  readonly removed: boolean;
}

export type ClientsJobAssignmentRemoveRequest = Record<string, never>;

/** Puts somebody on a requisition, or changes what they do on it. */
export interface ClientsJobAssignmentSetResponse {
  readonly jobId: string;
  readonly membershipId: string;
  readonly assignmentRole: "lead" | "recruiter" | "coordinator" | "sourcer";
}

export type ClientsJobAssignmentSetRequest = {
  readonly membershipId: string;
  readonly assignmentRole?: "lead" | "recruiter" | "coordinator" | "sourcer";
};

/** Sets the most a candidate may be offered on this requisition. */
export interface ClientsJobBudgetSetResponse {
  readonly jobId: string;
  readonly pay_budget: string | null;
  readonly rate_unit: string | null;
}

export type ClientsJobBudgetSetRequest = {
  readonly payBudget?: string | null;
  readonly rateUnit?: "hourly" | "daily" | "monthly" | "annual";
  readonly currency?: string;
};

/** What this requisition was billed at before, and who changed it. */
export interface ClientsJobCommercialsHistoryResponse {
  readonly jobId: string;
  readonly changes: readonly ({
    readonly id: string;
    readonly field: string;
    readonly from_value: string | null;
    readonly to_value: string | null;
    readonly changed_by_membership_id: string | null;
    readonly changed_at: string;
  })[];
}

export type ClientsJobCommercialsHistoryRequest = Record<string, never>;

/** Settles what this requisition earns — a margin, or a fee. */
export interface ClientsJobCommercialsSetResponse {
  readonly jobId: string;
  readonly bill_rate: string | null;
  readonly pay_budget: string | null;
  readonly fee_percent: string | null;
  readonly fee_amount: string | null;
  readonly rate_card_id: string | null;
  readonly commercials_bound_at: string | null;
}

export type ClientsJobCommercialsSetRequest = {
  readonly rateCardId?: string | null;
  readonly offCardReason?: string;
  readonly feePercent?: string | null;
  readonly feeAmount?: string | null;
  readonly billRate?: string | null;
  readonly payBudget?: string | null;
  readonly rateUnit?: "hourly" | "daily" | "monthly" | "annual";
  readonly currency?: string;
  readonly marginFloorPercent?: string | null;
};

/** Stores a guide or an assignment against a requisition, so it can be sent to candidates. */
export interface ClientsJobDocumentAddResponse {
  readonly id: string;
  readonly job_id: string;
  readonly kind: "candidate_guide" | "assessment";
  readonly filename: string;
  readonly mime_type: string;
  readonly byte_size: number;
  readonly created_at: string;
}

export type ClientsJobDocumentAddRequest = {
  readonly kind: "candidate_guide" | "assessment";
  readonly note?: string;
  readonly title?: string;
  readonly filename: string;
  readonly mimeType: "application/pdf" | "application/vnd.openxmlformats-officedocument.wordprocessingml.document" | "application/msword" | "text/plain" | "application/zip" | "application/x-zip-compressed" | "application/vnd.openxmlformats-officedocument.presentationml.presentation" | "application/vnd.ms-powerpoint" | "image/png" | "image/jpeg" | "image/webp";
  readonly content: string;
};

/** Points this requisition at a video or page, as one of its documents. */
export interface ClientsJobDocumentAddLinkResponse {
  readonly id: string;
  readonly kind: "jd" | "candidate_guide" | "assessment" | "video" | "other";
  readonly title: string;
  readonly filename: string;
  readonly link_url: string | null;
  readonly note: string | null;
  readonly mime_type: string;
  readonly byte_size: number | null;
  readonly uploaded_by_membership_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export type ClientsJobDocumentAddLinkRequest = {
  readonly kind: "jd" | "candidate_guide" | "assessment" | "video" | "other";
  readonly title: string;
  readonly note?: string;
  readonly linkUrl: string;
};

/** Writes or revises a candidate guide for a requisition. Stores nothing. */
export interface ClientsJobDocumentDraftResponse {
  readonly title: string;
  readonly body: string;
  readonly sample: boolean;
}

export type ClientsJobDocumentDraftRequest = {
  readonly kind: "candidate_guide" | "assessment";
  readonly instruction: string;
  readonly current?: {
    readonly title: string;
    readonly body: string;
  };
};

/** Every document and link this requisition holds, the job description included. */
export interface ClientsJobDocumentLibraryResponse {
  readonly documents: readonly ({
    readonly id: string;
    readonly kind: "jd" | "candidate_guide" | "assessment" | "video" | "other";
    readonly title: string;
    readonly filename: string;
    readonly link_url: string | null;
    readonly note: string | null;
    readonly mime_type: string;
    readonly byte_size: number | null;
    readonly uploaded_by_membership_id: string | null;
    readonly created_at: string;
    readonly updated_at: string;
  })[];
}

export type ClientsJobDocumentLibraryRequest = Record<string, never>;

/** The guides and assignments this requisition may send to a candidate. */
export interface ClientsJobDocumentListResponse {
  readonly documents: readonly ({
    readonly id: string;
    readonly job_id: string;
    readonly kind: "candidate_guide" | "assessment";
    readonly filename: string;
    readonly mime_type: string;
    readonly byte_size: number;
    readonly created_at: string;
  })[];
}

export type ClientsJobDocumentListRequest = Record<string, never>;

/** Reads an uploaded job description into field suggestions, each with its source. */
export interface ClientsJobDocumentParseResponse {
  readonly refused: boolean;
  readonly refusal: string | null;
  readonly suggestions: readonly ({
    readonly field: string;
    readonly value: string;
    readonly grounding: "jd" | "client" | "model";
    readonly evidence: string | null;
    readonly flaggedLines?: readonly number[];
  })[];
  readonly skills: readonly ({
    readonly skill: string;
    readonly band: "must" | "good" | "nice";
    readonly grounding: "jd" | "client" | "model";
  })[];
}

export type ClientsJobDocumentParseRequest = Record<string, never>;

/** Returns a stored job description, with its bytes, for viewing or download. */
export interface ClientsJobDocumentReadResponse {
  readonly id: string;
  readonly filename: string;
  readonly mime_type: string;
  readonly byte_size: number;
  readonly content: string;
}

export type ClientsJobDocumentReadRequest = Record<string, never>;

/** Takes a document off this requisition, leaving it on any other that uses it. */
export interface ClientsJobDocumentRemoveResponse {
  readonly id: string;
  readonly removed: boolean;
}

export type ClientsJobDocumentRemoveRequest = Record<string, never>;

/** Corrects a document’s title or what kind of thing it is. */
export interface ClientsJobDocumentRenameResponse {
  readonly id: string;
  readonly title: string;
  readonly kind: "jd" | "candidate_guide" | "assessment" | "video" | "other";
  readonly note: string | null;
}

export type ClientsJobDocumentRenameRequest = {
  readonly title: string;
  readonly kind: "jd" | "candidate_guide" | "assessment" | "video" | "other";
  readonly note?: string;
};

/** Lets another requisition send a guide or assignment that already exists. */
export interface ClientsJobDocumentReuseResponse {
  readonly id: string;
  readonly job_id: string;
  readonly kind: "candidate_guide" | "assessment";
  readonly filename: string;
  readonly mime_type: string;
  readonly byte_size: number;
  readonly created_at: string;
}

export type ClientsJobDocumentReuseRequest = Record<string, never>;

/** Renders an agreed draft to a Word document and keeps it against the requisition. */
export interface ClientsJobDocumentSaveDraftResponse {
  readonly id: string;
  readonly job_id: string;
  readonly kind: "candidate_guide" | "assessment";
  readonly filename: string;
  readonly mime_type: string;
  readonly byte_size: number;
  readonly created_at: string;
}

export type ClientsJobDocumentSaveDraftRequest = {
  readonly kind: "candidate_guide" | "assessment";
  readonly filename: string;
  readonly title: string;
  readonly note?: string;
  readonly body: string;
};

/** Stores an uploaded job description and reads the text out of it. */
export interface ClientsJobDocumentUploadResponse {
  readonly id: string;
  readonly filename: string;
  readonly mime_type: string;
  readonly byte_size: number;
  readonly job_id: string | null;
  readonly readable: boolean;
  readonly extraction_error: string | null;
  readonly preview: string | null;
  readonly created_at: string;
}

export type ClientsJobDocumentUploadRequest = {
  readonly filename: string;
  readonly mimeType: "application/pdf" | "application/vnd.openxmlformats-officedocument.wordprocessingml.document" | "application/msword" | "text/plain";
  readonly content: string;
};

/** The places a requisition is open for. */
export interface ClientsJobLocationListResponse {
  readonly jobId: string;
  readonly locations: readonly ({
    readonly id: string;
    readonly hiring_unit_id: string | null;
    readonly client_name: string | null;
    readonly country_iso2: string;
    readonly state_code: string | null;
    readonly state_name: string;
    readonly city_name: string;
    readonly latitude: number | null;
    readonly longitude: number | null;
    readonly label: string;
    readonly address_line: string | null;
    readonly map_url: string | null;
    readonly is_active: boolean;
    readonly created_at: string;
    readonly is_primary: boolean;
  })[];
}

export type ClientsJobLocationListRequest = Record<string, never>;

/** Replaces the places a requisition is open for. */
export interface ClientsJobLocationSetResponse {
  readonly jobId: string;
  readonly locations: readonly ({
    readonly id: string;
    readonly hiring_unit_id: string | null;
    readonly client_name: string | null;
    readonly country_iso2: string;
    readonly state_code: string | null;
    readonly state_name: string;
    readonly city_name: string;
    readonly latitude: number | null;
    readonly longitude: number | null;
    readonly label: string;
    readonly address_line: string | null;
    readonly map_url: string | null;
    readonly is_active: boolean;
    readonly created_at: string;
    readonly is_primary: boolean;
  })[];
}

export type ClientsJobLocationSetRequest = {
  readonly locations: readonly {
    readonly workLocationId: string;
    readonly isPrimary?: boolean;
  }[];
};

/** Replaces the skills a requisition asks for, in their three bands. */
export interface ClientsJobSkillSaveResponse {
  readonly jobId: string;
  readonly skills: readonly ({
    readonly skill: string;
    readonly band: "must" | "good" | "nice";
    readonly source: "recruiter" | "jd" | "client" | "model";
  })[];
}

export type ClientsJobSkillSaveRequest = {
  readonly skills: readonly ({
    readonly skill: string;
    readonly band?: "must" | "good" | "nice";
    readonly source?: "recruiter" | "jd" | "client" | "model";
  })[];
};

/** Records that somebody has read the verdict — §5.2’s second half of the gate. */
export interface ClientsJobAcknowledgeIntakeResponse {
  readonly verdict: "ready" | "needs_detail" | "not_workable";
  readonly summary: string;
  readonly analysed_at: string;
  readonly stale: boolean;
  readonly acknowledged_at: string | null;
  readonly generated_by_adapter: string | null;
  readonly open_flag_count: number;
  readonly flags: readonly ({
    readonly id: string;
    readonly title: string;
    readonly detail: string;
    readonly severity: "low" | "medium" | "high";
    readonly grounding: "jd" | "client" | "model";
    readonly question: string | null;
    readonly disposition: "open" | "accepted" | "dismissed" | "resolved" | "answered" | "asked_client";
    readonly disposition_note: string | null;
    readonly dispositioned_at: string | null;
    readonly asked_client_at: string | null;
  })[];
}

export type ClientsJobAcknowledgeIntakeRequest = Record<string, never>;

/** Analyses a requisition and raises what has to be answered before it opens. */
export interface ClientsJobAnalyseIntakeResponse {
  readonly verdict: "ready" | "needs_detail" | "not_workable";
  readonly summary: string;
  readonly analysed_at: string;
  readonly stale: boolean;
  readonly acknowledged_at: string | null;
  readonly generated_by_adapter: string | null;
  readonly open_flag_count: number;
  readonly flags: readonly ({
    readonly id: string;
    readonly title: string;
    readonly detail: string;
    readonly severity: "low" | "medium" | "high";
    readonly grounding: "jd" | "client" | "model";
    readonly question: string | null;
    readonly disposition: "open" | "accepted" | "dismissed" | "resolved" | "answered" | "asked_client";
    readonly disposition_note: string | null;
    readonly dispositioned_at: string | null;
    readonly asked_client_at: string | null;
  })[];
}

export type ClientsJobAnalyseIntakeRequest = Record<string, never>;

/** Every saved version of one artifact, newest first. */
export interface ClientsJobArtifactHistoryResponse {
  readonly versions: readonly ({
    readonly version_no: number;
    readonly origin: string;
    readonly created_at: string;
    readonly author_membership_id: string | null;
    readonly jd_version: number;
    readonly generated_by_sample: boolean | null;
  })[];
}

export type ClientsJobArtifactHistoryRequest = Record<string, never>;

/** The workbench for one requisition — all seven artifacts, generated or not. */
export interface ClientsJobArtifactsResponse {
  readonly job_id: string;
  readonly jd_version: number;
  readonly artifacts: readonly ({
    readonly kind: "role_explained" | "screening_questions" | "pitch_and_objections" | "calibration" | "boolean_strings" | "outreach_copy" | "social_post";
    readonly state: "absent" | "current" | "stale";
    readonly content: Readonly<Record<string, unknown>> | null;
    readonly version_no: number | null;
    readonly origin: string | null;
    readonly updated_at: string | null;
    readonly version_count: number;
    readonly ai_decision_id: string | null;
    readonly generated_by_sample: boolean | null;
    readonly proposal: {
      readonly content: Readonly<Record<string, unknown>>;
      readonly created_at: string;
      readonly jd_version: number;
    } | null;
    readonly run: {
      readonly id: string;
      readonly state: "queued" | "running" | "succeeded" | "failed" | "refused";
      readonly failure_code: string | null;
      readonly failure_message: string | null;
    } | null;
  })[];
}

export type ClientsJobArtifactsRequest = Record<string, never>;

/** Puts one person on several requisitions, without disturbing anybody already on them. */
export interface ClientsJobBulkAssignResponse {
  readonly done: number;
  readonly skipped: number;
  readonly results: readonly ({
    readonly jobId: string;
    readonly outcome: "done" | "not_found" | "skipped" | "failed";
    readonly reason: string | null;
  })[];
}

export type ClientsJobBulkAssignRequest = {
  readonly jobIds: readonly string[];
  readonly membershipId: string;
  readonly assignmentRole?: "lead" | "recruiter" | "coordinator" | "sourcer";
};

/** Closes several requisitions for one shared reason, reporting what happened to each. */
export interface ClientsJobBulkCloseResponse {
  readonly done: number;
  readonly skipped: number;
  readonly results: readonly ({
    readonly jobId: string;
    readonly outcome: "done" | "not_found" | "skipped" | "failed";
    readonly reason: string | null;
  })[];
}

export type ClientsJobBulkCloseRequest = {
  readonly jobIds: readonly string[];
  readonly closureReason: "filled" | "cancelled_by_client" | "lost" | "expired" | "superseded";
  readonly closureComment: string;
};

/** Hands several requisitions to one person, reporting what happened to each. */
export interface ClientsJobBulkSetOwnerResponse {
  readonly done: number;
  readonly skipped: number;
  readonly results: readonly ({
    readonly jobId: string;
    readonly outcome: "done" | "not_found" | "skipped" | "failed";
    readonly reason: string | null;
  })[];
}

export type ClientsJobBulkSetOwnerRequest = {
  readonly jobIds: readonly string[];
  readonly ownerMembershipId: string;
};

/** The most this job can be offered at, without saying what it earns. */
export interface ClientsJobCeilingResponse {
  readonly max_pay_rate: string | null;
  readonly basis: "agreed" | "calculated" | "none";
  readonly rate_unit: "hourly" | "daily" | "monthly" | "annual" | null;
  readonly currency: string | null;
  readonly health: "healthy" | "thin" | "below_floor" | "unknown";
  readonly capex_unallocated: boolean;
  readonly projected: boolean;
}

export type ClientsJobCeilingRequest = Record<string, never>;

/** Opens a requisition against a client. */
export interface ClientsJobCreateResponse {
  readonly jobId: string;
  readonly status: "draft" | "open" | "on_hold" | "closed" | "filled";
}

export type ClientsJobCreateRequest = {
  readonly clientId: string;
  readonly title: string;
  readonly descriptionMd?: string;
  readonly employmentType?: "full_time" | "contract" | "contract_to_hire" | "internship" | "consultant";
  readonly workModel?: "onsite" | "hybrid" | "remote";
  readonly experienceMinMonths?: number;
  readonly experienceMaxMonths?: number;
  readonly openings?: number;
  readonly priority?: "low" | "normal" | "high" | "critical";
  readonly status?: "draft" | "open";
  readonly employerOfRecord?: "client" | "agency";
  readonly candidateEngagement?: "client_permanent" | "agency_employee" | "agency_contractor";
  readonly showClientName?: boolean;
  readonly rateCardId?: string;
  readonly salaryMin?: string;
  readonly salaryMax?: string;
  readonly salaryCurrency?: string;
  readonly salaryBasis?: "annual" | "monthly" | "hourly";
  readonly shifts?: readonly {
    readonly label: string;
    readonly hours: string;
  }[];
  readonly idealCandidateNote?: string;
  readonly clientRequisitionId?: string;
  readonly jdUrl?: string;
  readonly confidential?: boolean;
  readonly owningGroupId?: string;
  readonly jdSourceDocumentId?: string;
};

/** Decides one intake flag — accepted, dismissed, answered, or asked of the client. */
export interface ClientsJobDispositionFlagResponse {
  readonly verdict: "ready" | "needs_detail" | "not_workable";
  readonly summary: string;
  readonly analysed_at: string;
  readonly stale: boolean;
  readonly acknowledged_at: string | null;
  readonly generated_by_adapter: string | null;
  readonly open_flag_count: number;
  readonly flags: readonly ({
    readonly id: string;
    readonly title: string;
    readonly detail: string;
    readonly severity: "low" | "medium" | "high";
    readonly grounding: "jd" | "client" | "model";
    readonly question: string | null;
    readonly disposition: "open" | "accepted" | "dismissed" | "resolved" | "answered" | "asked_client";
    readonly disposition_note: string | null;
    readonly dispositioned_at: string | null;
    readonly asked_client_at: string | null;
  })[];
}

export type ClientsJobDispositionFlagRequest = {
  readonly disposition: "accepted" | "dismissed" | "answered" | "asked_client";
  readonly note?: string;
};

/** Creates a draft from an existing requisition, copying what it asks for and nothing about how it went. */
export interface ClientsJobDuplicateResponse {
  readonly jobId: string;
  readonly status: "draft";
  readonly skillsCopied: number;
  readonly delegated: readonly ("screening_questions" | "interview_plan")[];
}

export type ClientsJobDuplicateRequest = {
  readonly title?: string;
  readonly clientId?: string;
};

/** Saves an edited artifact as a new version. The generated original is never lost. */
export interface ClientsJobEditArtifactResponse {
  readonly job_id: string;
  readonly jd_version: number;
  readonly artifacts: readonly ({
    readonly kind: "role_explained" | "screening_questions" | "pitch_and_objections" | "calibration" | "boolean_strings" | "outreach_copy" | "social_post";
    readonly state: "absent" | "current" | "stale";
    readonly content: Readonly<Record<string, unknown>> | null;
    readonly version_no: number | null;
    readonly origin: string | null;
    readonly updated_at: string | null;
    readonly version_count: number;
    readonly ai_decision_id: string | null;
    readonly generated_by_sample: boolean | null;
    readonly proposal: {
      readonly content: Readonly<Record<string, unknown>>;
      readonly created_at: string;
      readonly jd_version: number;
    } | null;
    readonly run: {
      readonly id: string;
      readonly state: "queued" | "running" | "succeeded" | "failed" | "refused";
      readonly failure_code: string | null;
      readonly failure_message: string | null;
    } | null;
  })[];
}

export type ClientsJobEditArtifactRequest = {
  readonly content: Readonly<Record<string, unknown>>;
};

/** Suggests a line for one field of a requisition, without writing it. */
export interface ClientsJobFieldAssistResponse {
  readonly field: "title" | "description_md";
  readonly suggestion: string;
  readonly grounding: "jd" | "client" | "model";
  readonly decisionId: string;
  readonly refused: boolean;
}

export type ClientsJobFieldAssistRequest = {
  readonly field: "title" | "description_md";
};

/** What this requisition’s fields used to say, and who changed them. */
export interface ClientsJobFieldHistoryResponse {
  readonly jobId: string;
  readonly changes: readonly ({
    readonly id: string;
    readonly field: string;
    readonly from_value: string | null;
    readonly to_value: string | null;
    readonly changed_by_membership_id: string | null;
    readonly changed_at: string;
  })[];
}

export type ClientsJobFieldHistoryRequest = Record<string, never>;

/** Generates or regenerates one workbench artifact. A regeneration never overwrites an edit. */
export interface ClientsJobGenerateArtifactResponse {
  readonly job_id: string;
  readonly jd_version: number;
  readonly artifacts: readonly ({
    readonly kind: "role_explained" | "screening_questions" | "pitch_and_objections" | "calibration" | "boolean_strings" | "outreach_copy" | "social_post";
    readonly state: "absent" | "current" | "stale";
    readonly content: Readonly<Record<string, unknown>> | null;
    readonly version_no: number | null;
    readonly origin: string | null;
    readonly updated_at: string | null;
    readonly version_count: number;
    readonly ai_decision_id: string | null;
    readonly generated_by_sample: boolean | null;
    readonly proposal: {
      readonly content: Readonly<Record<string, unknown>>;
      readonly created_at: string;
      readonly jd_version: number;
    } | null;
    readonly run: {
      readonly id: string;
      readonly state: "queued" | "running" | "succeeded" | "failed" | "refused";
      readonly failure_code: string | null;
      readonly failure_message: string | null;
    } | null;
  })[];
}

export type ClientsJobGenerateArtifactRequest = Record<string, never>;

/** Generates this requisition’s portal search strings — included for every tenant. */
export interface ClientsJobGenerateBooleanStringsResponse {
  readonly job_id: string;
  readonly jd_version: number;
  readonly artifacts: readonly ({
    readonly kind: "role_explained" | "screening_questions" | "pitch_and_objections" | "calibration" | "boolean_strings" | "outreach_copy" | "social_post";
    readonly state: "absent" | "current" | "stale";
    readonly content: Readonly<Record<string, unknown>> | null;
    readonly version_no: number | null;
    readonly origin: string | null;
    readonly updated_at: string | null;
    readonly version_count: number;
    readonly ai_decision_id: string | null;
    readonly generated_by_sample: boolean | null;
    readonly proposal: {
      readonly content: Readonly<Record<string, unknown>>;
      readonly created_at: string;
      readonly jd_version: number;
    } | null;
    readonly run: {
      readonly id: string;
      readonly state: "queued" | "running" | "succeeded" | "failed" | "refused";
      readonly failure_code: string | null;
      readonly failure_message: string | null;
    } | null;
  })[];
}

export type ClientsJobGenerateBooleanStringsRequest = {
  readonly tuning?: {
    readonly reach?: "as_is" | "broaden" | "narrow";
    readonly mustKeep?: readonly string[];
    readonly alsoTitles?: readonly string[];
    readonly maxLength?: number;
  };
};

/** The questions still outstanding with the client, ready to paste. */
export interface ClientsJobIntakeQuestionsResponse {
  readonly jobId: string;
  readonly questions: readonly ({
    readonly flagId: string;
    readonly question: string;
    readonly severity: "low" | "medium" | "high";
    readonly asked_at: string | null;
    readonly days_outstanding: number | null;
  })[];
}

export type ClientsJobIntakeQuestionsRequest = Record<string, never>;

/** Every job in this tenant, newest first, optionally filtered to one client. */
export interface ClientsJobListResponse {
  readonly jobs: readonly ({
    readonly id: string;
    readonly title: string;
    readonly hiring_unit_id: string;
    readonly client_name: string;
    readonly status: "draft" | "open" | "on_hold" | "closed" | "filled";
    readonly priority: "low" | "normal" | "high" | "critical";
    readonly employment_type: "full_time" | "contract" | "contract_to_hire" | "internship" | "consultant";
    readonly work_model: "onsite" | "hybrid" | "remote";
    readonly openings: number;
    readonly owner_membership_id: string | null;
    readonly owned_by_me: boolean;
    readonly assigned_to_me: boolean;
    readonly opened_at: string | null;
    readonly client_requisition_id: string | null;
    readonly confidential: boolean;
    readonly cleared_for_publishing: boolean;
    readonly owning_group_id: string | null;
    readonly filled_count: number;
    readonly target_close_on: string | null;
    readonly attention: readonly ("commercials_pending" | "fill_date_due")[];
    readonly created_at: string;
    readonly updated_at: string;
  })[];
  readonly scope: "own" | "team" | "all";
  readonly nextCursor: string | null;
}

export type ClientsJobListRequest = Record<string, never>;

/** The bill rate, the costs under it, and the margin between — §6.4 tier 2. */
export interface ClientsJobMarginResponse {
  readonly bill_rate: string | null;
  readonly pay_budget: string | null;
  readonly fee_percent: string | null;
  readonly fee_amount: string | null;
  readonly rate_card_id: string | null;
  readonly rate_unit: "hourly" | "daily" | "monthly" | "annual" | null;
  readonly currency: string | null;
  readonly margin_floor_percent: string | null;
  readonly margin_floor_source: "job" | "agency" | "none";
  readonly operating_cost: string | null;
  readonly operating_cost_percent: string | null;
  readonly capex: string | null;
  readonly max_pay_rate: string | null;
  readonly margin: string | null;
  readonly margin_percent: string | null;
  readonly markup_percent: string | null;
  readonly total_cost: string | null;
  readonly health: "healthy" | "thin" | "below_floor" | "unknown";
  readonly capex_unallocated: boolean;
  readonly cost_source: "job" | "template" | "none";
  readonly projected: boolean;
  readonly commercials_bound_at: string | null;
}

export type ClientsJobMarginRequest = Record<string, never>;

/** The figures the jobs landing opens with, over the whole book rather than a page. */
export interface ClientsJobOverviewResponse {
  readonly total: number;
  readonly open: number;
  readonly draft: number;
  readonly onHold: number;
  readonly closed: number;
  readonly filled: number;
  readonly commercialsPending: number;
  readonly fillDateDue: number;
  readonly added: number;
}

export type ClientsJobOverviewRequest = Record<string, never>;

/** What the public board would show for this job, if it were cleared right now. */
export interface ClientsJobPreviewPostingResponse {
  readonly tenant_name: string;
  readonly title: string;
  readonly description_md: string | null;
  readonly client_display: string;
  readonly employment_type: string;
  readonly candidate_engagement: string | null;
  readonly work_model: string;
  readonly experience_min_months: number | null;
  readonly experience_max_months: number | null;
  readonly salary_min: string | null;
  readonly salary_max: string | null;
  readonly salary_currency: string | null;
  readonly salary_basis: string | null;
  readonly locations: readonly ({
    readonly city: string | null;
    readonly state: string | null;
    readonly country: string | null;
  })[];
  readonly must_have_skills: readonly string[];
}

export type ClientsJobPreviewPostingRequest = Record<string, never>;

/** One job, with its description and the client it belongs to. */
export interface ClientsJobReadResponse {
  readonly job: {
    readonly id: string;
    readonly title: string;
    readonly hiring_unit_id: string;
    readonly client_name: string;
    readonly status: "draft" | "open" | "on_hold" | "closed" | "filled";
    readonly priority: "low" | "normal" | "high" | "critical";
    readonly employment_type: "full_time" | "contract" | "contract_to_hire" | "internship" | "consultant";
    readonly work_model: "onsite" | "hybrid" | "remote";
    readonly openings: number;
    readonly owner_membership_id: string | null;
    readonly owned_by_me: boolean;
    readonly assigned_to_me: boolean;
    readonly opened_at: string | null;
    readonly client_requisition_id: string | null;
    readonly confidential: boolean;
    readonly cleared_for_publishing: boolean;
    readonly owning_group_id: string | null;
    readonly filled_count: number;
    readonly target_close_on: string | null;
    readonly attention: readonly ("commercials_pending" | "fill_date_due")[];
    readonly created_at: string;
    readonly updated_at: string;
    readonly description_md: string | null;
    readonly jd_version: number;
    readonly experience_min_months: number | null;
    readonly experience_max_months: number | null;
    readonly stage_template_id: string | null;
    readonly hiring_manager_contact_id: string | null;
    readonly closed_at: string | null;
    readonly closure_reason: "filled" | "cancelled_by_client" | "lost" | "expired" | "superseded" | null;
    readonly superseded_by_job_id: string | null;
    readonly closure_comment: string | null;
    readonly hold_reason: string | null;
    readonly hold_note: string | null;
    readonly closure_label: string | null;
    readonly status_history: readonly ({
      readonly from_status: "draft" | "open" | "on_hold" | "closed" | "filled" | null;
      readonly to_status: "draft" | "open" | "on_hold" | "closed" | "filled";
      readonly closure_reason: "filled" | "cancelled_by_client" | "lost" | "expired" | "superseded" | null;
      readonly closure_comment: string | null;
      readonly hold_reason: string | null;
      readonly hold_note: string | null;
      readonly actor_membership_id: string | null;
      readonly occurred_at: string;
    })[];
    readonly jd_url: string | null;
    readonly commercials_bound_at: string | null;
    readonly jd_source_document_id: string | null;
    readonly assignments: readonly ({
      readonly membership_id: string;
      readonly assignment_role: "lead" | "recruiter" | "coordinator" | "sourcer";
      readonly assigned_by_membership_id: string | null;
      readonly assigned_at: string;
    })[];
    readonly skills: readonly ({
      readonly skill: string;
      readonly band: "must" | "good" | "nice";
      readonly source: "recruiter" | "jd" | "client" | "model";
    })[];
    readonly jd_history: readonly ({
      readonly jd_version: number;
      readonly description_md: string | null;
      readonly replaced_by_membership_id: string | null;
      readonly replaced_at: string;
    })[];
    readonly publish_cleared_at: string | null;
    readonly show_client_name: boolean;
    readonly posting: {
      readonly status: "live" | "expired" | "withdrawn";
      readonly slug: string;
      readonly published_at: string;
      readonly expires_at: string;
      readonly needs_refresh: boolean;
    } | null;
    readonly employer_of_record: string;
    readonly salary_min: string | null;
    readonly salary_max: string | null;
    readonly salary_currency: string | null;
    readonly salary_basis: string | null;
    readonly shifts: readonly {
      readonly label: string;
      readonly hours: string;
    }[];
    readonly ideal_candidate_note: string | null;
    readonly has_rate_card: boolean;
    readonly intake: {
      readonly verdict: "ready" | "needs_detail" | "not_workable";
      readonly summary: string;
      readonly analysed_at: string;
      readonly stale: boolean;
      readonly acknowledged_at: string | null;
      readonly generated_by_adapter: string | null;
      readonly open_flag_count: number;
      readonly flags: readonly ({
        readonly id: string;
        readonly title: string;
        readonly detail: string;
        readonly severity: "low" | "medium" | "high";
        readonly grounding: "jd" | "client" | "model";
        readonly question: string | null;
        readonly disposition: "open" | "accepted" | "dismissed" | "resolved" | "answered" | "asked_client";
        readonly disposition_note: string | null;
        readonly dispositioned_at: string | null;
        readonly asked_client_at: string | null;
      })[];
    } | null;
  };
}

export type ClientsJobReadRequest = Record<string, never>;

/** Republishes a live posting so the public board carries the current wording. */
export interface ClientsJobRefreshPostingResponse {
  readonly jobId: string;
  readonly posting: {
    readonly status: "live" | "expired" | "withdrawn";
    readonly slug: string;
    readonly published_at: string;
    readonly expires_at: string;
    readonly needs_refresh: boolean;
  };
}

export type ClientsJobRefreshPostingRequest = Record<string, never>;

/** Keeps or discards the fresh draft a regeneration offered against the working copy. */
export interface ClientsJobResolveArtifactProposalResponse {
  readonly job_id: string;
  readonly jd_version: number;
  readonly artifacts: readonly ({
    readonly kind: "role_explained" | "screening_questions" | "pitch_and_objections" | "calibration" | "boolean_strings" | "outreach_copy" | "social_post";
    readonly state: "absent" | "current" | "stale";
    readonly content: Readonly<Record<string, unknown>> | null;
    readonly version_no: number | null;
    readonly origin: string | null;
    readonly updated_at: string | null;
    readonly version_count: number;
    readonly ai_decision_id: string | null;
    readonly generated_by_sample: boolean | null;
    readonly proposal: {
      readonly content: Readonly<Record<string, unknown>>;
      readonly created_at: string;
      readonly jd_version: number;
    } | null;
    readonly run: {
      readonly id: string;
      readonly state: "queued" | "running" | "succeeded" | "failed" | "refused";
      readonly failure_code: string | null;
      readonly failure_message: string | null;
    } | null;
  })[];
}

export type ClientsJobResolveArtifactProposalRequest = {
  readonly decision: "keep" | "discard";
};

/** Restores an earlier version by writing it forward as a new one. */
export interface ClientsJobRestoreArtifactResponse {
  readonly job_id: string;
  readonly jd_version: number;
  readonly artifacts: readonly ({
    readonly kind: "role_explained" | "screening_questions" | "pitch_and_objections" | "calibration" | "boolean_strings" | "outreach_copy" | "social_post";
    readonly state: "absent" | "current" | "stale";
    readonly content: Readonly<Record<string, unknown>> | null;
    readonly version_no: number | null;
    readonly origin: string | null;
    readonly updated_at: string | null;
    readonly version_count: number;
    readonly ai_decision_id: string | null;
    readonly generated_by_sample: boolean | null;
    readonly proposal: {
      readonly content: Readonly<Record<string, unknown>>;
      readonly created_at: string;
      readonly jd_version: number;
    } | null;
    readonly run: {
      readonly id: string;
      readonly state: "queued" | "running" | "succeeded" | "failed" | "refused";
      readonly failure_code: string | null;
      readonly failure_message: string | null;
    } | null;
  })[];
}

export type ClientsJobRestoreArtifactRequest = {
  readonly versionNo: number;
};

/** Hands a requisition to the person answerable for it. */
export interface ClientsJobSetOwnerResponse {
  readonly jobId: string;
  readonly ownerMembershipId: string;
}

export type ClientsJobSetOwnerRequest = {
  readonly ownerMembershipId: string;
};

/** Clears a job for publishing outside the agency, or withdraws that clearance. */
export interface ClientsJobSetPublishClearanceResponse {
  readonly jobId: string;
  readonly cleared_for_publishing: boolean;
  readonly publish_cleared_at: string | null;
  readonly show_client_name: boolean;
  readonly unready: readonly string[];
}

export type ClientsJobSetPublishClearanceRequest = {
  readonly cleared: boolean;
  readonly showClientName?: boolean;
};

/** Corrects a job's requirement details, recording what changed. */
export interface ClientsJobUpdateResponse {
  readonly jobId: string;
  readonly changedFields: readonly string[];
  readonly jdVersion: number;
}

export type ClientsJobUpdateRequest = {
  readonly title?: string;
  readonly descriptionMd?: string | null;
  readonly workModel?: "onsite" | "hybrid" | "remote";
  readonly clientRequisitionId?: string | null;
  readonly jdUrl?: string | null;
  readonly confidential?: boolean;
  readonly experienceMinMonths?: number | null;
  readonly experienceMaxMonths?: number | null;
  readonly openings?: number;
  readonly priority?: "low" | "normal" | "high" | "critical";
  readonly targetCloseOn?: string | null;
  readonly hiringManagerContactId?: string | null;
  readonly employerOfRecord?: "client" | "agency";
  readonly salaryMin?: string | null;
  readonly salaryMax?: string | null;
  readonly salaryCurrency?: string | null;
  readonly salaryBasis?: "annual" | "monthly" | "hourly" | null;
  readonly shifts?: readonly {
    readonly label: string;
    readonly hours: string;
  }[] | null;
  readonly idealCandidateNote?: string | null;
};

/** Moves a job between draft, open, on hold, closed and filled, recording why it stopped. */
export interface ClientsJobUpdateStatusResponse {
  readonly jobId: string;
  readonly status: "draft" | "open" | "on_hold" | "closed" | "filled";
  readonly closureReason: "filled" | "cancelled_by_client" | "lost" | "expired" | "superseded" | null;
  readonly closureLabel: string | null;
}

export type ClientsJobUpdateStatusRequest = {
  readonly status: "draft" | "open" | "on_hold" | "closed" | "filled";
  readonly closureReason?: "filled" | "cancelled_by_client" | "lost" | "expired" | "superseded";
  readonly supersededByJobId?: string;
  readonly closureComment?: string;
  readonly closureLabelId?: string;
  readonly holdReason?: string;
  readonly holdNote?: string;
};

/** Corrects a figure typed wrong on a card, keeping a record of what it used to say. */
export interface ClientsRateCardCorrectResponse {
  readonly id: string;
  readonly corrected: number;
}

export type ClientsRateCardCorrectRequest = {
  readonly fee_percent?: string | null;
  readonly fee_amount?: string | null;
  readonly bill_rate?: string | null;
  readonly rate_unit?: "hourly" | "daily" | "monthly" | "annual" | null;
  readonly margin_floor_percent?: string | null;
  readonly reason?: string;
};

/** Adds a named rate card to a client’s menu for one engagement type. */
export interface ClientsRateCardCreateResponse {
  readonly rateCardId: string;
}

export type ClientsRateCardCreateRequest = {
  readonly employmentType: "full_time" | "contract" | "contract_to_hire" | "internship" | "consultant";
  readonly name: string;
  readonly fee_percent?: string;
  readonly fee_amount?: string;
  readonly bill_rate?: string;
  readonly rate_unit?: "hourly" | "daily" | "monthly" | "annual";
  readonly margin_floor_percent?: string;
  readonly currency: string;
  readonly effective_from?: string;
  readonly effective_to?: string;
};

/** Takes a card off the menu without deleting what was priced from it. */
export interface ClientsRateCardRetireResponse {
  readonly id: string;
  readonly active: boolean;
}

export type ClientsRateCardRetireRequest = {
  readonly effective_to?: string;
};

/** Renames a card or closes its window. The numbers are never edited in place. */
export interface ClientsRateCardUpdateResponse {
  readonly id: string;
}

export type ClientsRateCardUpdateRequest = {
  readonly name?: string;
  readonly effective_to?: string | null;
};

/** The service levels agreed with each client, in hours. No commercial terms. */
export interface ClientsSlaListResponse {
  readonly slas: readonly ({
    readonly id: string;
    readonly hiring_unit_id: string;
    readonly sla_type: "submission" | "feedback" | "scheduling" | "offer";
    readonly target_hours: number;
    readonly active: boolean;
  })[];
}

export type ClientsSlaListRequest = Record<string, never>;

/** Sets how fast this agency promised to act for a client. */
export interface ClientsSlaSetResponse {
  readonly slaId: string;
}

export type ClientsSlaSetRequest = {
  readonly target_hours: number;
  readonly active?: boolean;
};

/** Median days from opening a requisition to the placement that filled it, per client. */
export interface ClientsTimeToFillReadResponse {
  readonly buckets: readonly ({
    readonly hiring_unit_id: string | null;
    readonly client_name: string | null;
    readonly opened: number;
    readonly filled: number;
    readonly median_days_to_fill: number | null;
  })[];
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type ClientsTimeToFillReadRequest = Record<string, never>;

/** Records a place the agency works from, either a client's or a standalone site. */
export interface ClientsWorkLocationCreateResponse {
  readonly id: string;
  readonly hiring_unit_id: string | null;
  readonly client_name: string | null;
  readonly country_iso2: string;
  readonly state_code: string | null;
  readonly state_name: string;
  readonly city_name: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly label: string;
  readonly address_line: string | null;
  readonly map_url: string | null;
  readonly is_active: boolean;
  readonly created_at: string;
}

export type ClientsWorkLocationCreateRequest = {
  readonly countryIso2: string;
  readonly cityName: string;
  readonly stateCode?: string;
  readonly label: string;
  readonly hiringUnitId?: string;
  readonly addressLine?: string;
  readonly mapUrl?: string;
};

/** The places this agency knows about, optionally narrowed to one client's. */
export interface ClientsWorkLocationListResponse {
  readonly locations: readonly ({
    readonly id: string;
    readonly hiring_unit_id: string | null;
    readonly client_name: string | null;
    readonly country_iso2: string;
    readonly state_code: string | null;
    readonly state_name: string;
    readonly city_name: string;
    readonly latitude: number | null;
    readonly longitude: number | null;
    readonly label: string;
    readonly address_line: string | null;
    readonly map_url: string | null;
    readonly is_active: boolean;
    readonly created_at: string;
  })[];
}

export type ClientsWorkLocationListRequest = Record<string, never>;

/** Stops offering a place, without rewriting the requisitions that named it. */
export interface ClientsWorkLocationRetireResponse {
  readonly id: string;
  readonly hiring_unit_id: string | null;
  readonly client_name: string | null;
  readonly country_iso2: string;
  readonly state_code: string | null;
  readonly state_name: string;
  readonly city_name: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly label: string;
  readonly address_line: string | null;
  readonly map_url: string | null;
  readonly is_active: boolean;
  readonly created_at: string;
}

export type ClientsWorkLocationRetireRequest = {
  readonly active?: boolean;
};

/** Every AI feature and whether this agency has it switched on. */
export interface CopilotAiSettingsReadResponse {
  readonly features: readonly {
    readonly key: string;
    readonly tier: string;
    readonly purpose: string;
    readonly enabled: boolean;
    readonly data_class: string;
  }[];
}

export type CopilotAiSettingsReadRequest = Record<string, never>;

/** Switch one AI feature on or off for this agency. */
export interface CopilotAiSettingsUpdateResponse {
  readonly key: string;
  readonly enabled: boolean;
}

export type CopilotAiSettingsUpdateRequest = {
  readonly enabled: boolean;
};

/** Where one generated answer about a candidate came from, including what was sent. */
export interface CopilotCandidateDecisionReadResponse {
  readonly id: string;
  readonly feature_key: string;
  readonly purpose: string | null;
  readonly sample: boolean;
  readonly prompt_version: number;
  readonly subject_type: string;
  readonly subject_id: string;
  readonly refused: boolean;
  readonly latency_ms: number | null;
  readonly created_at: string;
  readonly input: readonly {
    readonly label: string;
    readonly value: string;
  }[];
  readonly redacted: boolean;
}

export type CopilotCandidateDecisionReadRequest = Record<string, never>;

/** Where one generated answer about a requisition came from — AI-04, as a person reads it. */
export interface CopilotJobDecisionReadResponse {
  readonly id: string;
  readonly feature_key: string;
  readonly purpose: string | null;
  readonly sample: boolean;
  readonly prompt_version: number;
  readonly subject_type: string;
  readonly subject_id: string;
  readonly refused: boolean;
  readonly latency_ms: number | null;
  readonly created_at: string;
  readonly input: readonly {
    readonly label: string;
    readonly value: string;
  }[];
}

export type CopilotJobDecisionReadRequest = Record<string, never>;

/** Providers, routing, prices and how every feature currently resolves. */
export interface CopilotPlatformAiConfigReadResponse {
  readonly adapters: readonly ({
    readonly name: string;
    readonly configured: boolean;
    readonly residency: string;
    readonly last_check: {
      readonly ok: boolean;
      readonly latency_ms: number;
      readonly failure_code: string | null;
      readonly model: string | null;
      readonly checked_at: string;
    } | null;
  })[];
  readonly features: readonly ({
    readonly key: string;
    readonly group: string | null;
    readonly tier: string;
    readonly data_class: string;
    readonly deterministic: boolean;
    readonly purpose: string;
    readonly about_a_role: {
      readonly adapter: string;
      readonly model: string | null;
      readonly source: string;
      readonly refused_reason: string | null;
    };
    readonly about_a_person: {
      readonly adapter: string;
      readonly model: string | null;
      readonly source: string;
      readonly refused_reason: string | null;
    };
  })[];
  readonly routes: readonly ({
    readonly id: string;
    readonly tenant_id: string | null;
    readonly scope: "tier" | "feature";
    readonly scope_key: string;
    readonly adapter: string;
    readonly model: string | null;
    readonly tier_class: string;
    readonly residency: string;
    readonly enabled: boolean;
  })[];
  readonly prices: readonly {
    readonly id: string;
    readonly adapter: string;
    readonly model: string;
    readonly input_price_per_mtok: string;
    readonly output_price_per_mtok: string;
    readonly currency: string;
    readonly inr_per_unit: string;
    readonly effective_from: string;
  }[];
}

export type CopilotPlatformAiConfigReadRequest = Record<string, never>;

/** Make one small real call to a provider and record what happened. */
export interface CopilotPlatformAiConnectionCheckResponse {
  readonly ok: boolean;
  readonly latency_ms: number;
  readonly failure_code: string | null;
  readonly input_tokens: number | null;
  readonly output_tokens: number | null;
}

export type CopilotPlatformAiConnectionCheckRequest = {
  readonly model: string | null;
};

/** Forgets a provider’s key, so nothing is sent to them again. */
export interface CopilotPlatformAiCredentialClearResponse {
  readonly provider: "anthropic" | "openai" | "mistral" | "gemini";
  readonly configured: boolean;
}

export type CopilotPlatformAiCredentialClearRequest = Record<string, never>;

/** Which model providers this installation holds a key for. */
export interface CopilotPlatformAiCredentialReadResponse {
  readonly credentials: readonly ({
    readonly provider: "anthropic" | "openai" | "mistral" | "gemini";
    readonly configured: boolean;
    readonly default_model: string | null;
    readonly organisation: string | null;
    readonly updated_at: string | null;
  })[];
  readonly can_store_credentials: boolean;
}

export type CopilotPlatformAiCredentialReadRequest = Record<string, never>;

/** Stores the API key this installation calls a model provider with. */
export interface CopilotPlatformAiCredentialSaveResponse {
  readonly provider: "anthropic" | "openai" | "mistral" | "gemini";
  readonly configured: boolean;
}

export type CopilotPlatformAiCredentialSaveRequest = {
  readonly apiKey?: string;
  readonly defaultModel: string;
  readonly organisation?: string | null;
};

/** The models a provider says it can serve, asked with the key this installation holds. */
export interface CopilotPlatformAiModelListResponse {
  readonly provider: "anthropic" | "openai" | "mistral" | "gemini";
  readonly models: readonly string[];
  readonly fallback: readonly string[];
  readonly problem: "not_configured" | "no_encryption" | "unauthorised" | "unavailable" | "timeout" | "malformed_response" | null;
}

export type CopilotPlatformAiModelListRequest = Record<string, never>;

/** Record what a model costs from now on, closing the previous price. */
export interface CopilotPlatformAiPriceCreateResponse {
  readonly id: string;
  readonly effective_from: string;
}

export type CopilotPlatformAiPriceCreateRequest = {
  readonly adapter: "fixture" | "anthropic" | "openai" | "local" | "mistral" | "gemini";
  readonly model: string;
  readonly inputPricePerMtok: string;
  readonly outputPricePerMtok: string;
  readonly currency: string;
  readonly inrPerUnit: string;
  readonly note: string | null;
};

/** Point a tier or a feature at a backend, for everybody or for one agency. */
export interface CopilotPlatformAiRouteUpsertResponse {
  readonly id: string;
  readonly refused_reason: string | null;
}

export type CopilotPlatformAiRouteUpsertRequest = {
  readonly tenantId?: string | null;
  readonly scope: "tier" | "feature";
  readonly scopeKey: string;
  readonly adapter: "fixture" | "anthropic" | "openai" | "local" | "mistral" | "gemini";
  readonly model: string | null;
  readonly tierClass: "t1" | "t2" | "t3" | "t4";
  readonly residency: "local" | "hosted";
  readonly enabled: boolean;
};

/** One tenant's AI usage and cost for the current month, against its allowance. */
export interface CopilotPlatformAiUsageReadResponse {
  readonly period_month: string;
  readonly currency: string;
  readonly lines: readonly {
    readonly feature_key: string;
    readonly unit: string;
    readonly quantity: number;
    readonly cost: string;
    readonly event_count: number;
  }[];
  readonly calls: number;
  readonly cost: string;
  readonly call_limit: number | null;
  readonly quota_period: string | null;
  readonly unmetered: number;
}

export type CopilotPlatformAiUsageReadRequest = Record<string, never>;

/** What the parser read from a resume, with per-field confidence. */
export interface CopilotResumeParseReadResponse {
  readonly parse: {
    readonly resume_id: string;
    readonly parser_version: string;
    readonly model_version: string | null;
    readonly contact: {
      readonly full_name: {
        readonly value: string | number;
        readonly confidence: number;
        readonly source: "rule" | "model" | "human";
        readonly low_confidence: boolean;
      } | null;
      readonly email: {
        readonly value: string | number;
        readonly confidence: number;
        readonly source: "rule" | "model" | "human";
        readonly low_confidence: boolean;
      } | null;
      readonly phone: {
        readonly value: string | number;
        readonly confidence: number;
        readonly source: "rule" | "model" | "human";
        readonly low_confidence: boolean;
      } | null;
      readonly location: {
        readonly value: string | number;
        readonly confidence: number;
        readonly source: "rule" | "model" | "human";
        readonly low_confidence: boolean;
      } | null;
      readonly headline: {
        readonly value: string | number;
        readonly confidence: number;
        readonly source: "rule" | "model" | "human";
        readonly low_confidence: boolean;
      } | null;
      readonly professional_title?: {
        readonly value: string | number;
        readonly confidence: number;
        readonly source: "rule" | "model" | "human";
        readonly low_confidence: boolean;
      } | null;
      readonly total_experience_months: {
        readonly value: string | number;
        readonly confidence: number;
        readonly source: "rule" | "model" | "human";
        readonly low_confidence: boolean;
      } | null;
      readonly notice_period_days: {
        readonly value: string | number;
        readonly confidence: number;
        readonly source: "rule" | "model" | "human";
        readonly low_confidence: boolean;
      } | null;
    };
    readonly links?: readonly {
      readonly kind: string;
      readonly url: string;
    }[];
    readonly career_gaps?: readonly ({
      readonly kind: "education_to_work" | "between_roles";
      readonly from: string;
      readonly to: string;
      readonly months: number;
      readonly approximate: boolean;
    })[];
    readonly skills: readonly ({
      readonly value: string | number;
      readonly confidence: number;
      readonly source: "rule" | "model" | "human";
      readonly low_confidence: boolean;
      readonly skill_type?: "specialized" | "common" | "certification" | null;
      readonly domain?: string | null;
      readonly evidence?: readonly ("project" | "employment" | "skills-section" | "summary")[];
      readonly context?: string | null;
    })[];
    readonly experience: readonly ({
      readonly company: string;
      readonly title: string | null;
      readonly starts_on: string | null;
      readonly ends_on: string | null;
      readonly is_current: boolean;
      readonly confidence: number;
      readonly low_confidence: boolean;
      readonly kind: "employment" | "project";
      readonly client: string | null;
      readonly location?: string | null;
      readonly project_name?: string | null;
    })[];
    readonly education: readonly ({
      readonly institution: string;
      readonly qualification: string | null;
      readonly field_of_study?: string | null;
      readonly grade?: string | null;
      readonly completed_year: number | null;
      readonly confidence: number;
      readonly low_confidence: boolean;
    })[];
    readonly timeline: readonly {
      readonly year: number;
      readonly label: string;
    }[];
    readonly ambiguous_dates?: readonly {
      readonly raw: string;
      readonly resolved: string;
    }[] | null;
    readonly quality_score: number | null;
    readonly confidence: number | null;
    readonly quarantine_flags: Readonly<Record<string, unknown>>;
    readonly redacted: boolean;
    readonly tier_path: readonly string[];
    readonly parsed_at: string;
  } | null;
}

export type CopilotResumeParseReadRequest = Record<string, never>;

/** Records what the reviewer changed about a parse, as a training pair. */
export interface CopilotResumeParseRecordReviewResponse {
  readonly kept: number;
  readonly edited: number;
  readonly removed: number;
  readonly added: number;
}

export type CopilotResumeParseRecordReviewRequest = {
  readonly fullName: string;
  readonly email?: string;
  readonly phone?: string;
  readonly headline?: string;
  readonly skills?: readonly string[];
  readonly experience?: readonly {
    readonly company: string;
    readonly title?: string;
  }[];
  readonly education?: readonly {
    readonly institution: string;
    readonly qualification?: string;
  }[];
};

/** Verifies an email and password and starts a session. */
export interface IdentityAuthPasswordResponse {
  readonly accessToken: string;
  readonly expiresIn: number;
}

export type IdentityAuthPasswordRequest = {
  readonly email: string;
  readonly password: string;
  readonly remember?: boolean;
};

/** Exchanges the refresh cookie for a new access token. */
export interface IdentityAuthRefreshResponse {
  readonly accessToken: string;
  readonly expiresIn: number;
}

export type IdentityAuthRefreshRequest = Record<string, never>;

/** Spends a reset token and sets a new password. */
export interface IdentityAuthResetConfirmResponse {
  readonly reset: true;
}

export type IdentityAuthResetConfirmRequest = {
  readonly token: string;
  readonly password: string;
};

/** Starts a password reset, revealing nothing about whether the address is known. */
export interface IdentityAuthResetRequestResponse {
  readonly sent: true;
}

export type IdentityAuthResetRequestRequest = {
  readonly email: string;
};

/** Ends the current session and clears the refresh cookie. */
export interface IdentityAuthSignOutResponse {
  readonly signedOut: true;
}

export type IdentityAuthSignOutRequest = Record<string, never>;

/** Accepts an invitation by setting a password, creating the account at the provider. */
export interface IdentityInvitationActivateResponse {
  readonly activated: true;
}

export type IdentityInvitationActivateRequest = {
  readonly email: string;
  readonly password: string;
};

/** The invitations queued for one tenant, newest first. */
export interface IdentityPlatformInvitationListResponse {
  readonly invitations: readonly ({
    readonly id: string;
    readonly email: string;
    readonly realm: string;
    readonly full_name: string | null;
    readonly status: "queued" | "sent" | "failed" | "skipped" | "awaiting_activation" | "accepted";
    readonly attempts: number;
    readonly last_error_code: string | null;
    readonly provider_reference: string | null;
    readonly created_at: string;
    readonly updated_at: string;
  })[];
}

export type IdentityPlatformInvitationListRequest = Record<string, never>;

/** Every application this person has made, across every agency. */
export interface IdentityPortalApplicationsResponse {
  readonly applications: readonly ({
    readonly intentId: string;
    readonly agencyName: string;
    readonly agencySlug: string;
    readonly jobTitle: string;
    readonly postingSlug: string;
    readonly status: "portal.candidate.stage.received" | "portal.candidate.stage.shared" | "portal.candidate.stage.interviewing" | "portal.candidate.stage.offer" | "portal.candidate.stage.accepted" | "portal.candidate.stage.closed" | "portal.candidate.stage.withdrawn";
    readonly isTerminal: boolean;
    readonly changedAt: string;
    readonly submittedAt: string;
    readonly state: "submitted" | "materialised" | "refused" | "failed";
    readonly refusedReason: "posting_expired" | "posting_withdrawn" | "already_applied" | null;
  })[];
}

export type IdentityPortalApplicationsRequest = Record<string, never>;

/** Records a request to be considered for a published role. */
export interface IdentityPortalApplyResponse {
  readonly intentId: string;
  readonly state: "submitted" | "materialised" | "refused" | "failed";
  readonly agencyName: string;
  readonly jobTitle: string;
}

export type IdentityPortalApplyRequest = {
  readonly postingId: string;
  readonly answers: {
    readonly serving_notice: boolean;
    readonly notice_days?: "immediate" | 15 | 30 | 45 | 60 | 90;
    readonly last_working_day?: string;
    readonly holds_offer?: boolean;
    readonly offer_amount?: number;
    readonly offer_deadline?: string;
    readonly years_with_skill?: string;
    readonly can_work_mode?: boolean;
    readonly pay_now?: number;
    readonly pay_wanted?: number;
    readonly note?: string;
  };
  readonly resumeObjectKey?: string;
};

/** Sends a verification link or code to an email address or phone number. */
export interface IdentityPortalStartResponse {
  readonly challengeId: string;
}

export type IdentityPortalStartRequest = {
  readonly email?: string;
  readonly phone?: string;
  readonly channel?: "email" | "whatsapp" | "sms";
};

/** Spends a verification secret and confirms the channel. */
export interface IdentityPortalVerifyResponse {
  readonly verified: true;
  readonly accessToken: string;
  readonly expiresIn: number;
  readonly isNewAccount: boolean;
}

export type IdentityPortalVerifyRequest = {
  readonly challengeId: string;
  readonly secret: string;
  readonly email?: string;
  readonly phone?: string;
};

/** What happened to one job, candidate or application — §28’s timeline. */
export interface InsightActivityListResponse {
  readonly entries: readonly ({
    readonly id: string;
    readonly kind: string;
    readonly summary: string;
    readonly actor_membership_id: string | null;
    readonly job_id: string | null;
    readonly application_id: string | null;
    readonly candidate_profile_id: string | null;
    readonly detail: Readonly<Record<string, unknown>>;
    readonly occurred_at: string;
  })[];
  readonly nextCursor: string | null;
}

export type InsightActivityListRequest = Record<string, never>;

/** Every candidature that is currently stuck, with who is holding it up and since when. */
export interface InsightAttentionReadResponse {
  readonly scope: string;
  readonly items: readonly ({
    readonly block_id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly job_title: string | null;
    readonly hiring_unit_id: string | null;
    readonly candidate_profile_id: string | null;
    readonly owner_membership_id: string | null;
    readonly blocked_by: "client" | "candidate" | "us";
    readonly label: string;
    readonly source: string;
    readonly core_stage: string;
    readonly chases: number;
    readonly last_chase_at: string | null;
    readonly started_at: string;
  })[];
  readonly total: number;
  readonly truncated: boolean;
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightAttentionReadRequest = Record<string, never>;

/** Live candidatures that are not moving, grouped by whose desk they sit on. */
export interface InsightBlockedByOwnerReadResponse {
  readonly scope: string;
  readonly people: readonly ({
    readonly owner_membership_id: string | null;
    readonly total: number;
    readonly blocked: {
      readonly us: number;
      readonly client: number;
      readonly candidate: number;
    };
    readonly median_days: number | null;
    readonly worst_days: number | null;
  })[];
  readonly total: number;
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightBlockedByOwnerReadRequest = Record<string, never>;

/** Live candidatures that are not moving, grouped by who is holding them up. */
export interface InsightBlockedReadResponse {
  readonly scope: string;
  readonly rows: readonly ({
    readonly blocked_by: "client" | "candidate" | "us";
    readonly count: number;
    readonly median_days: number | null;
    readonly worst_days: number | null;
    readonly system_raised: number;
  })[];
  readonly total: number;
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightBlockedReadRequest = Record<string, never>;

/** What moved since the previous working day, ranked by how much it matters. */
export interface InsightChangesReadResponse {
  readonly scope: string;
  readonly since: string;
  readonly widened: boolean;
  readonly changes: readonly ({
    readonly id: string;
    readonly kind: string;
    readonly summary: string;
    readonly materiality: "needs_a_look" | "moved_on" | "routine";
    readonly reason: "blocks_work" | "creates_a_decision" | "multi_record" | "exception" | null;
    readonly job_id: string | null;
    readonly job_title: string | null;
    readonly hiring_unit_id: string | null;
    readonly application_id: string | null;
    readonly candidate_profile_id: string | null;
    readonly actor_membership_id: string | null;
    readonly occurred_at: string;
  })[];
  readonly counts: readonly ({
    readonly materiality: "needs_a_look" | "moved_on" | "routine";
    readonly count: number;
  })[];
  readonly truncated: boolean;
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightChangesReadRequest = Record<string, never>;

/** How long each client takes to come back, from client-attributed waiting. */
export interface InsightClientResponsivenessReadResponse {
  readonly scope: string;
  readonly clients: readonly ({
    readonly hiring_unit_id: string;
    readonly turnaround_days: number | null;
    readonly waits: number;
    readonly days_waiting: number;
    readonly still_waiting: number;
  })[];
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightClientResponsivenessReadRequest = Record<string, never>;

/** Promises something for a day. §7.2: anyone in the group adds, and assigns an owner. */
export interface InsightCommitmentCreateResponse {
  readonly id: string;
  readonly standup_date: string;
}

export type InsightCommitmentCreateRequest = {
  readonly jobId: string;
  readonly ownerMembershipId?: string;
  readonly groupId?: string | null;
  readonly standupDate?: string;
  readonly actionType: "source" | "submit" | "screen" | "schedule_interview" | "follow_up_candidate" | "follow_up_client" | "other";
  readonly target?: number | null;
  readonly note?: string | null;
};

/** Removes a commitment. §7.2’s board is edited during the meeting. */
export interface InsightCommitmentDeleteResponse {
  readonly id: string;
  readonly removed: boolean;
}

export type InsightCommitmentDeleteRequest = Record<string, never>;

/** A day’s commitments — §7.2’s board, §7.3’s block and §7.4’s tab are one query. */
export interface InsightCommitmentListResponse {
  readonly commitments: readonly ({
    readonly id: string;
    readonly group_id: string | null;
    readonly job_id: string;
    readonly job_title: string | null;
    readonly hiring_unit_id: string | null;
    readonly owner_membership_id: string;
    readonly created_by_membership_id: string | null;
    readonly owned_by_me: boolean;
    readonly standup_date: string;
    readonly action_type: "source" | "submit" | "screen" | "schedule_interview" | "follow_up_candidate" | "follow_up_client" | "other";
    readonly target: number | null;
    readonly note: string | null;
    readonly actual: number;
    readonly status: "open" | "met" | "partial" | "missed" | "done";
    readonly measured: boolean;
    readonly done_at: string | null;
    readonly rolled_from_id: string | null;
    readonly created_at: string;
  })[];
  readonly scope: "own" | "team" | "all";
  readonly today: string;
}

export type InsightCommitmentListRequest = Record<string, never>;

/** §7.2’s "roll unmet forward" — one click, and the link back is kept. */
export interface InsightCommitmentRollForwardResponse {
  readonly rolled: number;
  readonly skipped: number;
  readonly to: string;
}

export type InsightCommitmentRollForwardRequest = {
  readonly from: string;
  readonly to?: string;
  readonly commitmentIds?: readonly string[];
  readonly scope?: "own" | "team" | "all";
};

/** Edits a commitment, or marks the kind that no event can close. */
export interface InsightCommitmentUpdateResponse {
  readonly id: string;
  readonly done_at: string | null;
}

export type InsightCommitmentUpdateRequest = {
  readonly target?: number | null;
  readonly note?: string | null;
  readonly done?: boolean;
};

/** Stage-to-stage conversion and median time in stage, at the scope this principal holds. */
export interface InsightConversionReadResponse {
  readonly scope: "own" | "team" | "all";
  readonly steps: readonly ({
    readonly step: "sourced_to_screened" | "screened_to_submitted" | "submitted_to_shortlisted" | "shortlisted_to_interviewing" | "interviewing_to_offered" | "offered_to_accepted" | "accepted_to_joined";
    readonly entered: number;
    readonly advanced: number;
    readonly rate: number | null;
    readonly median_days: number | null;
  })[];
  readonly applications: number;
  readonly closed: number;
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightConversionReadRequest = Record<string, never>;

/** Answers a question about the asker's own desk, from what the activity feed projects. */
export interface InsightCopilotAskResponse {
  readonly decisionId: string;
  readonly reply: string;
  readonly citations: readonly string[];
  readonly refused: boolean;
  readonly ai_sample: boolean;
  readonly context: {
    readonly kind: "job" | "candidate" | "application" | "desk";
    readonly label: string;
    readonly facts: readonly {
      readonly label: string;
      readonly value: string;
    }[];
  };
}

export type InsightCopilotAskRequest = {
  readonly question: string;
};

/** Answers a question about the reader’s book of work, from what a validated plan returned. */
export interface InsightCopilotAskBookResponse {
  readonly decisionId: string;
  readonly reply: string;
  readonly citations: readonly string[];
  readonly refused: boolean;
  readonly ai_sample: boolean;
  readonly plan: {
    readonly steps: readonly ({
      readonly kind: string;
      readonly id: string;
      readonly label: string;
      readonly state: "ok" | "refused" | "unknown";
      readonly found: number;
    })[];
    readonly residue: string | null;
    readonly confidence: number;
  };
  readonly facts: readonly {
    readonly label: string;
    readonly value: string;
  }[];
}

export type InsightCopilotAskBookRequest = {
  readonly question: string;
  readonly scope?: "own" | "team" | "all";
  readonly subjectId?: string;
};

/** This principal’s pipeline funnel, by core stage, at the scope they hold. */
export interface InsightDashboardReadResponse {
  readonly scope: "own" | "team" | "all";
  readonly stages: readonly ({
    readonly core_stage: "sourced" | "screened" | "submitted" | "shortlisted" | "interviewing" | "offered" | "accepted" | "joined" | "rejected" | "withdrawn";
    readonly count: number;
  })[];
  readonly open_total: number;
  readonly closed_total: number;
  readonly blocked_total: number;
  readonly projected_at: string | null;
}

export type InsightDashboardReadRequest = Record<string, never>;

/** The morning digest — four blocks and a paragraph that can be pasted into a chat. */
export interface InsightDigestReadResponse {
  readonly scope: "own" | "team" | "all";
  readonly day: string;
  readonly headline: string;
  readonly blocks: readonly ({
    readonly key: "changed" | "stuck_on_us" | "today" | "desk";
    readonly lines: readonly ({
      readonly tone: "up" | "down" | "flat" | "plain";
      readonly mark: string;
      readonly text: string;
      readonly job_id: string | null;
    })[];
  })[];
  readonly shareable: boolean;
  readonly text: string;
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightDigestReadRequest = Record<string, never>;

/** Interview effort per requisition, or rolled up per client. */
export interface InsightEffortBoardReadResponse {
  readonly rows: readonly ({
    readonly job_id: string | null;
    readonly title: string | null;
    readonly hiring_unit_id: string;
    readonly rounds_scheduled: number;
    readonly events_scheduled: number;
    readonly rounds_completed: number;
    readonly no_shows: number;
    readonly cancellations: number;
    readonly short_notice: number;
  })[];
  readonly truncated: boolean;
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightEffortBoardReadRequest = Record<string, never>;

/** Pipeline and activity facts per requisition, for the jobs list’s columns and filters. */
export interface InsightJobBoardReadResponse {
  readonly scope: "own" | "team" | "all";
  readonly jobs: readonly ({
    readonly job_id: string;
    readonly candidates: number;
    readonly interviewing: number;
    readonly blocked: number;
    readonly last_activity_at: string | null;
    readonly attention: readonly ("no_candidates" | "stalled" | "feedback_pending")[];
    readonly last_movement_at: string | null;
    readonly inaction: "one_month" | "two_months" | "beyond" | null;
    readonly stages?: readonly {
      readonly core_stage: string;
      readonly moving: number;
      readonly blocked: number;
    }[];
    readonly blockers?: readonly ({
      readonly blocked_by: "client" | "candidate" | "us";
      readonly count: number;
      readonly oldest_days: number;
    })[];
  })[];
  readonly counts: Readonly<Record<string, number>>;
  readonly inaction: Readonly<Record<string, number>>;
  readonly totals: {
    readonly jobs: number;
    readonly candidates: number;
    readonly interviewing: number;
  };
}

export type InsightJobBoardReadRequest = Record<string, never>;

/** How much interviewing a requisition has actually cost — §7.3. */
export interface InsightJobEffortReadResponse {
  readonly jobId: string;
  readonly rounds_scheduled: number;
  readonly events_scheduled: number;
  readonly rounds_completed: number;
  readonly still_to_happen: number;
  readonly cancelled_unattributed: number;
  readonly attributed: {
    readonly candidate: {
      readonly no_notice: number;
      readonly with_notice: number;
    };
    readonly panel: {
      readonly no_notice: number;
      readonly with_notice: number;
    };
  };
  readonly short_notice: {
    readonly candidate: number;
    readonly panel: number;
  };
  readonly last_activity_at: string | null;
}

export type InsightJobEffortReadRequest = Record<string, never>;

/** Where every requisition on the desk stands — a facts table, and nothing that instructs. */
export interface InsightStandupDeskResponse {
  readonly scope: string;
  readonly rows: readonly ({
    readonly job_id: string;
    readonly job_title: string;
    readonly hiring_unit_id: string;
    readonly status: string;
    readonly openings: number | null;
    readonly owner_membership_id: string | null;
    readonly opened_at: string | null;
    readonly live: number;
    readonly by_stage: {
      readonly sourced: number;
      readonly screened: number;
      readonly submitted: number;
      readonly shortlisted: number;
      readonly interviewing: number;
      readonly offered: number;
      readonly accepted: number;
      readonly joined: number;
    };
    readonly closed: number;
    readonly blocked: number;
    readonly last_submission_at: string | null;
  })[];
  readonly truncated: boolean;
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightStandupDeskRequest = Record<string, never>;

/** Median days blocked in each stage, and who the days were owed to. */
export interface InsightTimeLeakReadResponse {
  readonly scope: string;
  readonly stages: readonly ({
    readonly core_stage: string;
    readonly blocked_days: number | null;
    readonly spans: number;
    readonly mostly: "client" | "candidate" | "us" | null;
  })[];
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightTimeLeakReadRequest = Record<string, never>;

/** Today’s schedulings, and the ones that will not happen unless somebody acts. */
export interface InsightTodayReadResponse {
  readonly scope: "own" | "team" | "all";
  readonly day: string;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly schedulings: readonly ({
    readonly occurrence_id: string;
    readonly interview_id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly job_title: string | null;
    readonly hiring_unit_id: string;
    readonly candidate_profile_id: string;
    readonly owner_membership_id: string | null;
    readonly round_no: number;
    readonly round_label: string;
    readonly mode: string;
    readonly scheduled_at: string;
    readonly duration_minutes: number | null;
    readonly core_outcome: string;
    readonly outcome_recorded_at: string | null;
    readonly link_source: string;
    readonly candidate_response: string | null;
    readonly panel_size: number;
    readonly panel_unanswered: number;
  })[];
  readonly exceptions: readonly ({
    readonly kind: "outcome_missing" | "unconfirmed" | "link_pending" | "double_booked" | "panel_unreliable";
    readonly occurrence_id: string;
    readonly with_occurrence_id: string | null;
    readonly bites_at: string;
    readonly fix_group: string;
    readonly who: "candidate" | "panel" | "both" | null;
    readonly missed: number | null;
    readonly of: number | null;
  })[];
  readonly truncated: boolean;
  readonly visible_requisitions: number;
  readonly total_requisitions: number;
}

export type InsightTodayReadRequest = Record<string, never>;

/** The mailboxes this agency can send through, and whether each one works. */
export interface OutreachEmailSenderListResponse {
  readonly senders: readonly ({
    readonly id: string;
    readonly name: string;
    readonly provider: "smtp" | "google" | "microsoft";
    readonly from_address: string;
    readonly from_name: string | null;
    readonly reply_to: string | null;
    readonly host: string | null;
    readonly port: number | null;
    readonly encryption: "none" | "starttls" | "tls";
    readonly username: string | null;
    readonly status: "unverified" | "verified" | "disabled";
    readonly verified_at: string | null;
    readonly last_error_code: string | null;
    readonly last_attempt_at: string | null;
    readonly is_default: boolean;
    readonly has_credential: boolean;
  })[];
  readonly can_store_credentials: boolean;
  readonly recipient_allowlist: readonly string[] | null;
  readonly platform_fallback: {
    readonly from_address: string;
    readonly replies_come_back: boolean;
  } | null;
}

export type OutreachEmailSenderListRequest = Record<string, never>;

/** Records or amends a mailbox, and the password it sends with. */
export interface OutreachEmailSenderSaveResponse {
  readonly id: string;
  readonly status: "unverified" | "verified" | "disabled";
}

export type OutreachEmailSenderSaveRequest = {
  readonly id?: string;
  readonly name: string;
  readonly provider: "smtp";
  readonly fromAddress: string;
  readonly fromName?: string | null;
  readonly replyTo?: string | null;
  readonly host: string;
  readonly port: number;
  readonly encryption: "none" | "starttls" | "tls";
  readonly username: string;
  readonly password?: string;
  readonly isDefault?: boolean;
};

/** Turns a mailbox off, or back on for re-testing. */
export interface OutreachEmailSenderSetStatusResponse {
  readonly status: "unverified" | "verified" | "disabled";
}

export type OutreachEmailSenderSetStatusRequest = {
  readonly status: "disabled" | "unverified";
};

/** Sends a test message, and marks the sender working if it arrives. */
export interface OutreachEmailSenderVerifyResponse {
  readonly status: "unverified" | "verified" | "disabled";
  readonly verified_at: string | null;
  readonly error_code: string | null;
}

export type OutreachEmailSenderVerifyRequest = {
  readonly to: string;
};

/** Whether the invitation for one scheduling reached each person it was sent to. */
export interface OutreachInterviewInvitationListResponse {
  readonly deliveries: readonly ({
    readonly invitee_kind: "candidate" | "member" | "client_contact" | "panel_guest";
    readonly invitee_id: string;
    readonly status: "queued" | "sent" | "failed" | "suppressed";
    readonly abandoned: boolean;
    readonly last_error_code: string | null;
    readonly sent_at: string | null;
    readonly method: string;
    readonly sequence: number;
    readonly roster: boolean;
  })[];
}

export type OutreachInterviewInvitationListRequest = Record<string, never>;

/** Every message this product sends, and whether this agency sends it. */
export interface OutreachMessageSettingListResponse {
  readonly settings: readonly ({
    readonly key: string;
    readonly category: "tenant" | "membership" | "job" | "application" | "submission" | "interview" | "candidate_profile" | "system";
    readonly audience: string;
    readonly description: string;
    readonly trigger: string;
    readonly enabled: boolean;
    readonly decided_by: "agency" | "platform" | "default";
    readonly agency_choice: boolean | null;
    readonly inherited: boolean;
  })[];
}

export type OutreachMessageSettingListRequest = Record<string, never>;

/** Turns a message on or off for this agency, or hands the decision back. */
export interface OutreachMessageSettingSetResponse {
  readonly key: string;
  readonly enabled: boolean | null;
}

export type OutreachMessageSettingSetRequest = {
  readonly enabled: boolean | null;
};

/** Every message the product can send, and which of them have been edited. */
export interface OutreachMessageTemplateListResponse {
  readonly templates: readonly ({
    readonly key: string;
    readonly category: "tenant" | "membership" | "job" | "application" | "submission" | "interview" | "candidate_profile" | "system";
    readonly audience: "candidate" | "client_contact" | "member" | "operator";
    readonly dispatch: "automatic" | "optional" | "manual";
    readonly description: string;
    readonly trigger: string;
    readonly edited: boolean;
    readonly edited_at: string | null;
    readonly subject: string;
    readonly enabled: boolean;
    readonly switch_key: string;
    readonly sender_purpose: "transactional" | "candidate_facing" | "client_facing" | "credential";
    readonly sender_purpose_default: "transactional" | "candidate_facing" | "client_facing" | "credential";
    readonly sender_moved: boolean;
  })[];
  readonly categories: readonly ("tenant" | "membership" | "job" | "application" | "submission" | "interview" | "candidate_profile" | "system")[];
}

export type OutreachMessageTemplateListRequest = Record<string, never>;

/** One message: its content, the variables it offers, and what it looks like. */
export interface OutreachMessageTemplateReadResponse {
  readonly key: string;
  readonly category: "tenant" | "membership" | "job" | "application" | "submission" | "interview" | "candidate_profile" | "system";
  readonly audience: "candidate" | "client_contact" | "member" | "operator";
  readonly dispatch: "automatic" | "optional" | "manual";
  readonly description: string;
  readonly trigger: string;
  readonly edited: boolean;
  readonly edited_at: string | null;
  readonly subject: string;
  readonly enabled: boolean;
  readonly switch_key: string;
  readonly sender_purpose: "transactional" | "candidate_facing" | "client_facing" | "credential";
  readonly sender_purpose_default: "transactional" | "candidate_facing" | "client_facing" | "credential";
  readonly sender_moved: boolean;
  readonly body: string;
  readonly default_subject: string;
  readonly default_body: string;
  readonly variables: readonly ({
    readonly name: string;
    readonly label: string;
    readonly description: string;
    readonly sensitivity: "public" | "internal" | "commercial";
    readonly sample: string;
    readonly fallback: string | null;
  })[];
  readonly subject_max_length: number;
  readonly subject_visible_length: number;
  readonly body_max_length: number;
  readonly marketing_allowed: boolean;
  readonly default_enabled: boolean;
  readonly enabled_set: boolean | null;
  readonly recipient: "candidate" | "client_contact" | "member" | "panel_guest" | "operator";
  readonly preview: {
    readonly subject: string;
    readonly text: string;
    readonly html: string;
  } | null;
}

export type OutreachMessageTemplateReadRequest = Record<string, never>;

/** Puts the shipped wording back by removing the edit. */
export interface OutreachMessageTemplateRevertResponse {
  readonly key: string;
  readonly category: "tenant" | "membership" | "job" | "application" | "submission" | "interview" | "candidate_profile" | "system";
  readonly audience: "candidate" | "client_contact" | "member" | "operator";
  readonly dispatch: "automatic" | "optional" | "manual";
  readonly description: string;
  readonly trigger: string;
  readonly edited: boolean;
  readonly edited_at: string | null;
  readonly subject: string;
  readonly enabled: boolean;
  readonly switch_key: string;
  readonly sender_purpose: "transactional" | "candidate_facing" | "client_facing" | "credential";
  readonly sender_purpose_default: "transactional" | "candidate_facing" | "client_facing" | "credential";
  readonly sender_moved: boolean;
  readonly body: string;
  readonly default_subject: string;
  readonly default_body: string;
  readonly variables: readonly ({
    readonly name: string;
    readonly label: string;
    readonly description: string;
    readonly sensitivity: "public" | "internal" | "commercial";
    readonly sample: string;
    readonly fallback: string | null;
  })[];
  readonly subject_max_length: number;
  readonly subject_visible_length: number;
  readonly body_max_length: number;
  readonly marketing_allowed: boolean;
  readonly default_enabled: boolean;
  readonly enabled_set: boolean | null;
  readonly recipient: "candidate" | "client_contact" | "member" | "panel_guest" | "operator";
  readonly preview: {
    readonly subject: string;
    readonly text: string;
    readonly html: string;
  } | null;
}

export type OutreachMessageTemplateRevertRequest = Record<string, never>;

/** Replaces what this message says, and whether it sends at all. */
export interface OutreachMessageTemplateSaveResponse {
  readonly key: string;
  readonly category: "tenant" | "membership" | "job" | "application" | "submission" | "interview" | "candidate_profile" | "system";
  readonly audience: "candidate" | "client_contact" | "member" | "operator";
  readonly dispatch: "automatic" | "optional" | "manual";
  readonly description: string;
  readonly trigger: string;
  readonly edited: boolean;
  readonly edited_at: string | null;
  readonly subject: string;
  readonly enabled: boolean;
  readonly switch_key: string;
  readonly sender_purpose: "transactional" | "candidate_facing" | "client_facing" | "credential";
  readonly sender_purpose_default: "transactional" | "candidate_facing" | "client_facing" | "credential";
  readonly sender_moved: boolean;
  readonly body: string;
  readonly default_subject: string;
  readonly default_body: string;
  readonly variables: readonly ({
    readonly name: string;
    readonly label: string;
    readonly description: string;
    readonly sensitivity: "public" | "internal" | "commercial";
    readonly sample: string;
    readonly fallback: string | null;
  })[];
  readonly subject_max_length: number;
  readonly subject_visible_length: number;
  readonly body_max_length: number;
  readonly marketing_allowed: boolean;
  readonly default_enabled: boolean;
  readonly enabled_set: boolean | null;
  readonly recipient: "candidate" | "client_contact" | "member" | "panel_guest" | "operator";
  readonly preview: {
    readonly subject: string;
    readonly text: string;
    readonly html: string;
  } | null;
}

export type OutreachMessageTemplateSaveRequest = {
  readonly subject: string;
  readonly body: string;
  readonly enabled?: boolean;
};

/** Moves one message to a different mailbox, or puts it back on its own category. */
export interface OutreachMessageTemplateSetSenderResponse {
  readonly key: string;
  readonly sender_purpose: "transactional" | "candidate_facing" | "client_facing" | "credential";
  readonly sender_purpose_default: "transactional" | "candidate_facing" | "client_facing" | "credential";
  readonly sender_moved: boolean;
}

export type OutreachMessageTemplateSetSenderRequest = {
  readonly senderPurpose: "transactional" | "candidate_facing" | "client_facing" | "credential" | null;
};

/** Sends this message to an address, filled in with its sample values. */
export interface OutreachMessageTemplateTestSendResponse {
  readonly sent: boolean;
  readonly from_address: string | null;
  readonly error_code: string | null;
}

export type OutreachMessageTemplateTestSendRequest = {
  readonly to: string;
};

/** A first draft of a message to a candidate, for a recruiter to read and edit. */
export interface OutreachMessageDraftResponse {
  readonly subject: string;
  readonly body: string;
  readonly sample: boolean;
}

export type OutreachMessageDraftRequest = {
  readonly candidateProfileId: string;
  readonly jobId?: string;
  readonly intent: string;
};

/** The mail this agency has sent, newest first, and what became of it. */
export interface OutreachMessageListResponse {
  readonly messages: readonly ({
    readonly id: string;
    readonly template_key: string;
    readonly to_address: string;
    readonly to_name: string | null;
    readonly subject: string;
    readonly status: "queued" | "sent" | "failed" | "suppressed";
    readonly attempts: number;
    readonly abandoned: boolean;
    readonly last_error_code: string | null;
    readonly sender_source: string | null;
    readonly sent_at: string | null;
    readonly created_at: string;
  })[];
  readonly needs_attention: number;
}

export type OutreachMessageListRequest = Record<string, never>;

/** Puts a failed message back in the queue. */
export interface OutreachMessageRetryResponse {
  readonly id: string;
  readonly status: "queued" | "sent" | "failed" | "suppressed";
  readonly attempts: number;
}

export type OutreachMessageRetryRequest = Record<string, never>;

/** Write to a candidate from their record, recorded like every other message. */
export interface OutreachMessageSendResponse {
  readonly messageId: string | null;
  readonly outcome: "queued" | "suppressed" | "disabled" | "duplicate";
}

export type OutreachMessageSendRequest = {
  readonly applicationId: string;
  readonly candidateProfileId: string;
  readonly subject: string;
  readonly body: string;
  readonly documentIds?: readonly string[];
};

/** Removes a sending mailbox nobody uses any more. */
export interface OutreachPlatformEmailSenderDeleteResponse {
  readonly id: string;
}

export type OutreachPlatformEmailSenderDeleteRequest = Record<string, never>;

/** The mailbox every tenant falls back to. */
export interface OutreachPlatformEmailSenderReadResponse {
  readonly senders: readonly ({
    readonly id: string;
    readonly name: string;
    readonly provider: "smtp" | "ses";
    readonly from_address: string;
    readonly host: string | null;
    readonly port: number | null;
    readonly encryption: "none" | "starttls" | "tls" | null;
    readonly username: string | null;
    readonly configuration_set: string | null;
    readonly purpose: readonly ("transactional" | "candidate_facing" | "client_facing" | "credential")[];
    readonly status: "unverified" | "verified" | "disabled";
    readonly verified_at: string | null;
    readonly last_error_code: string | null;
    readonly last_attempt_at: string | null;
    readonly is_default: boolean;
    readonly has_credential: boolean;
  })[];
  readonly can_store_credentials: boolean;
  readonly suggested_addresses: readonly ({
    readonly address: string;
    readonly purpose: "transactional" | "candidate_facing" | "client_facing" | "credential";
    readonly configuration_set: string | null;
  })[];
}

export type OutreachPlatformEmailSenderReadRequest = Record<string, never>;

/** Records the mailbox every tenant falls back to. */
export interface OutreachPlatformEmailSenderSaveResponse {
  readonly id: string;
  readonly status: "unverified" | "verified" | "disabled";
}

export type OutreachPlatformEmailSenderSaveRequest = {
  readonly id?: string;
  readonly name: string;
  readonly provider?: "smtp" | "ses";
  readonly fromAddress: string;
  readonly host?: string;
  readonly port?: number;
  readonly encryption?: "none" | "starttls" | "tls";
  readonly username?: string;
  readonly password?: string;
  readonly configurationSet?: string;
  readonly purpose?: readonly ("transactional" | "candidate_facing" | "client_facing" | "credential")[];
  readonly isDefault?: boolean;
};

/** Sends a test message, and marks the default working if it goes. */
export interface OutreachPlatformEmailSenderVerifyResponse {
  readonly status: "unverified" | "verified" | "disabled";
  readonly verified_at: string | null;
  readonly error_code: string | null;
}

export type OutreachPlatformEmailSenderVerifyRequest = {
  readonly to: string;
};

/** What became of this tenant’s recent mail — delivery state only, never content. */
export interface OutreachPlatformMessageReadResponse {
  readonly messages: readonly ({
    readonly id: string;
    readonly template_key: string;
    readonly status: "queued" | "sent" | "failed" | "suppressed";
    readonly attempts: number;
    readonly last_error_code: string | null;
    readonly sender_source: string | null;
    readonly sent_at: string | null;
    readonly created_at: string;
  })[];
  readonly has_failure: boolean;
}

export type OutreachPlatformMessageReadRequest = Record<string, never>;

/** What this requisition sends when a candidature reaches each step. */
export interface OutreachStageMessageListResponse {
  readonly rules: readonly {
    readonly step_id: string;
    readonly subject: string;
    readonly body: string;
    readonly document_ids: readonly string[];
    readonly enabled: boolean;
  }[];
}

export type OutreachStageMessageListRequest = Record<string, never>;

/** Sets what this requisition sends when a candidature reaches one step. */
export interface OutreachStageMessageSaveResponse {
  readonly step_id: string;
  readonly subject: string;
  readonly body: string;
  readonly document_ids: readonly string[];
  readonly enabled: boolean;
}

export type OutreachStageMessageSaveRequest = {
  readonly subject: string;
  readonly body: string;
  readonly documentIds?: readonly string[];
  readonly enabled: boolean;
};

/** This pursuit’s screening questions and whatever has been answered so far. */
export interface PipelineApplicationAnswerListResponse {
  readonly answers: readonly ({
    readonly question_id: string;
    readonly prompt: string;
    readonly answer_type: "text" | "boolean" | "number" | "single_choice";
    readonly options: readonly string[];
    readonly required: boolean;
    readonly answer_text: string | null;
  })[];
  readonly unanswered_required: number;
}

export type PipelineApplicationAnswerListRequest = Record<string, never>;

/** Records what a candidate said to this requisition’s screening questions. */
export interface PipelineApplicationAnswerSaveResponse {
  readonly saved: number;
  readonly unanswered_required: number;
}

export type PipelineApplicationAnswerSaveRequest = {
  readonly answers: readonly {
    readonly question_id: string;
    readonly answer_text: string;
  }[];
};

/** Records that a candidature is not moving, and who is holding it up. */
export interface PipelineApplicationBlockResponse {
  readonly applicationId: string;
  readonly blockId: string;
  readonly blocked_by: "client" | "candidate" | "us";
  readonly current_core_stage: string;
}

export type PipelineApplicationBlockRequest = {
  readonly blockerId: string;
};

/** Moves many applications to one stage, reporting what happened to each. */
export interface PipelineApplicationBulkChangeStageResponse {
  readonly moved: number;
  readonly skipped: number;
  readonly results: readonly ({
    readonly applicationId: string;
    readonly outcome: "moved" | "not_found" | "closed" | "already_there";
  })[];
}

export type PipelineApplicationBulkChangeStageRequest = {
  readonly applicationIds: readonly string[];
  readonly toStepId: string;
  readonly reasonCode?: string;
  readonly reasonNote?: string;
};

/** Ends several candidatures with one reason, each keeping the stage it reached. */
export interface PipelineApplicationBulkCloseResponse {
  readonly closed: number;
  readonly skipped: readonly {
    readonly applicationId: string;
    readonly code: string;
  }[];
}

export type PipelineApplicationBulkCloseRequest = {
  readonly applicationIds: readonly string[];
  readonly reasonId: string;
  readonly reasonCode: "skills_gap" | "experience_level" | "compensation" | "location_or_mode" | "notice_period" | "communication" | "conduct_or_culture" | "eligibility_or_documents" | "unresponsive" | "accepted_elsewhere" | "counter_offer" | "role_closed" | "duplicate_or_error" | "other";
  readonly note?: string;
  readonly notifyCandidate?: boolean;
};

/** Moves an application to another stage — flow D2. */
export interface PipelineApplicationChangeStageResponse {
  readonly applicationId: string;
  readonly current_core_stage: "sourced" | "screened" | "submitted" | "shortlisted" | "interviewing" | "offered" | "accepted" | "joined" | "rejected" | "withdrawn";
  readonly current_step_id: string;
  readonly status: "active" | "rejected" | "withdrawn" | "placed";
}

export type PipelineApplicationChangeStageRequest = {
  readonly toStepId: string;
  readonly reasonCode?: string;
  readonly reasonNote?: string;
};

/** Ends a candidature with a reason, keeping the stage it reached. */
export interface PipelineApplicationCloseResponse {
  readonly applicationId: string;
  readonly status: string;
  readonly current_core_stage: string;
  readonly closed_at: string;
}

export type PipelineApplicationCloseRequest = {
  readonly reasonId: string;
  readonly reasonCode: "skills_gap" | "experience_level" | "compensation" | "location_or_mode" | "notice_period" | "communication" | "conduct_or_culture" | "eligibility_or_documents" | "unresponsive" | "accepted_elsewhere" | "counter_offer" | "role_closed" | "duplicate_or_error" | "other";
  readonly note?: string;
  readonly notifyCandidate?: boolean;
};

/** Puts a candidate into a job’s pipeline — flow D1. */
export interface PipelineApplicationCreateResponse {
  readonly applicationId: string;
  readonly current_core_stage: "sourced" | "screened" | "submitted" | "shortlisted" | "interviewing" | "offered" | "accepted" | "joined" | "rejected" | "withdrawn";
  readonly current_step_id: string;
}

export type PipelineApplicationCreateRequest = {
  readonly jobId: string;
  readonly candidateId: string;
  readonly source?: "search" | "recruiter" | "application" | "referral" | "import" | "api";
};

/** Applications in this tenant — one job’s board, one person’s history, or a book of work. */
export interface PipelineApplicationListResponse {
  readonly applications: readonly ({
    readonly id: string;
    readonly job_id: string;
    readonly candidate_profile_id: string;
    readonly current_step_id: string | null;
    readonly current_core_stage: "sourced" | "screened" | "submitted" | "shortlisted" | "interviewing" | "offered" | "accepted" | "joined" | "rejected" | "withdrawn";
    readonly current_step_label: string | null;
    readonly status: "active" | "rejected" | "withdrawn" | "placed";
    readonly blocker_label: string | null;
    readonly blocker_colour: string | null;
    readonly blocker_owner: "client" | "candidate" | "us" | null;
    readonly blocker_days: number | null;
    readonly blocker_source: "system" | "human" | null;
    readonly source: "search" | "recruiter" | "application" | "referral" | "import" | "api";
    readonly owner_membership_id: string | null;
    readonly owned_by_me: boolean;
    readonly match_score: number | null;
    readonly applied_at: string;
    readonly last_stage_change_at: string;
    readonly closed_at: string | null;
  })[];
  readonly nextCursor: string | null;
}

export type PipelineApplicationListRequest = Record<string, never>;

/** One application, with every stage it has moved through. */
export interface PipelineApplicationReadResponse {
  readonly application: {
    readonly id: string;
    readonly job_id: string;
    readonly candidate_profile_id: string;
    readonly current_step_id: string | null;
    readonly current_core_stage: "sourced" | "screened" | "submitted" | "shortlisted" | "interviewing" | "offered" | "accepted" | "joined" | "rejected" | "withdrawn";
    readonly current_step_label: string | null;
    readonly status: "active" | "rejected" | "withdrawn" | "placed";
    readonly blocker_label: string | null;
    readonly blocker_colour: string | null;
    readonly blocker_owner: "client" | "candidate" | "us" | null;
    readonly blocker_days: number | null;
    readonly blocker_source: "system" | "human" | null;
    readonly source: "search" | "recruiter" | "application" | "referral" | "import" | "api";
    readonly owner_membership_id: string | null;
    readonly owned_by_me: boolean;
    readonly match_score: number | null;
    readonly applied_at: string;
    readonly last_stage_change_at: string;
    readonly closed_at: string | null;
  };
  readonly blocker: {
    readonly block_id: string;
    readonly definition_id: string | null;
    readonly label: string;
    readonly blocked_by: "client" | "candidate" | "us";
    readonly source: "system" | "human";
    readonly raised_by_membership_id: string | null;
    readonly since: string;
    readonly days: number;
  } | null;
  readonly blocks: readonly ({
    readonly block_id: string;
    readonly label: string;
    readonly blocked_by: "client" | "candidate" | "us";
    readonly source: "system" | "human";
    readonly raised_by_membership_id: string | null;
    readonly since: string;
    readonly until: string | null;
    readonly ended_by_membership_id: string | null;
    readonly ended_reason: string | null;
  })[];
  readonly ending: {
    readonly label: string;
    readonly outcome: "rejected" | "withdrawn";
    readonly decided_by: string | null;
    readonly reason_code: string | null;
    readonly closed_at: string;
  } | null;
  readonly history: readonly ({
    readonly id: string;
    readonly from_step_label: string | null;
    readonly to_step_label: string;
    readonly to_core_stage: "sourced" | "screened" | "submitted" | "shortlisted" | "interviewing" | "offered" | "accepted" | "joined" | "rejected" | "withdrawn";
    readonly reason_code: string | null;
    readonly reason_note: string | null;
    readonly actor_membership_id: string | null;
    readonly is_ai_assisted: boolean;
    readonly occurred_at: string;
  })[];
  readonly match: {
    readonly score: number | null;
    readonly band: "strong" | "good" | "fair" | "weak" | null;
    readonly factors: readonly {
      readonly factor: string;
      readonly ratio: number;
      readonly weight: number;
      readonly points: number;
      readonly evidence: string;
      readonly missing: readonly string[];
      readonly unknown: boolean;
    }[];
    readonly stale: boolean;
    readonly skills_at: string | null;
    readonly computed_at: string;
  } | null;
}

export type PipelineApplicationReadRequest = Record<string, never>;

/** Brings an ended candidature back to the stage it left. */
export interface PipelineApplicationReviveResponse {
  readonly applicationId: string;
  readonly current_core_stage: string;
  readonly gap_days: number;
}

export type PipelineApplicationReviveRequest = {
  readonly note?: string;
};

/** Clears the blocker on a candidature — it is moving again. */
export interface PipelineApplicationUnblockResponse {
  readonly applicationId: string;
  readonly cleared: boolean;
}

export type PipelineApplicationUnblockRequest = Record<string, never>;

/** Takes an interview’s time out of a panellist’s offered window. */
export interface PipelineAvailabilityBookResponse {
  readonly id: string;
}

export type PipelineAvailabilityBookRequest = {
  readonly availabilityId: string;
  readonly starts_at: string;
  readonly ends_at: string;
};

/** What this job’s panellists have offered, and what is left of it. */
export interface PipelineAvailabilityListResponse {
  readonly windows: readonly ({
    readonly id: string;
    readonly display_name: string;
    readonly contact_id: string | null;
    readonly membership_id: string | null;
    readonly starts_at: string;
    readonly ends_at: string;
    readonly timezone: string | null;
    readonly note: string | null;
    readonly free: readonly {
      readonly starts_at: string;
      readonly ends_at: string;
    }[];
    readonly taken: readonly {
      readonly occurrence_id: string;
      readonly job_id: string;
      readonly round_label: string;
      readonly starts_at: string;
      readonly ends_at: string;
    }[];
  })[];
}

export type PipelineAvailabilityListRequest = Record<string, never>;

/** Records time a panellist has offered, against the person rather than the job. */
export interface PipelineAvailabilityRecordResponse {
  readonly id: string;
}

export type PipelineAvailabilityRecordRequest = {
  readonly starts_at: string;
  readonly ends_at: string;
  readonly timezone?: string | null;
  readonly note?: string | null;
};

/** The panellist has taken the offer back — a separate act from an interview moving. */
export interface PipelineAvailabilityWithdrawResponse {
  readonly id: string;
  readonly withdrawn: boolean;
}

export type PipelineAvailabilityWithdrawRequest = Record<string, never>;

/** Computes a compensation package forward, backward from a take-home, or with pinned components. */
export interface PipelineCompensationComputeResponse {
  readonly headline: string;
  readonly residual: string | null;
  readonly breakdown: {
    readonly country: string;
    readonly currency: string;
    readonly rulesetVersion: string;
    readonly quotingBasis: "cost_inclusive" | "gross_exclusive";
    readonly components: readonly {
      readonly key: string;
      readonly kind: string;
      readonly annual: string;
      readonly basis?: string;
    }[];
    readonly costToEmployer: string;
    readonly gross: string;
    readonly employeeDeductions: string;
    readonly tax: string;
    readonly net: string;
    readonly findings: readonly ({
      readonly code: string;
      readonly severity: "blocking" | "advisory";
      readonly messageKey: string;
      readonly values?: Readonly<Record<string, string | number>>;
    })[];
  };
  readonly choices: readonly ({
    readonly key: string;
    readonly labelKey: string;
    readonly type: "percent" | "money" | "enum" | "boolean";
    readonly options?: readonly {
      readonly value: string;
      readonly labelKey: string;
    }[];
    readonly default: string | number | boolean;
    readonly assumption?: boolean;
  })[];
}

export type PipelineCompensationComputeRequest = {
  readonly mode?: "forward" | "reverse" | "adjust";
  readonly headline?: string;
  readonly targetNet?: string;
  readonly choices?: Readonly<Record<string, string | number | boolean>>;
  readonly overrides?: Readonly<Record<string, string>>;
  readonly countryIso2?: string;
  readonly currency?: string;
  readonly on?: string;
};

/** Answers a question about one candidacy, from its own history. */
export interface PipelineCopilotAskResponse {
  readonly decisionId: string;
  readonly reply: string;
  readonly citations: readonly string[];
  readonly refused: boolean;
  readonly ai_sample: boolean;
  readonly context: {
    readonly kind: "job" | "candidate" | "application" | "desk";
    readonly label: string;
    readonly facts: readonly {
      readonly label: string;
      readonly value: string;
    }[];
  };
}

export type PipelineCopilotAskRequest = {
  readonly question: string;
};

/** The agency’s clause library. */
export interface PipelineDocumentClauseReadResponse {
  readonly clauses: readonly ({
    readonly id: string;
    readonly key: string;
    readonly label: string;
    readonly body: string;
    readonly necessity: "required" | "optional" | "conditional";
    readonly condition: {
      readonly field: string;
      readonly op: "eq" | "neq" | "gte" | "present";
      readonly value?: string | number | boolean;
    } | null;
    readonly version: number;
    readonly is_active: boolean;
  })[];
}

export type PipelineDocumentClauseReadRequest = Record<string, never>;

/** Creates or amends a clause. Amending bumps its version. */
export interface PipelineDocumentClauseSaveResponse {
  readonly clause: {
    readonly id: string;
    readonly key: string;
    readonly label: string;
    readonly body: string;
    readonly necessity: "required" | "optional" | "conditional";
    readonly condition: {
      readonly field: string;
      readonly op: "eq" | "neq" | "gte" | "present";
      readonly value?: string | number | boolean;
    } | null;
    readonly version: number;
    readonly is_active: boolean;
  };
}

export type PipelineDocumentClauseSaveRequest = {
  readonly label: string;
  readonly body: string;
  readonly necessity: "required" | "optional" | "conditional";
  readonly condition?: {
    readonly field: string;
    readonly op: "eq" | "neq" | "gte" | "present";
    readonly value?: string | number | boolean;
  } | null;
  readonly isActive?: boolean;
};

/** Opens a document collection link and lists what is being asked for. */
export interface PipelineDocumentLinkOpenResponse {
  readonly collection: {
    readonly request_id: string;
    readonly set_label: string;
    readonly expires_at: string;
    readonly is_complete: boolean;
    readonly requirements: readonly ({
      readonly id: string;
      readonly kind: "identity_pan" | "identity_aadhaar" | "identity_passport" | "address_proof" | "photograph" | "experience_letter" | "relieving_letter" | "payslip" | "bank_statement" | "previous_offer_letter" | "degree_certificate" | "marksheet" | "other";
      readonly label: string;
      readonly hint: string | null;
      readonly is_mandatory: boolean;
      readonly copies_expected: number;
      readonly position: number;
    })[];
    readonly submissions: readonly ({
      readonly id: string;
      readonly document_requirement_id: string;
      readonly filename: string;
      readonly mime_type: string;
      readonly byte_size: number;
      readonly status: "submitted" | "accepted" | "rejected";
      readonly review_note: string | null;
      readonly reviewed_at: string | null;
      readonly uploaded_at: string;
    })[];
  };
}

export type PipelineDocumentLinkOpenRequest = {
  readonly token: string;
};

/** Records a document a candidate uploaded against one requirement. */
export interface PipelineDocumentLinkSubmitResponse {
  readonly collection: {
    readonly request_id: string;
    readonly set_label: string;
    readonly expires_at: string;
    readonly is_complete: boolean;
    readonly requirements: readonly ({
      readonly id: string;
      readonly kind: "identity_pan" | "identity_aadhaar" | "identity_passport" | "address_proof" | "photograph" | "experience_letter" | "relieving_letter" | "payslip" | "bank_statement" | "previous_offer_letter" | "degree_certificate" | "marksheet" | "other";
      readonly label: string;
      readonly hint: string | null;
      readonly is_mandatory: boolean;
      readonly copies_expected: number;
      readonly position: number;
    })[];
    readonly submissions: readonly ({
      readonly id: string;
      readonly document_requirement_id: string;
      readonly filename: string;
      readonly mime_type: string;
      readonly byte_size: number;
      readonly status: "submitted" | "accepted" | "rejected";
      readonly review_note: string | null;
      readonly reviewed_at: string | null;
      readonly uploaded_at: string;
    })[];
  };
}

export type PipelineDocumentLinkSubmitRequest = {
  readonly token: string;
  readonly requirementId: string;
  readonly filename: string;
  readonly mimeType: "application/pdf" | "image/jpeg" | "image/png" | "image/heic" | "image/webp";
  readonly content: string;
};

/** Raises a document collection for an application and returns its one-time link token. */
export interface PipelineDocumentRequestCreateResponse {
  readonly request: {
    readonly id: string;
    readonly application_id: string;
    readonly document_set_id: string;
    readonly set_label: string;
    readonly expires_at: string;
    readonly opened_at: string | null;
    readonly completed_at: string | null;
    readonly revoked_at: string | null;
    readonly is_complete: boolean;
    readonly outstanding: number;
    readonly requirements: readonly ({
      readonly id: string;
      readonly kind: "identity_pan" | "identity_aadhaar" | "identity_passport" | "address_proof" | "photograph" | "experience_letter" | "relieving_letter" | "payslip" | "bank_statement" | "previous_offer_letter" | "degree_certificate" | "marksheet" | "other";
      readonly label: string;
      readonly hint: string | null;
      readonly is_mandatory: boolean;
      readonly copies_expected: number;
      readonly position: number;
    })[];
    readonly submissions: readonly ({
      readonly id: string;
      readonly document_requirement_id: string;
      readonly filename: string;
      readonly mime_type: string;
      readonly byte_size: number;
      readonly status: "submitted" | "accepted" | "rejected";
      readonly review_note: string | null;
      readonly reviewed_at: string | null;
      readonly uploaded_at: string;
    })[];
    readonly created_at: string;
  };
  readonly token: string;
}

export type PipelineDocumentRequestCreateRequest = {
  readonly documentSetId?: string;
  readonly expiresInDays?: number;
};

/** The document collection for one application, with what has arrived. */
export interface PipelineDocumentRequestReadResponse {
  readonly request: {
    readonly id: string;
    readonly application_id: string;
    readonly document_set_id: string;
    readonly set_label: string;
    readonly expires_at: string;
    readonly opened_at: string | null;
    readonly completed_at: string | null;
    readonly revoked_at: string | null;
    readonly is_complete: boolean;
    readonly outstanding: number;
    readonly requirements: readonly ({
      readonly id: string;
      readonly kind: "identity_pan" | "identity_aadhaar" | "identity_passport" | "address_proof" | "photograph" | "experience_letter" | "relieving_letter" | "payslip" | "bank_statement" | "previous_offer_letter" | "degree_certificate" | "marksheet" | "other";
      readonly label: string;
      readonly hint: string | null;
      readonly is_mandatory: boolean;
      readonly copies_expected: number;
      readonly position: number;
    })[];
    readonly submissions: readonly ({
      readonly id: string;
      readonly document_requirement_id: string;
      readonly filename: string;
      readonly mime_type: string;
      readonly byte_size: number;
      readonly status: "submitted" | "accepted" | "rejected";
      readonly review_note: string | null;
      readonly reviewed_at: string | null;
      readonly uploaded_at: string;
    })[];
    readonly created_at: string;
  } | null;
  readonly resolved_set: {
    readonly id: string;
    readonly label: string;
  } | null;
}

export type PipelineDocumentRequestReadRequest = Record<string, never>;

/** Stops a document link working, keeping what has already arrived. */
export interface PipelineDocumentRequestRevokeResponse {
  readonly request: {
    readonly id: string;
    readonly application_id: string;
    readonly document_set_id: string;
    readonly set_label: string;
    readonly expires_at: string;
    readonly opened_at: string | null;
    readonly completed_at: string | null;
    readonly revoked_at: string | null;
    readonly is_complete: boolean;
    readonly outstanding: number;
    readonly requirements: readonly ({
      readonly id: string;
      readonly kind: "identity_pan" | "identity_aadhaar" | "identity_passport" | "address_proof" | "photograph" | "experience_letter" | "relieving_letter" | "payslip" | "bank_statement" | "previous_offer_letter" | "degree_certificate" | "marksheet" | "other";
      readonly label: string;
      readonly hint: string | null;
      readonly is_mandatory: boolean;
      readonly copies_expected: number;
      readonly position: number;
    })[];
    readonly submissions: readonly ({
      readonly id: string;
      readonly document_requirement_id: string;
      readonly filename: string;
      readonly mime_type: string;
      readonly byte_size: number;
      readonly status: "submitted" | "accepted" | "rejected";
      readonly review_note: string | null;
      readonly reviewed_at: string | null;
      readonly uploaded_at: string;
    })[];
    readonly created_at: string;
  };
}

export type PipelineDocumentRequestRevokeRequest = Record<string, never>;

/** Every document set the agency has configured, with its requirements. */
export interface PipelineDocumentSetReadResponse {
  readonly sets: readonly ({
    readonly id: string;
    readonly label: string;
    readonly hiring_unit_id: string | null;
    readonly employment_type: "full_time" | "part_time" | "contract" | "contract_to_hire" | "consultant" | null;
    readonly is_active: boolean;
    readonly requirements: readonly ({
      readonly id: string;
      readonly kind: "identity_pan" | "identity_aadhaar" | "identity_passport" | "address_proof" | "photograph" | "experience_letter" | "relieving_letter" | "payslip" | "bank_statement" | "previous_offer_letter" | "degree_certificate" | "marksheet" | "other";
      readonly label: string;
      readonly hint: string | null;
      readonly is_mandatory: boolean;
      readonly copies_expected: number;
      readonly position: number;
    })[];
  })[];
}

export type PipelineDocumentSetReadRequest = Record<string, never>;

/** Creates or replaces a document set and the documents it asks for. */
export interface PipelineDocumentSetSaveResponse {
  readonly set: {
    readonly id: string;
    readonly label: string;
    readonly hiring_unit_id: string | null;
    readonly employment_type: "full_time" | "part_time" | "contract" | "contract_to_hire" | "consultant" | null;
    readonly is_active: boolean;
    readonly requirements: readonly ({
      readonly id: string;
      readonly kind: "identity_pan" | "identity_aadhaar" | "identity_passport" | "address_proof" | "photograph" | "experience_letter" | "relieving_letter" | "payslip" | "bank_statement" | "previous_offer_letter" | "degree_certificate" | "marksheet" | "other";
      readonly label: string;
      readonly hint: string | null;
      readonly is_mandatory: boolean;
      readonly copies_expected: number;
      readonly position: number;
    })[];
  };
}

export type PipelineDocumentSetSaveRequest = {
  readonly label: string;
  readonly hiringUnitId?: string | null;
  readonly employmentType?: "full_time" | "part_time" | "contract" | "contract_to_hire" | "consultant" | null;
  readonly isActive?: boolean;
  readonly requirements: readonly ({
    readonly kind: "identity_pan" | "identity_aadhaar" | "identity_passport" | "address_proof" | "photograph" | "experience_letter" | "relieving_letter" | "payslip" | "bank_statement" | "previous_offer_letter" | "degree_certificate" | "marksheet" | "other";
    readonly label: string;
    readonly hint?: string | null;
    readonly isMandatory?: boolean;
    readonly copiesExpected?: number;
  })[];
};

/** Accepts a submitted document, or sends it back with a reason. */
export interface PipelineDocumentSubmissionReviewResponse {
  readonly request: {
    readonly id: string;
    readonly application_id: string;
    readonly document_set_id: string;
    readonly set_label: string;
    readonly expires_at: string;
    readonly opened_at: string | null;
    readonly completed_at: string | null;
    readonly revoked_at: string | null;
    readonly is_complete: boolean;
    readonly outstanding: number;
    readonly requirements: readonly ({
      readonly id: string;
      readonly kind: "identity_pan" | "identity_aadhaar" | "identity_passport" | "address_proof" | "photograph" | "experience_letter" | "relieving_letter" | "payslip" | "bank_statement" | "previous_offer_letter" | "degree_certificate" | "marksheet" | "other";
      readonly label: string;
      readonly hint: string | null;
      readonly is_mandatory: boolean;
      readonly copies_expected: number;
      readonly position: number;
    })[];
    readonly submissions: readonly ({
      readonly id: string;
      readonly document_requirement_id: string;
      readonly filename: string;
      readonly mime_type: string;
      readonly byte_size: number;
      readonly status: "submitted" | "accepted" | "rejected";
      readonly review_note: string | null;
      readonly reviewed_at: string | null;
      readonly uploaded_at: string;
    })[];
    readonly created_at: string;
  };
}

export type PipelineDocumentSubmissionReviewRequest = {
  readonly status: "accepted" | "rejected";
  readonly reviewNote?: string;
};

/** Every document template the agency has, with the clauses each composes. */
export interface PipelineDocumentTemplateReadResponse {
  readonly templates: readonly ({
    readonly id: string;
    readonly kind: "employment_contract" | "offer_letter" | "conversion_letter" | "addendum";
    readonly label: string;
    readonly employment_type: "full_time" | "part_time" | "contract" | "contract_to_hire" | "consultant" | null;
    readonly instrument: "binding" | "invitation";
    readonly requires_signature: boolean;
    readonly preamble: string | null;
    readonly closing: string | null;
    readonly version: number;
    readonly status: "draft" | "live" | "retired";
    readonly clause_keys: readonly string[];
  })[];
}

export type PipelineDocumentTemplateReadRequest = Record<string, never>;

/** Creates or replaces a template and the clauses it composes. */
export interface PipelineDocumentTemplateSaveResponse {
  readonly template: {
    readonly id: string;
    readonly kind: "employment_contract" | "offer_letter" | "conversion_letter" | "addendum";
    readonly label: string;
    readonly employment_type: "full_time" | "part_time" | "contract" | "contract_to_hire" | "consultant" | null;
    readonly instrument: "binding" | "invitation";
    readonly requires_signature: boolean;
    readonly preamble: string | null;
    readonly closing: string | null;
    readonly version: number;
    readonly status: "draft" | "live" | "retired";
    readonly clause_keys: readonly string[];
  };
}

export type PipelineDocumentTemplateSaveRequest = {
  readonly kind: "employment_contract" | "offer_letter" | "conversion_letter" | "addendum";
  readonly label: string;
  readonly employmentType?: "full_time" | "part_time" | "contract" | "contract_to_hire" | "consultant" | null;
  readonly instrument: "binding" | "invitation";
  readonly requiresSignature?: boolean;
  readonly preamble?: string | null;
  readonly closing?: string | null;
  readonly status?: "draft" | "live" | "retired";
  readonly clauseKeys?: readonly string[];
};

/** Adds a note, or a correction to one. Never replaces what was already written. */
export interface PipelineInterviewNoteAddResponse {
  readonly id: string;
  readonly created_at: string;
}

export type PipelineInterviewNoteAddRequest = {
  readonly body_md: string;
  readonly visibility?: "internal" | "hiring_unit";
  readonly occurrence_id?: string | null;
  readonly supersedes_id?: string | null;
  readonly is_feedback?: boolean;
};

/** Every note written on a round, oldest first, corrections included. */
export interface PipelineInterviewNoteListResponse {
  readonly notes: readonly ({
    readonly id: string;
    readonly occurrence_id: string | null;
    readonly author_membership_id: string;
    readonly body_md: string;
    readonly visibility: "internal" | "hiring_unit";
    readonly supersedes_id: string | null;
    readonly superseded: boolean;
    readonly is_feedback: boolean;
    readonly created_at: string;
  })[];
}

export type PipelineInterviewNoteListRequest = Record<string, never>;

/** The round plan a job inherits, and where it came from. */
export interface PipelineInterviewPlanReadResponse {
  readonly rounds: readonly ({
    readonly round_no: number;
    readonly label: string;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly duration_minutes?: number;
    readonly panellists?: readonly {
      readonly contact_id?: string;
      readonly display_name: string;
    }[];
  })[];
  readonly source: "job" | "client" | "agency" | "builtin";
}

export type PipelineInterviewPlanReadRequest = Record<string, never>;

/** Drops a job’s own plan so it inherits the agency default again. */
export interface PipelineInterviewPlanResetJobResponse {
  readonly rounds: readonly ({
    readonly round_no: number;
    readonly label: string;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly duration_minutes?: number;
    readonly panellists?: readonly {
      readonly contact_id?: string;
      readonly display_name: string;
    }[];
  })[];
  readonly source: "job" | "client" | "agency" | "builtin";
}

export type PipelineInterviewPlanResetJobRequest = Record<string, never>;

/** Sets how this client interviews — inherited by every requisition they place. */
export interface PipelineInterviewPlanSaveClientResponse {
  readonly clientId: string;
  readonly rounds: readonly ({
    readonly round_no: number;
    readonly label: string;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly duration_minutes?: number;
    readonly panellists?: readonly {
      readonly contact_id?: string;
      readonly display_name: string;
    }[];
  })[];
}

export type PipelineInterviewPlanSaveClientRequest = {
  readonly rounds: readonly ({
    readonly round_no: number;
    readonly label: string;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly duration_minutes?: number;
    readonly panellists?: readonly {
      readonly contact_id?: string;
      readonly display_name: string;
    }[];
  })[];
};

/** Sets the agency’s default round plan — the root every job inherits from. */
export interface PipelineInterviewPlanSaveDefaultResponse {
  readonly jobId: string | null;
  readonly rounds: readonly ({
    readonly round_no: number;
    readonly label: string;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly duration_minutes?: number;
    readonly panellists?: readonly {
      readonly contact_id?: string;
      readonly display_name: string;
    }[];
  })[];
}

export type PipelineInterviewPlanSaveDefaultRequest = {
  readonly rounds: readonly ({
    readonly round_no: number;
    readonly label: string;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly duration_minutes?: number;
    readonly panellists?: readonly {
      readonly contact_id?: string;
      readonly display_name: string;
    }[];
  })[];
};

/** Amends one job’s round plan, leaving the agency default alone. */
export interface PipelineInterviewPlanSaveJobResponse {
  readonly jobId: string | null;
  readonly rounds: readonly ({
    readonly round_no: number;
    readonly label: string;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly duration_minutes?: number;
    readonly panellists?: readonly {
      readonly contact_id?: string;
      readonly display_name: string;
    }[];
  })[];
}

export type PipelineInterviewPlanSaveJobRequest = {
  readonly rounds: readonly ({
    readonly round_no: number;
    readonly label: string;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly duration_minutes?: number;
    readonly panellists?: readonly {
      readonly contact_id?: string;
      readonly display_name: string;
    }[];
  })[];
};

/** Who is already busy at this time — a warning, never a refusal. */
export interface PipelineInterviewClashesResponse {
  readonly clashes: readonly ({
    readonly kind: "candidate" | "panellist";
    readonly who: string;
    readonly occurrence_id: string;
    readonly job_id: string;
    readonly round_label: string;
    readonly starts_at: string;
    readonly ends_at: string;
  })[];
}

export type PipelineInterviewClashesRequest = Record<string, never>;

/** The client-safe level-of-effort report for one client’s jobs. Allowlisted columns only. */
export interface PipelineInterviewClientViewResponse {
  readonly rows: readonly ({
    readonly job_id: string;
    readonly candidate_ref: string;
    readonly round_no: number;
    readonly round_label: string;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly scheduled_at: string | null;
    readonly core_outcome: "scheduled" | "completed_positive" | "completed_negative" | "completed_pending" | "no_show_candidate" | "no_show_panel" | "cancelled" | "cancelled_by_candidate" | "cancelled_by_panel" | "flagged";
    readonly status_label: string | null;
    readonly days_since_interview: number | null;
  })[];
  readonly truncated: boolean;
}

export type PipelineInterviewClientViewRequest = Record<string, never>;

/** What this round took — every scheduling of it, and every time it moved. */
export interface PipelineInterviewHistoryResponse {
  readonly events: readonly ({
    readonly id: string;
    readonly kind: string;
    readonly from_scheduled_at: string | null;
    readonly to_scheduled_at: string | null;
    readonly from_core_outcome: string | null;
    readonly to_core_outcome: string | null;
    readonly note: string | null;
    readonly actor_membership_id: string | null;
    readonly occurred_at: string;
  })[];
  readonly occurrences: readonly ({
    readonly id: string;
    readonly seq: number;
    readonly scheduled_at: string;
    readonly duration_minutes: number | null;
    readonly timezone: string | null;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly location: string | null;
    readonly core_outcome: "scheduled" | "completed_positive" | "completed_negative" | "completed_pending" | "no_show_candidate" | "no_show_panel" | "cancelled" | "cancelled_by_candidate" | "cancelled_by_panel" | "flagged";
    readonly interview_status_label: string | null;
    readonly outcome_recorded_at: string | null;
    readonly superseded: boolean;
    readonly panel: readonly ({
      readonly id: string;
      readonly membership_id: string | null;
      readonly contact_id: string | null;
      readonly display_name: string;
      readonly panel_role: string | null;
    })[];
  })[];
}

export type PipelineInterviewHistoryRequest = Record<string, never>;

/** Every interview in flight — §12’s cross-job coordination table. */
export interface PipelineInterviewListResponse {
  readonly interviews: readonly ({
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly candidate_profile_id: string;
    readonly owner_membership_id: string | null;
    readonly owner_is_explicit: boolean;
    readonly owned_by_me: boolean;
    readonly round_no: number;
    readonly round_label: string;
    readonly planned: boolean;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly scheduled_at: string | null;
    readonly duration_minutes: number | null;
    readonly timezone: string | null;
    readonly location: string | null;
    readonly meeting_service: "google_meet" | "teams" | "other" | null;
    readonly meeting_url: string | null;
    readonly link_source: "we_created" | "client_sent" | "client_pending" | "none";
    readonly organiser_kind: "agency_identity" | "personal_account" | null;
    readonly organiser_ref: string | null;
    readonly candidate_confirmation: {
      readonly asked_at: string | null;
      readonly responded_at: string | null;
      readonly response: "accepted" | "declined" | "tentative" | null;
    };
    readonly core_outcome: "scheduled" | "completed_positive" | "completed_negative" | "completed_pending" | "no_show_candidate" | "no_show_panel" | "cancelled" | "cancelled_by_candidate" | "cancelled_by_panel" | "flagged";
    readonly interview_status_id: string | null;
    readonly interview_status_label: string | null;
    readonly interview_status_colour: string | null;
    readonly comments: string | null;
    readonly days_since_interview: number | null;
    readonly invitation: {
      readonly issued_at: string;
      readonly issue_no: number;
      readonly occurrence_id: string | null;
      readonly method: string | null;
    } | null;
    readonly occurrence_count: number;
    readonly occurrences: readonly ({
      readonly id: string;
      readonly seq: number;
      readonly scheduled_at: string;
      readonly duration_minutes: number | null;
      readonly timezone: string | null;
      readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
      readonly core_outcome: "scheduled" | "completed_positive" | "completed_negative" | "completed_pending" | "no_show_candidate" | "no_show_panel" | "cancelled" | "cancelled_by_candidate" | "cancelled_by_panel" | "flagged";
      readonly interview_status_label: string | null;
      readonly outcome_recorded_at: string | null;
      readonly superseded: boolean;
      readonly candidate_confirmation: {
        readonly asked_at: string | null;
        readonly responded_at: string | null;
        readonly response: "accepted" | "declined" | "tentative" | null;
      };
    })[];
    readonly panel: readonly ({
      readonly id: string;
      readonly membership_id: string | null;
      readonly contact_id: string | null;
      readonly display_name: string;
      readonly panel_role: string | null;
      readonly confirmation: {
        readonly asked_at: string | null;
        readonly responded_at: string | null;
        readonly response: "accepted" | "declined" | "tentative" | null;
      };
    })[];
    readonly created_at: string;
    readonly updated_at: string;
  })[];
  readonly scope: "own" | "team" | "all" | null;
  readonly nextCursor: string | null;
}

export type PipelineInterviewListRequest = Record<string, never>;

/** Creates a meeting link for a scheduled round on the agency’s own connected service. */
export interface PipelineInterviewProvisionMeetingResponse {
  readonly occurrenceId: string;
  readonly meeting_service: string;
  readonly meeting_url: string;
  readonly link_source: "we_created";
  readonly organiser_kind: string;
}

export type PipelineInterviewProvisionMeetingRequest = {
  readonly replace?: boolean;
};

/** One round and every scheduling of it. */
export interface PipelineInterviewReadResponse {
  readonly interview: {
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly candidate_profile_id: string;
    readonly owner_membership_id: string | null;
    readonly owner_is_explicit: boolean;
    readonly owned_by_me: boolean;
    readonly round_no: number;
    readonly round_label: string;
    readonly planned: boolean;
    readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
    readonly scheduled_at: string | null;
    readonly duration_minutes: number | null;
    readonly timezone: string | null;
    readonly location: string | null;
    readonly meeting_service: "google_meet" | "teams" | "other" | null;
    readonly meeting_url: string | null;
    readonly link_source: "we_created" | "client_sent" | "client_pending" | "none";
    readonly organiser_kind: "agency_identity" | "personal_account" | null;
    readonly organiser_ref: string | null;
    readonly candidate_confirmation: {
      readonly asked_at: string | null;
      readonly responded_at: string | null;
      readonly response: "accepted" | "declined" | "tentative" | null;
    };
    readonly core_outcome: "scheduled" | "completed_positive" | "completed_negative" | "completed_pending" | "no_show_candidate" | "no_show_panel" | "cancelled" | "cancelled_by_candidate" | "cancelled_by_panel" | "flagged";
    readonly interview_status_id: string | null;
    readonly interview_status_label: string | null;
    readonly interview_status_colour: string | null;
    readonly comments: string | null;
    readonly days_since_interview: number | null;
    readonly invitation: {
      readonly issued_at: string;
      readonly issue_no: number;
      readonly occurrence_id: string | null;
      readonly method: string | null;
    } | null;
    readonly occurrence_count: number;
    readonly occurrences: readonly ({
      readonly id: string;
      readonly seq: number;
      readonly scheduled_at: string;
      readonly duration_minutes: number | null;
      readonly timezone: string | null;
      readonly mode: "phone" | "video" | "in_person" | "take_home" | "async";
      readonly core_outcome: "scheduled" | "completed_positive" | "completed_negative" | "completed_pending" | "no_show_candidate" | "no_show_panel" | "cancelled" | "cancelled_by_candidate" | "cancelled_by_panel" | "flagged";
      readonly interview_status_label: string | null;
      readonly outcome_recorded_at: string | null;
      readonly superseded: boolean;
      readonly candidate_confirmation: {
        readonly asked_at: string | null;
        readonly responded_at: string | null;
        readonly response: "accepted" | "declined" | "tentative" | null;
      };
    })[];
    readonly panel: readonly ({
      readonly id: string;
      readonly membership_id: string | null;
      readonly contact_id: string | null;
      readonly display_name: string;
      readonly panel_role: string | null;
      readonly confirmation: {
        readonly asked_at: string | null;
        readonly responded_at: string | null;
        readonly response: "accepted" | "declined" | "tentative" | null;
      };
    })[];
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineInterviewReadRequest = Record<string, never>;

/** Records that an invitee was asked about a scheduling, or what they answered. */
export interface PipelineInterviewRecordConfirmationResponse {
  readonly occurrenceId: string;
  readonly invitee: "candidate" | "panellist";
  readonly panellist_id: string | null;
  readonly asked_at: string | null;
  readonly responded_at: string | null;
  readonly response: "accepted" | "declined" | "tentative" | null;
}

export type PipelineInterviewRecordConfirmationRequest = {
  readonly invitee: "candidate" | "panellist";
  readonly panellist_id?: string | null;
  readonly asked?: boolean;
  readonly response?: "accepted" | "declined" | "tentative" | null;
};

/** Records an interview that already happened — one the client ran without telling us. */
export interface PipelineInterviewRecordPastResponse {
  readonly id: string;
  readonly occurrence_id: string;
  readonly core_outcome: "scheduled" | "completed_positive" | "completed_negative" | "completed_pending" | "no_show_candidate" | "no_show_panel" | "cancelled" | "cancelled_by_candidate" | "cancelled_by_panel" | "flagged";
}

export type PipelineInterviewRecordPastRequest = {
  readonly round_no: number;
  readonly round_label: string;
  readonly mode?: "phone" | "video" | "in_person" | "take_home" | "async";
  readonly scheduled_at: string;
  readonly duration_minutes?: number | null;
  readonly timezone?: string | null;
  readonly statusId: string;
  readonly note?: string;
  readonly panel?: readonly ({
    readonly membership_id?: string | null;
    readonly contact_id?: string | null;
    readonly display_name: string;
    readonly email?: string | null;
    readonly panel_role?: string | null;
  })[];
  readonly owner_membership_id?: string | null;
};

/** Removes a round that has never been scheduled. Anything in the diary is cancelled. */
export interface PipelineInterviewRemoveResponse {
  readonly id: string;
  readonly removed: boolean;
}

export type PipelineInterviewRemoveRequest = Record<string, never>;

/** Adds a round to an application — from the plan or entirely ad hoc. */
export interface PipelineInterviewScheduleResponse {
  readonly id: string;
  readonly round_no: number;
  readonly occurrence_id: string | null;
}

export type PipelineInterviewScheduleRequest = {
  readonly round_no: number;
  readonly round_label: string;
  readonly mode?: "phone" | "video" | "in_person" | "take_home" | "async";
  readonly scheduled_at?: string | null;
  readonly duration_minutes?: number | null;
  readonly timezone?: string | null;
  readonly location?: string | null;
  readonly comments?: string | null;
  readonly meeting_service?: "google_meet" | "teams" | "other" | null;
  readonly meeting_url?: string | null;
  readonly link_source?: "we_created" | "client_sent" | "client_pending" | "none";
  readonly organiser_kind?: "agency_identity" | "personal_account" | null;
  readonly organiser_ref?: string | null;
  readonly owner_membership_id?: string | null;
  readonly planned?: boolean;
  readonly panel?: readonly ({
    readonly membership_id?: string | null;
    readonly contact_id?: string | null;
    readonly display_name: string;
    readonly email?: string | null;
    readonly panel_role?: string | null;
  })[];
};

/** Seeds an application’s rounds from the plan its job inherits — a starting point only. */
export interface PipelineInterviewSeedFromPlanResponse {
  readonly added: number;
  readonly skipped: number;
  readonly source: "job" | "client" | "agency" | "builtin";
}

export type PipelineInterviewSeedFromPlanRequest = Record<string, never>;

/** Sends the calendar invitation for a scheduling, or sends it again to one person. */
export interface PipelineInterviewSendInvitationResponse {
  readonly occurrenceId: string;
  readonly uid: string;
  readonly issue_no: number;
  readonly delivery_no: number;
  readonly invitees: number;
  readonly last_issued_at: string;
}

export type PipelineInterviewSendInvitationRequest = {
  readonly only?: {
    readonly kind: "candidate" | "member" | "client_contact" | "panel_guest";
    readonly id: string;
  };
};

/** Records how a round went, in the agency’s own words over the fixed core outcome. */
export interface PipelineInterviewSetOutcomeResponse {
  readonly id: string;
  readonly core_outcome: "scheduled" | "completed_positive" | "completed_negative" | "completed_pending" | "no_show_candidate" | "no_show_panel" | "cancelled" | "cancelled_by_candidate" | "cancelled_by_panel" | "flagged";
  readonly interview_status_id: string;
  readonly application_id: string;
}

export type PipelineInterviewSetOutcomeRequest = {
  readonly statusId: string;
  readonly note?: string;
};

/** Names the recruiter answerable for a round, or hands it back to the pursuit’s owner. */
export interface PipelineInterviewSetOwnerResponse {
  readonly id: string;
  readonly owner_membership_id: string | null;
  readonly owner_is_explicit: boolean;
}

export type PipelineInterviewSetOwnerRequest = {
  readonly owner_membership_id: string | null;
};

/** Reschedules a round or amends its details, keeping the history of what moved. */
export interface PipelineInterviewUpdateResponse {
  readonly id: string;
  readonly rescheduled: boolean;
  readonly occurrence_id: string | null;
}

export type PipelineInterviewUpdateRequest = {
  readonly round_label?: string;
  readonly mode?: "phone" | "video" | "in_person" | "take_home" | "async";
  readonly scheduled_at?: string | null;
  readonly duration_minutes?: number | null;
  readonly timezone?: string | null;
  readonly location?: string | null;
  readonly comments?: string | null;
  readonly meeting_service?: "google_meet" | "teams" | "other" | null;
  readonly meeting_url?: string | null;
  readonly link_source?: "we_created" | "client_sent" | "client_pending" | "none";
  readonly organiser_kind?: "agency_identity" | "personal_account" | null;
  readonly organiser_ref?: string | null;
  readonly note?: string;
  readonly panel?: readonly ({
    readonly membership_id?: string | null;
    readonly contact_id?: string | null;
    readonly display_name: string;
    readonly email?: string | null;
    readonly panel_role?: string | null;
  })[];
};

/** Puts somebody on this requisition’s panel. */
export interface PipelineJobPanelAddResponse {
  readonly id: string;
}

export type PipelineJobPanelAddRequest = {
  readonly contact_id?: string | null;
  readonly membership_id?: string | null;
  readonly display_name: string;
  readonly email?: string | null;
  readonly panel_role?: string | null;
};

/** Who runs interviews for this requisition. */
export interface PipelineJobPanelListResponse {
  readonly panellists: readonly ({
    readonly id: string;
    readonly contact_id: string | null;
    readonly membership_id: string | null;
    readonly display_name: string;
    readonly email: string | null;
    readonly panel_role: string | null;
    readonly created_at: string;
  })[];
}

export type PipelineJobPanelListRequest = Record<string, never>;

/** Takes somebody off this requisition’s panel, keeping the interviews they sat. */
export interface PipelineJobPanelRemoveResponse {
  readonly id: string;
  readonly removed: boolean;
}

export type PipelineJobPanelRemoveRequest = Record<string, never>;

/** What this requisition asks a candidate before their CV goes to the client. */
export interface PipelineJobQuestionListResponse {
  readonly questions: readonly ({
    readonly id: string;
    readonly job_id: string;
    readonly prompt: string;
    readonly answer_type: "text" | "boolean" | "number" | "single_choice";
    readonly options: readonly string[];
    readonly required: boolean;
    readonly position: number;
    readonly active: boolean;
    readonly created_at: string;
  })[];
}

export type PipelineJobQuestionListRequest = Record<string, never>;

/** Replaces this requisition’s screening questions, retiring the ones left out. */
export interface PipelineJobQuestionSaveResponse {
  readonly questions: readonly ({
    readonly id: string;
    readonly job_id: string;
    readonly prompt: string;
    readonly answer_type: "text" | "boolean" | "number" | "single_choice";
    readonly options: readonly string[];
    readonly required: boolean;
    readonly position: number;
    readonly active: boolean;
    readonly created_at: string;
  })[];
}

export type PipelineJobQuestionSaveRequest = {
  readonly questions: readonly ({
    readonly id?: string;
    readonly prompt: string;
    readonly answer_type?: "text" | "boolean" | "number" | "single_choice";
    readonly options?: readonly string[];
    readonly required?: boolean;
  })[];
};

/** Records a check-in as sent, or as answered. */
export interface PipelineJoiningTouchpointRecordResponse {
  readonly joining: {
    readonly id: string;
    readonly application_id: string;
    readonly offer_id: string | null;
    readonly accepted_on: string;
    readonly notice_period_days: number | null;
    readonly resignation_confirmed_on: string | null;
    readonly last_working_day_on: string | null;
    readonly joining_date_confirmed_on: string | null;
    readonly documents_complete_on: string | null;
    readonly statutory_linked_on: string | null;
    readonly joined_on: string | null;
    readonly dropped_on: string | null;
    readonly drop_reason: "counter_offer_from_employer" | "another_offer" | "personal" | "relocation" | "client_withdrew" | "no_response" | "other" | null;
    readonly drop_note: string | null;
    readonly risk_flagged_at: string | null;
    readonly risk_level: "none" | "watch" | "high";
    readonly risk_signals: readonly ({
      readonly code: string;
      readonly messageKey: string;
      readonly values?: Readonly<Record<string, string | number>>;
    })[];
    readonly days_since_accepted: number;
    readonly next_touchpoint_due: string | null;
    readonly touchpoints: readonly ({
      readonly id: string;
      readonly purpose: "check_in" | "document_chase" | "client_introduction" | "joining_logistics" | "resignation_follow_up";
      readonly channel: "email" | "whatsapp" | "call" | "sms";
      readonly due_on: string;
      readonly sent_at: string | null;
      readonly replied_at: string | null;
      readonly note: string | null;
    })[];
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineJoiningTouchpointRecordRequest = {
  readonly sent?: boolean;
  readonly replied?: boolean;
  readonly note?: string;
};

/** Schedules a check-in during the notice period. */
export interface PipelineJoiningTouchpointScheduleResponse {
  readonly joining: {
    readonly id: string;
    readonly application_id: string;
    readonly offer_id: string | null;
    readonly accepted_on: string;
    readonly notice_period_days: number | null;
    readonly resignation_confirmed_on: string | null;
    readonly last_working_day_on: string | null;
    readonly joining_date_confirmed_on: string | null;
    readonly documents_complete_on: string | null;
    readonly statutory_linked_on: string | null;
    readonly joined_on: string | null;
    readonly dropped_on: string | null;
    readonly drop_reason: "counter_offer_from_employer" | "another_offer" | "personal" | "relocation" | "client_withdrew" | "no_response" | "other" | null;
    readonly drop_note: string | null;
    readonly risk_flagged_at: string | null;
    readonly risk_level: "none" | "watch" | "high";
    readonly risk_signals: readonly ({
      readonly code: string;
      readonly messageKey: string;
      readonly values?: Readonly<Record<string, string | number>>;
    })[];
    readonly days_since_accepted: number;
    readonly next_touchpoint_due: string | null;
    readonly touchpoints: readonly ({
      readonly id: string;
      readonly purpose: "check_in" | "document_chase" | "client_introduction" | "joining_logistics" | "resignation_follow_up";
      readonly channel: "email" | "whatsapp" | "call" | "sms";
      readonly due_on: string;
      readonly sent_at: string | null;
      readonly replied_at: string | null;
      readonly note: string | null;
    })[];
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineJoiningTouchpointScheduleRequest = {
  readonly purpose?: "check_in" | "document_chase" | "client_introduction" | "joining_logistics" | "resignation_follow_up";
  readonly channel?: "email" | "whatsapp" | "call" | "sms";
  readonly dueOn?: string;
  readonly note?: string;
};

/** Records that the candidate did not join, and why. */
export interface PipelineJoiningDroppedResponse {
  readonly joining: {
    readonly id: string;
    readonly application_id: string;
    readonly offer_id: string | null;
    readonly accepted_on: string;
    readonly notice_period_days: number | null;
    readonly resignation_confirmed_on: string | null;
    readonly last_working_day_on: string | null;
    readonly joining_date_confirmed_on: string | null;
    readonly documents_complete_on: string | null;
    readonly statutory_linked_on: string | null;
    readonly joined_on: string | null;
    readonly dropped_on: string | null;
    readonly drop_reason: "counter_offer_from_employer" | "another_offer" | "personal" | "relocation" | "client_withdrew" | "no_response" | "other" | null;
    readonly drop_note: string | null;
    readonly risk_flagged_at: string | null;
    readonly risk_level: "none" | "watch" | "high";
    readonly risk_signals: readonly ({
      readonly code: string;
      readonly messageKey: string;
      readonly values?: Readonly<Record<string, string | number>>;
    })[];
    readonly days_since_accepted: number;
    readonly next_touchpoint_due: string | null;
    readonly touchpoints: readonly ({
      readonly id: string;
      readonly purpose: "check_in" | "document_chase" | "client_introduction" | "joining_logistics" | "resignation_follow_up";
      readonly channel: "email" | "whatsapp" | "call" | "sms";
      readonly due_on: string;
      readonly sent_at: string | null;
      readonly replied_at: string | null;
      readonly note: string | null;
    })[];
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineJoiningDroppedRequest = {
  readonly droppedOn: string;
  readonly reason: "counter_offer_from_employer" | "another_offer" | "personal" | "relocation" | "client_withdrew" | "no_response" | "other";
  readonly note?: string;
};

/** Records that the candidate started work. */
export interface PipelineJoiningJoinedResponse {
  readonly joining: {
    readonly id: string;
    readonly application_id: string;
    readonly offer_id: string | null;
    readonly accepted_on: string;
    readonly notice_period_days: number | null;
    readonly resignation_confirmed_on: string | null;
    readonly last_working_day_on: string | null;
    readonly joining_date_confirmed_on: string | null;
    readonly documents_complete_on: string | null;
    readonly statutory_linked_on: string | null;
    readonly joined_on: string | null;
    readonly dropped_on: string | null;
    readonly drop_reason: "counter_offer_from_employer" | "another_offer" | "personal" | "relocation" | "client_withdrew" | "no_response" | "other" | null;
    readonly drop_note: string | null;
    readonly risk_flagged_at: string | null;
    readonly risk_level: "none" | "watch" | "high";
    readonly risk_signals: readonly ({
      readonly code: string;
      readonly messageKey: string;
      readonly values?: Readonly<Record<string, string | number>>;
    })[];
    readonly days_since_accepted: number;
    readonly next_touchpoint_due: string | null;
    readonly touchpoints: readonly ({
      readonly id: string;
      readonly purpose: "check_in" | "document_chase" | "client_introduction" | "joining_logistics" | "resignation_follow_up";
      readonly channel: "email" | "whatsapp" | "call" | "sms";
      readonly due_on: string;
      readonly sent_at: string | null;
      readonly replied_at: string | null;
      readonly note: string | null;
    })[];
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineJoiningJoinedRequest = {
  readonly joinedOn: string;
};

/** Joinings still in flight across the agency, oldest acceptance first. */
export interface PipelineJoiningListResponse {
  readonly joinings: readonly ({
    readonly id: string;
    readonly application_id: string;
    readonly offer_id: string | null;
    readonly accepted_on: string;
    readonly notice_period_days: number | null;
    readonly resignation_confirmed_on: string | null;
    readonly last_working_day_on: string | null;
    readonly joining_date_confirmed_on: string | null;
    readonly documents_complete_on: string | null;
    readonly statutory_linked_on: string | null;
    readonly joined_on: string | null;
    readonly dropped_on: string | null;
    readonly drop_reason: "counter_offer_from_employer" | "another_offer" | "personal" | "relocation" | "client_withdrew" | "no_response" | "other" | null;
    readonly drop_note: string | null;
    readonly risk_flagged_at: string | null;
    readonly risk_level: "none" | "watch" | "high";
    readonly risk_signals: readonly ({
      readonly code: string;
      readonly messageKey: string;
      readonly values?: Readonly<Record<string, string | number>>;
    })[];
    readonly days_since_accepted: number;
    readonly next_touchpoint_due: string | null;
    readonly touchpoints: readonly ({
      readonly id: string;
      readonly purpose: "check_in" | "document_chase" | "client_introduction" | "joining_logistics" | "resignation_follow_up";
      readonly channel: "email" | "whatsapp" | "call" | "sms";
      readonly due_on: string;
      readonly sent_at: string | null;
      readonly replied_at: string | null;
      readonly note: string | null;
    })[];
    readonly created_at: string;
    readonly updated_at: string;
  })[];
  readonly next_cursor: string | null;
}

export type PipelineJoiningListRequest = Record<string, never>;

/** The joining record for one application, with its risk and its check-ins. */
export interface PipelineJoiningReadResponse {
  readonly joining: {
    readonly id: string;
    readonly application_id: string;
    readonly offer_id: string | null;
    readonly accepted_on: string;
    readonly notice_period_days: number | null;
    readonly resignation_confirmed_on: string | null;
    readonly last_working_day_on: string | null;
    readonly joining_date_confirmed_on: string | null;
    readonly documents_complete_on: string | null;
    readonly statutory_linked_on: string | null;
    readonly joined_on: string | null;
    readonly dropped_on: string | null;
    readonly drop_reason: "counter_offer_from_employer" | "another_offer" | "personal" | "relocation" | "client_withdrew" | "no_response" | "other" | null;
    readonly drop_note: string | null;
    readonly risk_flagged_at: string | null;
    readonly risk_level: "none" | "watch" | "high";
    readonly risk_signals: readonly ({
      readonly code: string;
      readonly messageKey: string;
      readonly values?: Readonly<Record<string, string | number>>;
    })[];
    readonly days_since_accepted: number;
    readonly next_touchpoint_due: string | null;
    readonly touchpoints: readonly ({
      readonly id: string;
      readonly purpose: "check_in" | "document_chase" | "client_introduction" | "joining_logistics" | "resignation_follow_up";
      readonly channel: "email" | "whatsapp" | "call" | "sms";
      readonly due_on: string;
      readonly sent_at: string | null;
      readonly replied_at: string | null;
      readonly note: string | null;
    })[];
    readonly created_at: string;
    readonly updated_at: string;
  } | null;
}

export type PipelineJoiningReadRequest = Record<string, never>;

/** Records a joining milestone. */
export interface PipelineJoiningUpdateResponse {
  readonly joining: {
    readonly id: string;
    readonly application_id: string;
    readonly offer_id: string | null;
    readonly accepted_on: string;
    readonly notice_period_days: number | null;
    readonly resignation_confirmed_on: string | null;
    readonly last_working_day_on: string | null;
    readonly joining_date_confirmed_on: string | null;
    readonly documents_complete_on: string | null;
    readonly statutory_linked_on: string | null;
    readonly joined_on: string | null;
    readonly dropped_on: string | null;
    readonly drop_reason: "counter_offer_from_employer" | "another_offer" | "personal" | "relocation" | "client_withdrew" | "no_response" | "other" | null;
    readonly drop_note: string | null;
    readonly risk_flagged_at: string | null;
    readonly risk_level: "none" | "watch" | "high";
    readonly risk_signals: readonly ({
      readonly code: string;
      readonly messageKey: string;
      readonly values?: Readonly<Record<string, string | number>>;
    })[];
    readonly days_since_accepted: number;
    readonly next_touchpoint_due: string | null;
    readonly touchpoints: readonly ({
      readonly id: string;
      readonly purpose: "check_in" | "document_chase" | "client_introduction" | "joining_logistics" | "resignation_follow_up";
      readonly channel: "email" | "whatsapp" | "call" | "sms";
      readonly due_on: string;
      readonly sent_at: string | null;
      readonly replied_at: string | null;
      readonly note: string | null;
    })[];
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineJoiningUpdateRequest = {
  readonly noticePeriodDays?: number | null;
  readonly resignationConfirmedOn?: string | null;
  readonly lastWorkingDayOn?: string | null;
  readonly joiningDateConfirmedOn?: string | null;
  readonly documentsCompleteOn?: string | null;
  readonly statutoryLinkedOn?: string | null;
};

/** Which factors this agency scores fit on, and at what weight. */
export interface PipelineMatchSettingsReadResponse {
  readonly factors: readonly ({
    readonly factor: "must_skills" | "nice_skills" | "project_evidence" | "experience_years" | "location_mode" | "compensation" | "notice";
    readonly enabled: boolean;
    readonly weight: number;
    readonly core: boolean;
  })[];
}

export type PipelineMatchSettingsReadRequest = Record<string, never>;

/** Set which factors count and how much, then rescore what is in play. */
export interface PipelineMatchSettingsSaveResponse {
  readonly factors: readonly ({
    readonly factor: "must_skills" | "nice_skills" | "project_evidence" | "experience_years" | "location_mode" | "compensation" | "notice";
    readonly enabled: boolean;
    readonly weight: number;
    readonly core: boolean;
  })[];
  readonly rescored: number;
}

export type PipelineMatchSettingsSaveRequest = {
  readonly factors: readonly ({
    readonly factor: "must_skills" | "nice_skills" | "project_evidence" | "experience_years" | "location_mode" | "compensation" | "notice";
    readonly enabled: boolean;
    readonly weight: number;
  })[];
};

/** Remove this agency’s registered meeting application. */
export interface PipelineMeetingApplicationRemoveResponse {
  readonly removed: boolean;
}

export type PipelineMeetingApplicationRemoveRequest = Record<string, never>;

/** Register the OAuth application this agency creates meeting links through. */
export interface PipelineMeetingApplicationSaveResponse {
  readonly provider: "google" | "microsoft";
  readonly client_id: string;
  readonly has_secret: boolean;
  readonly redirect_uri: string;
}

export type PipelineMeetingApplicationSaveRequest = {
  readonly provider: "google";
  readonly client_id: string;
  readonly client_secret?: string;
};

/** Removes a connected meeting account and the credential stored for it. */
export interface PipelineMeetingConnectionDisconnectResponse {
  readonly disconnected: true;
}

export type PipelineMeetingConnectionDisconnectRequest = Record<string, never>;

/** Completes a consent begun earlier, storing the account’s sealed credential. */
export interface PipelineMeetingConnectionFinishResponse {
  readonly connection_id: string;
  readonly account_label: string | null;
  readonly status: "connecting" | "unverified" | "verified" | "disabled";
  readonly scope: "agency" | "mine";
}

export type PipelineMeetingConnectionFinishRequest = {
  readonly state: string;
  readonly code: string;
};

/** The meeting accounts this agency creates joining links on, and whether each one works. */
export interface PipelineMeetingConnectionListResponse {
  readonly connections: readonly ({
    readonly id: string;
    readonly provider: "google" | "microsoft";
    readonly scope: "agency" | "mine";
    readonly account_label: string | null;
    readonly status: "connecting" | "unverified" | "verified" | "disabled";
    readonly scopes: readonly string[];
    readonly verified_at: string | null;
    readonly last_error_code: string | null;
    readonly last_attempt_at: string | null;
    readonly has_credential: boolean;
    readonly awaiting_consent: boolean;
  })[];
  readonly applications: readonly ({
    readonly provider: "google" | "microsoft";
    readonly client_id: string;
    readonly has_secret: boolean;
  })[];
  readonly redirect_uri: string;
  readonly can_store_credentials: boolean;
  readonly may_administer: boolean;
  readonly resolves_to: "mine" | "agency" | "nothing";
}

export type PipelineMeetingConnectionListRequest = Record<string, never>;

/** Begins connecting a meeting account, returning the provider’s own consent link. */
export interface PipelineMeetingConnectionStartResponse {
  readonly connection_id: string;
  readonly authorisation_url: string;
  readonly expires_at: string;
}

export type PipelineMeetingConnectionStartRequest = {
  readonly provider: "google" | "microsoft";
  readonly scope: "agency" | "mine";
};

/** Creates a throwaway meeting on the connected account, and records whether it worked. */
export interface PipelineMeetingConnectionVerifyResponse {
  readonly status: "connecting" | "unverified" | "verified" | "disabled";
  readonly verified_at: string | null;
  readonly error_code: string | null;
}

export type PipelineMeetingConnectionVerifyRequest = Record<string, never>;

/** Renders a document for an offer from a template, or reports what is missing. */
export interface PipelineOfferDocumentGenerateResponse {
  readonly document: {
    readonly id: string;
    readonly offer_id: string;
    readonly template_id: string;
    readonly template_version: number;
    readonly ruleset_version: string | null;
    readonly body: string;
    readonly content_hash: string;
    readonly rendered_at: string;
    readonly issued_at: string | null;
    readonly superseded_at: string | null;
    readonly signatures: readonly ({
      readonly id: string;
      readonly signer_kind: "candidate" | "agency" | "client";
      readonly signer_name: string;
      readonly method: "drawn" | "uploaded";
      readonly signed_at: string;
      readonly matches_document: boolean;
    })[];
  } | null;
  readonly findings: readonly ({
    readonly code: "merge_field_missing" | "clause_condition_unresolvable";
    readonly messageKey: string;
    readonly field: string;
    readonly clauseKey: string;
  })[];
}

export type PipelineOfferDocumentGenerateRequest = {
  readonly templateId: string;
};

/** Records that a document went to the candidate. */
export interface PipelineOfferDocumentIssueResponse {
  readonly document: {
    readonly id: string;
    readonly offer_id: string;
    readonly template_id: string;
    readonly template_version: number;
    readonly ruleset_version: string | null;
    readonly body: string;
    readonly content_hash: string;
    readonly rendered_at: string;
    readonly issued_at: string | null;
    readonly superseded_at: string | null;
    readonly signatures: readonly ({
      readonly id: string;
      readonly signer_kind: "candidate" | "agency" | "client";
      readonly signer_name: string;
      readonly method: "drawn" | "uploaded";
      readonly signed_at: string;
      readonly matches_document: boolean;
    })[];
  };
}

export type PipelineOfferDocumentIssueRequest = Record<string, never>;

/** Every document rendered for an offer, newest first. */
export interface PipelineOfferDocumentReadResponse {
  readonly documents: readonly ({
    readonly id: string;
    readonly offer_id: string;
    readonly template_id: string;
    readonly template_version: number;
    readonly ruleset_version: string | null;
    readonly body: string;
    readonly content_hash: string;
    readonly rendered_at: string;
    readonly issued_at: string | null;
    readonly superseded_at: string | null;
    readonly signatures: readonly ({
      readonly id: string;
      readonly signer_kind: "candidate" | "agency" | "client";
      readonly signer_name: string;
      readonly method: "drawn" | "uploaded";
      readonly signed_at: string;
      readonly matches_document: boolean;
    })[];
  })[];
}

export type PipelineOfferDocumentReadRequest = Record<string, never>;

/** Records a drawn or uploaded signature against a document, with its evidence. */
export interface PipelineOfferDocumentSignResponse {
  readonly document: {
    readonly id: string;
    readonly offer_id: string;
    readonly template_id: string;
    readonly template_version: number;
    readonly ruleset_version: string | null;
    readonly body: string;
    readonly content_hash: string;
    readonly rendered_at: string;
    readonly issued_at: string | null;
    readonly superseded_at: string | null;
    readonly signatures: readonly ({
      readonly id: string;
      readonly signer_kind: "candidate" | "agency" | "client";
      readonly signer_name: string;
      readonly method: "drawn" | "uploaded";
      readonly signed_at: string;
      readonly matches_document: boolean;
    })[];
  };
}

export type PipelineOfferDocumentSignRequest = {
  readonly signerKind: "candidate" | "agency" | "client";
  readonly signerName: string;
  readonly signerEmail?: string;
  readonly method: "drawn" | "uploaded";
  readonly image: string;
  readonly imageMimeType?: "image/png" | "image/jpeg";
  readonly userAgent?: string;
};

/** Records the candidate countering a scenario, and what they asked for. */
export interface PipelineOfferScenarioCounterResponse {
  readonly scenario: {
    readonly id: string;
    readonly application_id: string;
    readonly label: string;
    readonly status: "draft" | "shared" | "countered" | "promoted" | "rejected" | "lapsed";
    readonly position: number;
    readonly money_shape: "salary" | "rate";
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly pay_amount: string | null;
    readonly bill_amount: string | null;
    readonly period: string | null;
    readonly duration_months: number | null;
    readonly ruleset_version: string | null;
    readonly country_iso2: string;
    readonly computed: {
      readonly country: string;
      readonly currency: string;
      readonly rulesetVersion: string;
      readonly quotingBasis: "cost_inclusive" | "gross_exclusive";
      readonly components: readonly {
        readonly key: string;
        readonly kind: string;
        readonly annual: string;
        readonly basis?: string;
      }[];
      readonly costToEmployer: string;
      readonly gross: string;
      readonly employeeDeductions: string;
      readonly tax: string;
      readonly net: string;
      readonly findings: readonly ({
        readonly code: string;
        readonly severity: "blocking" | "advisory";
        readonly messageKey: string;
        readonly values?: Readonly<Record<string, string | number>>;
      })[];
    } | null;
    readonly components: readonly {
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly position: number;
      readonly overridden: boolean;
    }[];
    readonly shared_at: string | null;
    readonly countered_at: string | null;
    readonly counter_note: string | null;
    readonly promoted_at: string | null;
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineOfferScenarioCounterRequest = {
  readonly note: string;
};

/** Adds a costed scenario to a negotiation. */
export interface PipelineOfferScenarioCreateResponse {
  readonly scenario: {
    readonly id: string;
    readonly application_id: string;
    readonly label: string;
    readonly status: "draft" | "shared" | "countered" | "promoted" | "rejected" | "lapsed";
    readonly position: number;
    readonly money_shape: "salary" | "rate";
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly pay_amount: string | null;
    readonly bill_amount: string | null;
    readonly period: string | null;
    readonly duration_months: number | null;
    readonly ruleset_version: string | null;
    readonly country_iso2: string;
    readonly computed: {
      readonly country: string;
      readonly currency: string;
      readonly rulesetVersion: string;
      readonly quotingBasis: "cost_inclusive" | "gross_exclusive";
      readonly components: readonly {
        readonly key: string;
        readonly kind: string;
        readonly annual: string;
        readonly basis?: string;
      }[];
      readonly costToEmployer: string;
      readonly gross: string;
      readonly employeeDeductions: string;
      readonly tax: string;
      readonly net: string;
      readonly findings: readonly ({
        readonly code: string;
        readonly severity: "blocking" | "advisory";
        readonly messageKey: string;
        readonly values?: Readonly<Record<string, string | number>>;
      })[];
    } | null;
    readonly components: readonly {
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly position: number;
      readonly overridden: boolean;
    }[];
    readonly shared_at: string | null;
    readonly countered_at: string | null;
    readonly counter_note: string | null;
    readonly promoted_at: string | null;
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineOfferScenarioCreateRequest = unknown;

/** Promotes a scenario to the offer, copying its money onto it. */
export interface PipelineOfferScenarioPromoteResponse {
  readonly scenario: {
    readonly id: string;
    readonly application_id: string;
    readonly label: string;
    readonly status: "draft" | "shared" | "countered" | "promoted" | "rejected" | "lapsed";
    readonly position: number;
    readonly money_shape: "salary" | "rate";
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly pay_amount: string | null;
    readonly bill_amount: string | null;
    readonly period: string | null;
    readonly duration_months: number | null;
    readonly ruleset_version: string | null;
    readonly country_iso2: string;
    readonly computed: {
      readonly country: string;
      readonly currency: string;
      readonly rulesetVersion: string;
      readonly quotingBasis: "cost_inclusive" | "gross_exclusive";
      readonly components: readonly {
        readonly key: string;
        readonly kind: string;
        readonly annual: string;
        readonly basis?: string;
      }[];
      readonly costToEmployer: string;
      readonly gross: string;
      readonly employeeDeductions: string;
      readonly tax: string;
      readonly net: string;
      readonly findings: readonly ({
        readonly code: string;
        readonly severity: "blocking" | "advisory";
        readonly messageKey: string;
        readonly values?: Readonly<Record<string, string | number>>;
      })[];
    } | null;
    readonly components: readonly {
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly position: number;
      readonly overridden: boolean;
    }[];
    readonly shared_at: string | null;
    readonly countered_at: string | null;
    readonly counter_note: string | null;
    readonly promoted_at: string | null;
    readonly created_at: string;
    readonly updated_at: string;
  };
  readonly offer_id: string;
  readonly offer_created: boolean;
}

export type PipelineOfferScenarioPromoteRequest = {
  readonly designation?: string;
  readonly location?: string;
  readonly joiningDate?: string;
  readonly noticePeriodDays?: number;
};

/** Every scenario on one application, in negotiation order. */
export interface PipelineOfferScenarioReadResponse {
  readonly scenarios: readonly ({
    readonly id: string;
    readonly application_id: string;
    readonly label: string;
    readonly status: "draft" | "shared" | "countered" | "promoted" | "rejected" | "lapsed";
    readonly position: number;
    readonly money_shape: "salary" | "rate";
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly pay_amount: string | null;
    readonly bill_amount: string | null;
    readonly period: string | null;
    readonly duration_months: number | null;
    readonly ruleset_version: string | null;
    readonly country_iso2: string;
    readonly computed: {
      readonly country: string;
      readonly currency: string;
      readonly rulesetVersion: string;
      readonly quotingBasis: "cost_inclusive" | "gross_exclusive";
      readonly components: readonly {
        readonly key: string;
        readonly kind: string;
        readonly annual: string;
        readonly basis?: string;
      }[];
      readonly costToEmployer: string;
      readonly gross: string;
      readonly employeeDeductions: string;
      readonly tax: string;
      readonly net: string;
      readonly findings: readonly ({
        readonly code: string;
        readonly severity: "blocking" | "advisory";
        readonly messageKey: string;
        readonly values?: Readonly<Record<string, string | number>>;
      })[];
    } | null;
    readonly components: readonly {
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly position: number;
      readonly overridden: boolean;
    }[];
    readonly shared_at: string | null;
    readonly countered_at: string | null;
    readonly counter_note: string | null;
    readonly promoted_at: string | null;
    readonly created_at: string;
    readonly updated_at: string;
  })[];
}

export type PipelineOfferScenarioReadRequest = Record<string, never>;

/** Records that a scenario was put to the candidate. */
export interface PipelineOfferScenarioShareResponse {
  readonly scenario: {
    readonly id: string;
    readonly application_id: string;
    readonly label: string;
    readonly status: "draft" | "shared" | "countered" | "promoted" | "rejected" | "lapsed";
    readonly position: number;
    readonly money_shape: "salary" | "rate";
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly pay_amount: string | null;
    readonly bill_amount: string | null;
    readonly period: string | null;
    readonly duration_months: number | null;
    readonly ruleset_version: string | null;
    readonly country_iso2: string;
    readonly computed: {
      readonly country: string;
      readonly currency: string;
      readonly rulesetVersion: string;
      readonly quotingBasis: "cost_inclusive" | "gross_exclusive";
      readonly components: readonly {
        readonly key: string;
        readonly kind: string;
        readonly annual: string;
        readonly basis?: string;
      }[];
      readonly costToEmployer: string;
      readonly gross: string;
      readonly employeeDeductions: string;
      readonly tax: string;
      readonly net: string;
      readonly findings: readonly ({
        readonly code: string;
        readonly severity: "blocking" | "advisory";
        readonly messageKey: string;
        readonly values?: Readonly<Record<string, string | number>>;
      })[];
    } | null;
    readonly components: readonly {
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly position: number;
      readonly overridden: boolean;
    }[];
    readonly shared_at: string | null;
    readonly countered_at: string | null;
    readonly counter_note: string | null;
    readonly promoted_at: string | null;
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineOfferScenarioShareRequest = Record<string, never>;

/** Recomputes or relabels a scenario that has not been promoted. */
export interface PipelineOfferScenarioUpdateResponse {
  readonly scenario: {
    readonly id: string;
    readonly application_id: string;
    readonly label: string;
    readonly status: "draft" | "shared" | "countered" | "promoted" | "rejected" | "lapsed";
    readonly position: number;
    readonly money_shape: "salary" | "rate";
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly pay_amount: string | null;
    readonly bill_amount: string | null;
    readonly period: string | null;
    readonly duration_months: number | null;
    readonly ruleset_version: string | null;
    readonly country_iso2: string;
    readonly computed: {
      readonly country: string;
      readonly currency: string;
      readonly rulesetVersion: string;
      readonly quotingBasis: "cost_inclusive" | "gross_exclusive";
      readonly components: readonly {
        readonly key: string;
        readonly kind: string;
        readonly annual: string;
        readonly basis?: string;
      }[];
      readonly costToEmployer: string;
      readonly gross: string;
      readonly employeeDeductions: string;
      readonly tax: string;
      readonly net: string;
      readonly findings: readonly ({
        readonly code: string;
        readonly severity: "blocking" | "advisory";
        readonly messageKey: string;
        readonly values?: Readonly<Record<string, string | number>>;
      })[];
    } | null;
    readonly components: readonly {
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly position: number;
      readonly overridden: boolean;
    }[];
    readonly shared_at: string | null;
    readonly countered_at: string | null;
    readonly counter_note: string | null;
    readonly promoted_at: string | null;
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineOfferScenarioUpdateRequest = unknown;

/** Sets the agency's CTC vocabulary, in the order it appears on an offer. */
export interface PipelineOfferSettingsComponentsResponse {
  readonly components: readonly ({
    readonly id: string;
    readonly key: string;
    readonly label: string;
    readonly kind: "fixed" | "variable" | "one_time" | "deduction" | "equity" | "employer_cost";
    readonly position: number;
    readonly is_active: boolean;
  })[];
}

export type PipelineOfferSettingsComponentsRequest = {
  readonly components: readonly ({
    readonly key: string;
    readonly label: string;
    readonly kind: "fixed" | "variable" | "one_time" | "deduction" | "equity" | "employer_cost";
    readonly is_active?: boolean;
  })[];
};

/** The agency's offer rules and its CTC vocabulary. */
export interface PipelineOfferSettingsReadResponse {
  readonly policy: {
    readonly approval_required: boolean;
    readonly approval_threshold_amount: string | null;
    readonly approval_threshold_currency: string | null;
    readonly response_window_days: number;
  };
  readonly components: readonly ({
    readonly id: string;
    readonly key: string;
    readonly label: string;
    readonly kind: "fixed" | "variable" | "one_time" | "deduction" | "equity" | "employer_cost";
    readonly position: number;
    readonly is_active: boolean;
  })[];
  readonly suggested: readonly {
    readonly key: string;
    readonly label: string;
    readonly kind: string;
  }[];
}

export type PipelineOfferSettingsReadRequest = Record<string, never>;

/** Sets the agency's approval rule and response window. */
export interface PipelineOfferSettingsUpdateResponse {
  readonly policy: {
    readonly approval_required: boolean;
    readonly approval_threshold_amount: string | null;
    readonly approval_threshold_currency: string | null;
    readonly response_window_days: number;
  };
}

export type PipelineOfferSettingsUpdateRequest = {
  readonly approvalRequired: boolean;
  readonly approvalThresholdAmount?: string | null;
  readonly approvalThresholdCurrency?: string | null;
  readonly responseWindowDays: number;
};

/** Approves a drafted offer. The drafter may never approve their own. */
export interface PipelineOfferApproveResponse {
  readonly offer: {
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly status: "drafted" | "approved" | "released" | "accepted" | "declined" | "withdrawn";
    readonly designation: string | null;
    readonly location: string | null;
    readonly joining_date: string | null;
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly notice_period_days: number | null;
    readonly drafted_by_membership_id: string;
    readonly approved_by_membership_id: string | null;
    readonly approved_at: string | null;
    readonly released_at: string | null;
    readonly responded_at: string | null;
    readonly decline_reason: string | null;
    readonly may_approve: boolean;
    readonly response_due_at: string | null;
    readonly issuance_mode: "issue" | "handover" | "record";
    readonly money_shape: "salary" | "rate";
    readonly country_iso2: string;
    readonly ruleset_version: string | null;
    readonly rate_terms: {
      readonly pay_amount: string;
      readonly bill_amount: string | null;
      readonly currency: string;
      readonly period: string;
      readonly duration_months: number | null;
      readonly end_date: string | null;
    } | null;
    readonly components: readonly {
      readonly id: string;
      readonly component_type_id: string;
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly frequency: string;
      readonly position: number;
    }[];
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineOfferApproveRequest = Record<string, never>;

/** Drafts an offer for an application, with its CTC breakup. */
export interface PipelineOfferCreateResponse {
  readonly offer: {
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly status: "drafted" | "approved" | "released" | "accepted" | "declined" | "withdrawn";
    readonly designation: string | null;
    readonly location: string | null;
    readonly joining_date: string | null;
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly notice_period_days: number | null;
    readonly drafted_by_membership_id: string;
    readonly approved_by_membership_id: string | null;
    readonly approved_at: string | null;
    readonly released_at: string | null;
    readonly responded_at: string | null;
    readonly decline_reason: string | null;
    readonly may_approve: boolean;
    readonly response_due_at: string | null;
    readonly issuance_mode: "issue" | "handover" | "record";
    readonly money_shape: "salary" | "rate";
    readonly country_iso2: string;
    readonly ruleset_version: string | null;
    readonly rate_terms: {
      readonly pay_amount: string;
      readonly bill_amount: string | null;
      readonly currency: string;
      readonly period: string;
      readonly duration_months: number | null;
      readonly end_date: string | null;
    } | null;
    readonly components: readonly {
      readonly id: string;
      readonly component_type_id: string;
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly frequency: string;
      readonly position: number;
    }[];
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineOfferCreateRequest = {
  readonly designation?: string;
  readonly location?: string;
  readonly joiningDate?: string;
  readonly annualCtcAmount?: string;
  readonly currency: string;
  readonly noticePeriodDays?: number;
  readonly components?: readonly ({
    readonly component_type_id: string;
    readonly amount: string;
    readonly currency: string;
    readonly frequency?: "annual" | "monthly" | "one_time";
  })[];
  readonly rateTerms?: {
    readonly payAmount: string;
    readonly billAmount?: string | null;
    readonly currency?: string;
    readonly period: "hourly" | "daily" | "monthly" | "annual";
    readonly durationMonths?: number | null;
    readonly endDate?: string | null;
  } | null;
};

/** Records that an accepted candidate actually joined, and moves the application. */
export interface PipelineOfferJoiningResponse {
  readonly offer: {
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly status: "drafted" | "approved" | "released" | "accepted" | "declined" | "withdrawn";
    readonly designation: string | null;
    readonly location: string | null;
    readonly joining_date: string | null;
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly notice_period_days: number | null;
    readonly drafted_by_membership_id: string;
    readonly approved_by_membership_id: string | null;
    readonly approved_at: string | null;
    readonly released_at: string | null;
    readonly responded_at: string | null;
    readonly decline_reason: string | null;
    readonly may_approve: boolean;
    readonly response_due_at: string | null;
    readonly issuance_mode: "issue" | "handover" | "record";
    readonly money_shape: "salary" | "rate";
    readonly country_iso2: string;
    readonly ruleset_version: string | null;
    readonly rate_terms: {
      readonly pay_amount: string;
      readonly bill_amount: string | null;
      readonly currency: string;
      readonly period: string;
      readonly duration_months: number | null;
      readonly end_date: string | null;
    } | null;
    readonly components: readonly {
      readonly id: string;
      readonly component_type_id: string;
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly frequency: string;
      readonly position: number;
    }[];
    readonly created_at: string;
    readonly updated_at: string;
  };
  readonly stage: "moved" | "no_matching_stage" | "ambiguous_stage" | "application_closed";
}

export type PipelineOfferJoiningRequest = {
  readonly joinedOn?: string;
};

/** Offers in play across the agency, with their attention conditions. */
export interface PipelineOfferListResponse {
  readonly offers: readonly ({
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly status: "drafted" | "approved" | "released" | "accepted" | "declined" | "withdrawn";
    readonly designation: string | null;
    readonly location: string | null;
    readonly joining_date: string | null;
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly notice_period_days: number | null;
    readonly drafted_by_membership_id: string;
    readonly approved_by_membership_id: string | null;
    readonly approved_at: string | null;
    readonly released_at: string | null;
    readonly responded_at: string | null;
    readonly decline_reason: string | null;
    readonly may_approve: boolean;
    readonly response_due_at: string | null;
    readonly issuance_mode: "issue" | "handover" | "record";
    readonly money_shape: "salary" | "rate";
    readonly country_iso2: string;
    readonly ruleset_version: string | null;
    readonly rate_terms: {
      readonly pay_amount: string;
      readonly bill_amount: string | null;
      readonly currency: string;
      readonly period: string;
      readonly duration_months: number | null;
      readonly end_date: string | null;
    } | null;
    readonly components: readonly {
      readonly id: string;
      readonly component_type_id: string;
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly frequency: string;
      readonly position: number;
    }[];
    readonly created_at: string;
    readonly updated_at: string;
    readonly attention: readonly ("awaiting_approval" | "awaiting_response" | "joining_overdue")[];
    readonly current_core_stage: string;
    readonly candidate_profile_id: string;
    readonly client_name: string | null;
  })[];
  readonly next_cursor: string | null;
}

export type PipelineOfferListRequest = Record<string, never>;

/** Every offer ever made on one application, newest first. */
export interface PipelineOfferReadResponse {
  readonly offers: readonly ({
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly status: "drafted" | "approved" | "released" | "accepted" | "declined" | "withdrawn";
    readonly designation: string | null;
    readonly location: string | null;
    readonly joining_date: string | null;
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly notice_period_days: number | null;
    readonly drafted_by_membership_id: string;
    readonly approved_by_membership_id: string | null;
    readonly approved_at: string | null;
    readonly released_at: string | null;
    readonly responded_at: string | null;
    readonly decline_reason: string | null;
    readonly may_approve: boolean;
    readonly response_due_at: string | null;
    readonly issuance_mode: "issue" | "handover" | "record";
    readonly money_shape: "salary" | "rate";
    readonly country_iso2: string;
    readonly ruleset_version: string | null;
    readonly rate_terms: {
      readonly pay_amount: string;
      readonly bill_amount: string | null;
      readonly currency: string;
      readonly period: string;
      readonly duration_months: number | null;
      readonly end_date: string | null;
    } | null;
    readonly components: readonly {
      readonly id: string;
      readonly component_type_id: string;
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly frequency: string;
      readonly position: number;
    }[];
    readonly created_at: string;
    readonly updated_at: string;
  })[];
}

export type PipelineOfferReadRequest = Record<string, never>;

/** Releases an offer to the candidate and moves the application to offered. */
export interface PipelineOfferReleaseResponse {
  readonly offer: {
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly status: "drafted" | "approved" | "released" | "accepted" | "declined" | "withdrawn";
    readonly designation: string | null;
    readonly location: string | null;
    readonly joining_date: string | null;
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly notice_period_days: number | null;
    readonly drafted_by_membership_id: string;
    readonly approved_by_membership_id: string | null;
    readonly approved_at: string | null;
    readonly released_at: string | null;
    readonly responded_at: string | null;
    readonly decline_reason: string | null;
    readonly may_approve: boolean;
    readonly response_due_at: string | null;
    readonly issuance_mode: "issue" | "handover" | "record";
    readonly money_shape: "salary" | "rate";
    readonly country_iso2: string;
    readonly ruleset_version: string | null;
    readonly rate_terms: {
      readonly pay_amount: string;
      readonly bill_amount: string | null;
      readonly currency: string;
      readonly period: string;
      readonly duration_months: number | null;
      readonly end_date: string | null;
    } | null;
    readonly components: readonly {
      readonly id: string;
      readonly component_type_id: string;
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly frequency: string;
      readonly position: number;
    }[];
    readonly created_at: string;
    readonly updated_at: string;
  };
  readonly stage: "moved" | "no_matching_stage" | "ambiguous_stage" | "application_closed";
}

export type PipelineOfferReleaseRequest = {
  readonly recipientEmail?: string;
};

/** Records the candidate's answer to a released offer. */
export interface PipelineOfferRespondResponse {
  readonly offer: {
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly status: "drafted" | "approved" | "released" | "accepted" | "declined" | "withdrawn";
    readonly designation: string | null;
    readonly location: string | null;
    readonly joining_date: string | null;
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly notice_period_days: number | null;
    readonly drafted_by_membership_id: string;
    readonly approved_by_membership_id: string | null;
    readonly approved_at: string | null;
    readonly released_at: string | null;
    readonly responded_at: string | null;
    readonly decline_reason: string | null;
    readonly may_approve: boolean;
    readonly response_due_at: string | null;
    readonly issuance_mode: "issue" | "handover" | "record";
    readonly money_shape: "salary" | "rate";
    readonly country_iso2: string;
    readonly ruleset_version: string | null;
    readonly rate_terms: {
      readonly pay_amount: string;
      readonly bill_amount: string | null;
      readonly currency: string;
      readonly period: string;
      readonly duration_months: number | null;
      readonly end_date: string | null;
    } | null;
    readonly components: readonly {
      readonly id: string;
      readonly component_type_id: string;
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly frequency: string;
      readonly position: number;
    }[];
    readonly created_at: string;
    readonly updated_at: string;
  };
  readonly stage: "moved" | "no_matching_stage" | "ambiguous_stage" | "application_closed";
}

export type PipelineOfferRespondRequest = {
  readonly outcome: "accepted" | "declined";
  readonly declineReasonCode?: string;
  readonly declineReason?: string;
};

/** Amends an offer that has not been released. */
export interface PipelineOfferUpdateResponse {
  readonly offer: {
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly status: "drafted" | "approved" | "released" | "accepted" | "declined" | "withdrawn";
    readonly designation: string | null;
    readonly location: string | null;
    readonly joining_date: string | null;
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly notice_period_days: number | null;
    readonly drafted_by_membership_id: string;
    readonly approved_by_membership_id: string | null;
    readonly approved_at: string | null;
    readonly released_at: string | null;
    readonly responded_at: string | null;
    readonly decline_reason: string | null;
    readonly may_approve: boolean;
    readonly response_due_at: string | null;
    readonly issuance_mode: "issue" | "handover" | "record";
    readonly money_shape: "salary" | "rate";
    readonly country_iso2: string;
    readonly ruleset_version: string | null;
    readonly rate_terms: {
      readonly pay_amount: string;
      readonly bill_amount: string | null;
      readonly currency: string;
      readonly period: string;
      readonly duration_months: number | null;
      readonly end_date: string | null;
    } | null;
    readonly components: readonly {
      readonly id: string;
      readonly component_type_id: string;
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly frequency: string;
      readonly position: number;
    }[];
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineOfferUpdateRequest = {
  readonly designation?: string | null;
  readonly location?: string | null;
  readonly joiningDate?: string | null;
  readonly annualCtcAmount?: string | null;
  readonly currency?: string;
  readonly noticePeriodDays?: number | null;
  readonly components?: readonly ({
    readonly component_type_id: string;
    readonly amount: string;
    readonly currency: string;
    readonly frequency?: "annual" | "monthly" | "one_time";
  })[];
  readonly rateTerms?: {
    readonly payAmount: string;
    readonly billAmount?: string | null;
    readonly currency?: string;
    readonly period: "hourly" | "daily" | "monthly" | "annual";
    readonly durationMonths?: number | null;
    readonly endDate?: string | null;
  } | null;
};

/** Withdraws an offer. The row survives; the application is free to be offered again. */
export interface PipelineOfferWithdrawResponse {
  readonly offer: {
    readonly id: string;
    readonly application_id: string;
    readonly job_id: string;
    readonly status: "drafted" | "approved" | "released" | "accepted" | "declined" | "withdrawn";
    readonly designation: string | null;
    readonly location: string | null;
    readonly joining_date: string | null;
    readonly annual_ctc_amount: string | null;
    readonly currency: string;
    readonly notice_period_days: number | null;
    readonly drafted_by_membership_id: string;
    readonly approved_by_membership_id: string | null;
    readonly approved_at: string | null;
    readonly released_at: string | null;
    readonly responded_at: string | null;
    readonly decline_reason: string | null;
    readonly may_approve: boolean;
    readonly response_due_at: string | null;
    readonly issuance_mode: "issue" | "handover" | "record";
    readonly money_shape: "salary" | "rate";
    readonly country_iso2: string;
    readonly ruleset_version: string | null;
    readonly rate_terms: {
      readonly pay_amount: string;
      readonly bill_amount: string | null;
      readonly currency: string;
      readonly period: string;
      readonly duration_months: number | null;
      readonly end_date: string | null;
    } | null;
    readonly components: readonly {
      readonly id: string;
      readonly component_type_id: string;
      readonly label: string;
      readonly kind: string;
      readonly amount: string;
      readonly currency: string;
      readonly frequency: string;
      readonly position: number;
    }[];
    readonly created_at: string;
    readonly updated_at: string;
  };
}

export type PipelineOfferWithdrawRequest = {
  readonly reason?: string;
};

/** Sets this agency up with the standard stages, statuses and round plan, once. */
export interface PipelineSetupCreateDefaultsResponse {
  readonly template: {
    readonly id: string;
    readonly name: string;
    readonly is_default: boolean;
    readonly created_at: string;
    readonly steps: readonly ({
      readonly id: string;
      readonly position: number;
      readonly label: string;
      readonly core_stage: "sourced" | "screened" | "submitted" | "shortlisted" | "interviewing" | "offered" | "accepted" | "joined" | "rejected" | "withdrawn";
      readonly is_terminal: boolean;
      readonly requires_reason: boolean;
      readonly sla_hours: number | null;
    })[];
  };
  readonly statusCount: number;
  readonly interviewStatusCount: number;
  readonly planRounds: number;
}

export type PipelineSetupCreateDefaultsRequest = Record<string, never>;

/** This agency’s pipeline stages, in order — the columns a board is drawn from. */
export interface PipelineStageTemplateReadDefaultResponse {
  readonly template: {
    readonly id: string;
    readonly name: string;
    readonly is_default: boolean;
    readonly created_at: string;
    readonly steps: readonly ({
      readonly id: string;
      readonly position: number;
      readonly label: string;
      readonly core_stage: "sourced" | "screened" | "submitted" | "shortlisted" | "interviewing" | "offered" | "accepted" | "joined" | "rejected" | "withdrawn";
      readonly is_terminal: boolean;
      readonly requires_reason: boolean;
      readonly sla_hours: number | null;
    })[];
  } | null;
}

export type PipelineStageTemplateReadDefaultRequest = Record<string, never>;

/** Sends a CV to the client and moves the application to Submitted, in one act. */
export interface PipelineSubmissionCreateResponse {
  readonly submissionId: string;
  readonly applicationId: string;
  readonly submitted_at: string;
  readonly current_step_id: string;
}

export type PipelineSubmissionCreateRequest = {
  readonly hiringUnitId: string;
  readonly resumeId: string;
  readonly toStepId?: string;
  readonly package?: {
    readonly note?: string;
    readonly expected_ctc?: string;
    readonly notice_period?: string;
    readonly availability?: string;
  };
};

/** Submissions made to clients, newest first. */
export interface PipelineSubmissionListResponse {
  readonly submissions: readonly ({
    readonly id: string;
    readonly application_id: string;
    readonly hiring_unit_id: string;
    readonly company_id: string | null;
    readonly submitted_at: string;
    readonly submitted_by_membership_id: string;
    readonly resume_id: string;
    readonly client_status: "pending" | "viewed" | "shortlisted" | "rejected";
    readonly client_responded_at: string | null;
    readonly package: Readonly<Record<string, unknown>>;
    readonly job_id: string;
    readonly candidate_profile_id: string;
  })[];
  readonly nextCursor: string | null;
}

export type PipelineSubmissionListRequest = Record<string, never>;

/** Records what the client said about a CV that was sent. */
export interface PipelineSubmissionRecordResponseResponse {
  readonly submissionId: string;
  readonly client_status: "pending" | "viewed" | "shortlisted" | "rejected";
  readonly client_responded_at: string | null;
}

export type PipelineSubmissionRecordResponseRequest = {
  readonly client_status: "pending" | "viewed" | "shortlisted" | "rejected";
};

/** Every configurable label, with the core values each dimension can map onto. */
export interface PipelineTaxonomyBuilderReadResponse {
  readonly entries: readonly ({
    readonly id: string;
    readonly dimension: "pipeline_stage" | "application_blocker" | "application_exit" | "interview_status";
    readonly label: string;
    readonly maps_to: string;
    readonly outcome_signal: "none" | "positive" | "negative" | "hold";
    readonly is_blocker_reason: boolean;
    readonly colour: string;
    readonly display_order: number;
    readonly active: boolean;
    readonly version: number;
    readonly remapped_at: string | null;
    readonly decided_by: "client" | "candidate" | "us" | null;
    readonly comment_required: boolean;
    readonly raise_rule: "manual" | "client_no_reply_after_submit" | "no_interview_feedback" | "cv_not_sent_after_screen" | "notice_period_silence" | "no_activity" | null;
    readonly raise_after_days: number | null;
    readonly occupied: number | null;
  })[];
  readonly coreValues: Readonly<Record<string, readonly string[]>>;
  readonly orphaned: Readonly<Record<string, readonly string[]>>;
  readonly raiseRules: Readonly<Record<string, {
    readonly stages: readonly string[] | null;
    readonly usesDays: boolean;
  }>>;
  readonly outcomeMeaning: Readonly<Record<string, {
    readonly happened: "not_yet" | "yes" | "no";
    readonly verdict: "none" | "positive" | "negative";
    readonly chase: "nobody" | "client" | "candidate" | "us";
  }>>;
}

export type PipelineTaxonomyBuilderReadRequest = Record<string, never>;

/** Adds one of this agency’s own labels to a dimension. */
export interface PipelineTaxonomyCreateResponse {
  readonly entryId: string;
}

export type PipelineTaxonomyCreateRequest = {
  readonly dimension: "pipeline_stage" | "application_blocker" | "application_exit" | "interview_status";
  readonly label: string;
  readonly maps_to: string;
  readonly colour?: string;
  readonly display_order?: number;
  readonly decided_by?: "client" | "candidate" | "us";
  readonly comment_required?: boolean;
  readonly raise_rule?: "manual" | "client_no_reply_after_submit" | "no_interview_feedback" | "cv_not_sent_after_screen" | "notice_period_silence" | "no_activity";
  readonly raise_after_days?: number;
};

/** This agency’s own labels for a status dimension, in its own order. */
export interface PipelineTaxonomyListResponse {
  readonly entries: readonly ({
    readonly id: string;
    readonly dimension: "application_status" | "interview_status" | "application_blocker" | "application_exit";
    readonly label: string;
    readonly maps_to: string;
    readonly outcome_signal: "none" | "positive" | "negative" | "hold";
    readonly is_blocker_reason: boolean;
    readonly colour: string;
    readonly display_order: number;
    readonly active: boolean;
    readonly decided_by: string | null;
    readonly comment_required: boolean;
    readonly raise_rule: string | null;
    readonly raise_after_days: number | null;
  })[];
  readonly nextCursor: string | null;
}

export type PipelineTaxonomyListRequest = Record<string, never>;

/** Renames, recolours, reorders, remaps or retires one label. */
export interface PipelineTaxonomyUpdateResponse {
  readonly entryId: string;
  readonly version: number;
  readonly remapped: boolean;
}

export type PipelineTaxonomyUpdateRequest = {
  readonly label?: string;
  readonly maps_to?: string;
  readonly colour?: string;
  readonly display_order?: number;
  readonly active?: boolean;
  readonly decided_by?: "client" | "candidate" | "us";
  readonly comment_required?: boolean;
  readonly raise_rule?: "manual" | "client_no_reply_after_submit" | "no_interview_feedback" | "cv_not_sent_after_screen" | "notice_period_silence" | "no_activity";
  readonly raise_after_days?: number;
};

export type OperationId = "access.agency_profile.update" | "access.group.create" | "access.group.list" | "access.group.read" | "access.group.remove_member" | "access.group.set_member" | "access.group.update" | "access.me.read" | "access.member.deactivate" | "access.member.invite" | "access.member.list" | "access.member.reactivate" | "access.member.update_role" | "access.platform_operator.read" | "access.platform_tenant.close" | "access.platform_tenant.create" | "access.platform_tenant.list" | "access.platform_tenant.reactivate" | "access.platform_tenant.read" | "access.platform_tenant.set_entitlements" | "access.platform_tenant.set_term" | "access.platform_tenant.suspend" | "access.preference.clear" | "access.preference.list" | "access.preference.save" | "access.saved_view.delete" | "access.saved_view.list" | "access.saved_view.save" | "candidates.candidate.create" | "candidates.candidate.history" | "candidates.candidate.list" | "candidates.candidate.overview" | "candidates.candidate.read" | "candidates.candidate.search" | "candidates.candidate.set_photo" | "candidates.candidate.set_reachability" | "candidates.candidate.update" | "candidates.candidate.update_status" | "candidates.copilot.ask" | "candidates.credit.balance" | "candidates.identity_number.read" | "candidates.identity_number.reveal" | "candidates.identity_number.save" | "candidates.identity_reveal.read" | "candidates.intelligence.generate" | "candidates.intelligence.read" | "candidates.merge_review.decide_many" | "candidates.merge_review.list" | "candidates.merge_review.merge" | "candidates.merge_review.reject" | "candidates.note.add" | "candidates.note.delete" | "candidates.note.list" | "candidates.note.update" | "candidates.resume.accept" | "candidates.resume.check_duplicate" | "candidates.resume.check_held" | "candidates.resume.discard" | "candidates.resume.list" | "candidates.resume.read" | "candidates.resume.upload" | "candidates.saved_search.delete" | "candidates.saved_search.list" | "candidates.saved_search.save" | "candidates.search.log_impressions" | "candidates.search.suggest" | "candidates.talent_pool.add_member" | "candidates.talent_pool.archive" | "candidates.talent_pool.create" | "candidates.talent_pool.list" | "candidates.talent_pool.read" | "candidates.talent_pool.remove_member" | "candidates.talent_pool.share" | "candidates.talent_pool.sharing" | "clients.client.assign" | "clients.client.create" | "clients.client.list" | "clients.client.overview" | "clients.client.read" | "clients.client.set_meeting_service" | "clients.client.set_owner" | "clients.client.unassign" | "clients.client.update_status" | "clients.closure_label.create" | "clients.closure_label.read" | "clients.closure_label.update" | "clients.commercials.read" | "clients.contact.create" | "clients.contact.remove" | "clients.contact.set_primary" | "clients.contact.update" | "clients.contract.correct" | "clients.contract.create" | "clients.contract.list" | "clients.copilot.ask" | "clients.cost_template.list" | "clients.cost_template.save" | "clients.engagement_contact.create" | "clients.engagement_contact.read" | "clients.engagement_contact.remove" | "clients.engagement_contact.update" | "clients.engagement_terms.set" | "clients.engagement_types.list" | "clients.geography.read" | "clients.geography.search" | "clients.job_assignment.remove" | "clients.job_assignment.set" | "clients.job_budget.set" | "clients.job_commercials.history" | "clients.job_commercials.set" | "clients.job_document.add" | "clients.job_document.add_link" | "clients.job_document.draft" | "clients.job_document.library" | "clients.job_document.list" | "clients.job_document.parse" | "clients.job_document.read" | "clients.job_document.remove" | "clients.job_document.rename" | "clients.job_document.reuse" | "clients.job_document.save_draft" | "clients.job_document.upload" | "clients.job_location.list" | "clients.job_location.set" | "clients.job_skill.save" | "clients.job.acknowledge_intake" | "clients.job.analyse_intake" | "clients.job.artifact_history" | "clients.job.artifacts" | "clients.job.bulk_assign" | "clients.job.bulk_close" | "clients.job.bulk_set_owner" | "clients.job.ceiling" | "clients.job.create" | "clients.job.disposition_flag" | "clients.job.duplicate" | "clients.job.edit_artifact" | "clients.job.field_assist" | "clients.job.field_history" | "clients.job.generate_artifact" | "clients.job.generate_boolean_strings" | "clients.job.intake_questions" | "clients.job.list" | "clients.job.margin" | "clients.job.overview" | "clients.job.preview_posting" | "clients.job.read" | "clients.job.refresh_posting" | "clients.job.resolve_artifact_proposal" | "clients.job.restore_artifact" | "clients.job.set_owner" | "clients.job.set_publish_clearance" | "clients.job.update" | "clients.job.update_status" | "clients.rate_card.correct" | "clients.rate_card.create" | "clients.rate_card.retire" | "clients.rate_card.update" | "clients.sla.list" | "clients.sla.set" | "clients.time_to_fill.read" | "clients.work_location.create" | "clients.work_location.list" | "clients.work_location.retire" | "copilot.ai_settings.read" | "copilot.ai_settings.update" | "copilot.candidate_decision.read" | "copilot.job_decision.read" | "copilot.platform_ai_config.read" | "copilot.platform_ai_connection.check" | "copilot.platform_ai_credential.clear" | "copilot.platform_ai_credential.read" | "copilot.platform_ai_credential.save" | "copilot.platform_ai_model.list" | "copilot.platform_ai_price.create" | "copilot.platform_ai_route.upsert" | "copilot.platform_ai_usage.read" | "copilot.resume_parse.read" | "copilot.resume_parse.record_review" | "identity.auth.password" | "identity.auth.refresh" | "identity.auth.reset_confirm" | "identity.auth.reset_request" | "identity.auth.sign_out" | "identity.invitation.activate" | "identity.platform_invitation.list" | "identity.portal.applications" | "identity.portal.apply" | "identity.portal.start" | "identity.portal.verify" | "insight.activity.list" | "insight.attention.read" | "insight.blocked_by_owner.read" | "insight.blocked.read" | "insight.changes.read" | "insight.client_responsiveness.read" | "insight.commitment.create" | "insight.commitment.delete" | "insight.commitment.list" | "insight.commitment.roll_forward" | "insight.commitment.update" | "insight.conversion.read" | "insight.copilot.ask" | "insight.copilot.ask_book" | "insight.dashboard.read" | "insight.digest.read" | "insight.effort_board.read" | "insight.job_board.read" | "insight.job_effort.read" | "insight.standup.desk" | "insight.time_leak.read" | "insight.today.read" | "outreach.email_sender.list" | "outreach.email_sender.save" | "outreach.email_sender.set_status" | "outreach.email_sender.verify" | "outreach.interview_invitation.list" | "outreach.message_setting.list" | "outreach.message_setting.set" | "outreach.message_template.list" | "outreach.message_template.read" | "outreach.message_template.revert" | "outreach.message_template.save" | "outreach.message_template.set_sender" | "outreach.message_template.test_send" | "outreach.message.draft" | "outreach.message.list" | "outreach.message.retry" | "outreach.message.send" | "outreach.platform_email_sender.delete" | "outreach.platform_email_sender.read" | "outreach.platform_email_sender.save" | "outreach.platform_email_sender.verify" | "outreach.platform_message.read" | "outreach.stage_message.list" | "outreach.stage_message.save" | "pipeline.application_answer.list" | "pipeline.application_answer.save" | "pipeline.application.block" | "pipeline.application.bulk_change_stage" | "pipeline.application.bulk_close" | "pipeline.application.change_stage" | "pipeline.application.close" | "pipeline.application.create" | "pipeline.application.list" | "pipeline.application.read" | "pipeline.application.revive" | "pipeline.application.unblock" | "pipeline.availability.book" | "pipeline.availability.list" | "pipeline.availability.record" | "pipeline.availability.withdraw" | "pipeline.compensation.compute" | "pipeline.copilot.ask" | "pipeline.document_clause.read" | "pipeline.document_clause.save" | "pipeline.document_link.open" | "pipeline.document_link.submit" | "pipeline.document_request.create" | "pipeline.document_request.read" | "pipeline.document_request.revoke" | "pipeline.document_set.read" | "pipeline.document_set.save" | "pipeline.document_submission.review" | "pipeline.document_template.read" | "pipeline.document_template.save" | "pipeline.interview_note.add" | "pipeline.interview_note.list" | "pipeline.interview_plan.read" | "pipeline.interview_plan.reset_job" | "pipeline.interview_plan.save_client" | "pipeline.interview_plan.save_default" | "pipeline.interview_plan.save_job" | "pipeline.interview.clashes" | "pipeline.interview.client_view" | "pipeline.interview.history" | "pipeline.interview.list" | "pipeline.interview.provision_meeting" | "pipeline.interview.read" | "pipeline.interview.record_confirmation" | "pipeline.interview.record_past" | "pipeline.interview.remove" | "pipeline.interview.schedule" | "pipeline.interview.seed_from_plan" | "pipeline.interview.send_invitation" | "pipeline.interview.set_outcome" | "pipeline.interview.set_owner" | "pipeline.interview.update" | "pipeline.job_panel.add" | "pipeline.job_panel.list" | "pipeline.job_panel.remove" | "pipeline.job_question.list" | "pipeline.job_question.save" | "pipeline.joining_touchpoint.record" | "pipeline.joining_touchpoint.schedule" | "pipeline.joining.dropped" | "pipeline.joining.joined" | "pipeline.joining.list" | "pipeline.joining.read" | "pipeline.joining.update" | "pipeline.match_settings.read" | "pipeline.match_settings.save" | "pipeline.meeting_application.remove" | "pipeline.meeting_application.save" | "pipeline.meeting_connection.disconnect" | "pipeline.meeting_connection.finish" | "pipeline.meeting_connection.list" | "pipeline.meeting_connection.start" | "pipeline.meeting_connection.verify" | "pipeline.offer_document.generate" | "pipeline.offer_document.issue" | "pipeline.offer_document.read" | "pipeline.offer_document.sign" | "pipeline.offer_scenario.counter" | "pipeline.offer_scenario.create" | "pipeline.offer_scenario.promote" | "pipeline.offer_scenario.read" | "pipeline.offer_scenario.share" | "pipeline.offer_scenario.update" | "pipeline.offer_settings.components" | "pipeline.offer_settings.read" | "pipeline.offer_settings.update" | "pipeline.offer.approve" | "pipeline.offer.create" | "pipeline.offer.joining" | "pipeline.offer.list" | "pipeline.offer.read" | "pipeline.offer.release" | "pipeline.offer.respond" | "pipeline.offer.update" | "pipeline.offer.withdraw" | "pipeline.setup.create_defaults" | "pipeline.stage_template.read_default" | "pipeline.submission.create" | "pipeline.submission.list" | "pipeline.submission.record_response" | "pipeline.taxonomy.builder_read" | "pipeline.taxonomy.create" | "pipeline.taxonomy.list" | "pipeline.taxonomy.update";

/** Every route the API publishes, with the realm whose token it accepts. */
export const API_OPERATIONS = {
  "access.agency_profile.update": {
    id: "access.agency_profile.update",
    method: "PUT",
    path: "/v1/agency-profile",
    realm: "workforce",
    mutates: true,
  },
  "access.group.create": {
    id: "access.group.create",
    method: "POST",
    path: "/v1/groups",
    realm: "workforce",
    mutates: true,
  },
  "access.group.list": {
    id: "access.group.list",
    method: "GET",
    path: "/v1/groups",
    realm: "workforce",
    mutates: false,
  },
  "access.group.read": {
    id: "access.group.read",
    method: "GET",
    path: "/v1/groups/{groupId}",
    realm: "workforce",
    mutates: false,
  },
  "access.group.remove_member": {
    id: "access.group.remove_member",
    method: "DELETE",
    path: "/v1/groups/{groupId}/members/{membershipId}",
    realm: "workforce",
    mutates: true,
  },
  "access.group.set_member": {
    id: "access.group.set_member",
    method: "PUT",
    path: "/v1/groups/{groupId}/members/{membershipId}",
    realm: "workforce",
    mutates: true,
  },
  "access.group.update": {
    id: "access.group.update",
    method: "PATCH",
    path: "/v1/groups/{groupId}",
    realm: "workforce",
    mutates: true,
  },
  "access.me.read": {
    id: "access.me.read",
    method: "GET",
    path: "/v1/me",
    realm: "workforce",
    mutates: false,
  },
  "access.member.deactivate": {
    id: "access.member.deactivate",
    method: "POST",
    path: "/v1/members/{membershipId}/deactivate",
    realm: "workforce",
    mutates: true,
  },
  "access.member.invite": {
    id: "access.member.invite",
    method: "POST",
    path: "/v1/members",
    realm: "workforce",
    mutates: true,
  },
  "access.member.list": {
    id: "access.member.list",
    method: "GET",
    path: "/v1/members",
    realm: "workforce",
    mutates: false,
  },
  "access.member.reactivate": {
    id: "access.member.reactivate",
    method: "POST",
    path: "/v1/members/{membershipId}/reactivate",
    realm: "workforce",
    mutates: true,
  },
  "access.member.update_role": {
    id: "access.member.update_role",
    method: "POST",
    path: "/v1/members/{membershipId}/role",
    realm: "workforce",
    mutates: true,
  },
  "access.platform_operator.read": {
    id: "access.platform_operator.read",
    method: "GET",
    path: "/v1/platform/me",
    realm: "platform",
    mutates: false,
  },
  "access.platform_tenant.close": {
    id: "access.platform_tenant.close",
    method: "POST",
    path: "/v1/platform/tenants/{tenantId}/close",
    realm: "platform",
    mutates: true,
  },
  "access.platform_tenant.create": {
    id: "access.platform_tenant.create",
    method: "POST",
    path: "/v1/platform/tenants",
    realm: "platform",
    mutates: true,
  },
  "access.platform_tenant.list": {
    id: "access.platform_tenant.list",
    method: "GET",
    path: "/v1/platform/tenants",
    realm: "platform",
    mutates: false,
  },
  "access.platform_tenant.reactivate": {
    id: "access.platform_tenant.reactivate",
    method: "POST",
    path: "/v1/platform/tenants/{tenantId}/reactivate",
    realm: "platform",
    mutates: true,
  },
  "access.platform_tenant.read": {
    id: "access.platform_tenant.read",
    method: "GET",
    path: "/v1/platform/tenants/{tenantId}",
    realm: "platform",
    mutates: false,
  },
  "access.platform_tenant.set_entitlements": {
    id: "access.platform_tenant.set_entitlements",
    method: "PUT",
    path: "/v1/platform/tenants/{tenantId}/entitlements",
    realm: "platform",
    mutates: true,
  },
  "access.platform_tenant.set_term": {
    id: "access.platform_tenant.set_term",
    method: "POST",
    path: "/v1/platform/tenants/{tenantId}/term",
    realm: "platform",
    mutates: true,
  },
  "access.platform_tenant.suspend": {
    id: "access.platform_tenant.suspend",
    method: "POST",
    path: "/v1/platform/tenants/{tenantId}/suspend",
    realm: "platform",
    mutates: true,
  },
  "access.preference.clear": {
    id: "access.preference.clear",
    method: "DELETE",
    path: "/v1/preferences/{key}",
    realm: "workforce",
    mutates: true,
  },
  "access.preference.list": {
    id: "access.preference.list",
    method: "GET",
    path: "/v1/preferences",
    realm: "workforce",
    mutates: false,
  },
  "access.preference.save": {
    id: "access.preference.save",
    method: "PUT",
    path: "/v1/preferences/{key}",
    realm: "workforce",
    mutates: true,
  },
  "access.saved_view.delete": {
    id: "access.saved_view.delete",
    method: "DELETE",
    path: "/v1/saved-views/{savedViewId}",
    realm: "workforce",
    mutates: true,
  },
  "access.saved_view.list": {
    id: "access.saved_view.list",
    method: "GET",
    path: "/v1/saved-views",
    realm: "workforce",
    mutates: false,
  },
  "access.saved_view.save": {
    id: "access.saved_view.save",
    method: "PUT",
    path: "/v1/saved-views",
    realm: "workforce",
    mutates: true,
  },
  "candidates.candidate.create": {
    id: "candidates.candidate.create",
    method: "POST",
    path: "/v1/candidates",
    realm: "workforce",
    mutates: true,
  },
  "candidates.candidate.history": {
    id: "candidates.candidate.history",
    method: "GET",
    path: "/v1/candidates/{candidateId}/history",
    realm: "workforce",
    mutates: false,
  },
  "candidates.candidate.list": {
    id: "candidates.candidate.list",
    method: "GET",
    path: "/v1/candidates",
    realm: "workforce",
    mutates: false,
  },
  "candidates.candidate.overview": {
    id: "candidates.candidate.overview",
    method: "GET",
    path: "/v1/candidates/overview",
    realm: "workforce",
    mutates: false,
  },
  "candidates.candidate.read": {
    id: "candidates.candidate.read",
    method: "GET",
    path: "/v1/candidates/{candidateId}",
    realm: "workforce",
    mutates: false,
  },
  "candidates.candidate.search": {
    id: "candidates.candidate.search",
    method: "GET",
    path: "/v1/search/candidates",
    realm: "workforce",
    mutates: false,
  },
  "candidates.candidate.set_photo": {
    id: "candidates.candidate.set_photo",
    method: "POST",
    path: "/v1/candidates/{candidateId}/photo",
    realm: "workforce",
    mutates: true,
  },
  "candidates.candidate.set_reachability": {
    id: "candidates.candidate.set_reachability",
    method: "POST",
    path: "/v1/candidates/{candidateId}/reachability",
    realm: "workforce",
    mutates: true,
  },
  "candidates.candidate.update": {
    id: "candidates.candidate.update",
    method: "POST",
    path: "/v1/candidates/{candidateId}/reported",
    realm: "workforce",
    mutates: true,
  },
  "candidates.candidate.update_status": {
    id: "candidates.candidate.update_status",
    method: "POST",
    path: "/v1/candidates/{candidateId}/status",
    realm: "workforce",
    mutates: true,
  },
  "candidates.copilot.ask": {
    id: "candidates.copilot.ask",
    method: "POST",
    path: "/v1/candidates/{candidateId}/ask",
    realm: "workforce",
    mutates: true,
  },
  "candidates.credit.balance": {
    id: "candidates.credit.balance",
    method: "GET",
    path: "/v1/credits/balance",
    realm: "workforce",
    mutates: false,
  },
  "candidates.identity_number.read": {
    id: "candidates.identity_number.read",
    method: "GET",
    path: "/v1/candidates/{candidateProfileId}/identity-numbers",
    realm: "workforce",
    mutates: false,
  },
  "candidates.identity_number.reveal": {
    id: "candidates.identity_number.reveal",
    method: "POST",
    path: "/v1/candidates/{candidateProfileId}/identity-numbers/{kind}/reveal",
    realm: "workforce",
    mutates: true,
  },
  "candidates.identity_number.save": {
    id: "candidates.identity_number.save",
    method: "PUT",
    path: "/v1/candidates/{candidateProfileId}/identity-numbers/{kind}",
    realm: "workforce",
    mutates: true,
  },
  "candidates.identity_reveal.read": {
    id: "candidates.identity_reveal.read",
    method: "GET",
    path: "/v1/candidates/{candidateProfileId}/identity-reveals",
    realm: "workforce",
    mutates: false,
  },
  "candidates.intelligence.generate": {
    id: "candidates.intelligence.generate",
    method: "POST",
    path: "/v1/candidates/{candidateId}/intelligence/{kind}",
    realm: "workforce",
    mutates: true,
  },
  "candidates.intelligence.read": {
    id: "candidates.intelligence.read",
    method: "GET",
    path: "/v1/candidates/{candidateId}/intelligence",
    realm: "workforce",
    mutates: false,
  },
  "candidates.merge_review.decide_many": {
    id: "candidates.merge_review.decide_many",
    method: "POST",
    path: "/v1/merge-reviews/decide",
    realm: "workforce",
    mutates: true,
  },
  "candidates.merge_review.list": {
    id: "candidates.merge_review.list",
    method: "GET",
    path: "/v1/merge-reviews",
    realm: "workforce",
    mutates: false,
  },
  "candidates.merge_review.merge": {
    id: "candidates.merge_review.merge",
    method: "POST",
    path: "/v1/merge-reviews/{reviewId}/merge",
    realm: "workforce",
    mutates: true,
  },
  "candidates.merge_review.reject": {
    id: "candidates.merge_review.reject",
    method: "POST",
    path: "/v1/merge-reviews/{reviewId}/reject",
    realm: "workforce",
    mutates: true,
  },
  "candidates.note.add": {
    id: "candidates.note.add",
    method: "POST",
    path: "/v1/candidates/{candidateProfileId}/notes",
    realm: "workforce",
    mutates: true,
  },
  "candidates.note.delete": {
    id: "candidates.note.delete",
    method: "DELETE",
    path: "/v1/notes/{noteId}",
    realm: "workforce",
    mutates: true,
  },
  "candidates.note.list": {
    id: "candidates.note.list",
    method: "GET",
    path: "/v1/candidates/{candidateProfileId}/notes",
    realm: "workforce",
    mutates: false,
  },
  "candidates.note.update": {
    id: "candidates.note.update",
    method: "PATCH",
    path: "/v1/notes/{noteId}",
    realm: "workforce",
    mutates: true,
  },
  "candidates.resume.accept": {
    id: "candidates.resume.accept",
    method: "POST",
    path: "/v1/resumes/{resumeId}/accept",
    realm: "workforce",
    mutates: true,
  },
  "candidates.resume.check_duplicate": {
    id: "candidates.resume.check_duplicate",
    method: "GET",
    path: "/v1/resumes/duplicate",
    realm: "workforce",
    mutates: false,
  },
  "candidates.resume.check_held": {
    id: "candidates.resume.check_held",
    method: "GET",
    path: "/v1/resumes/held",
    realm: "workforce",
    mutates: false,
  },
  "candidates.resume.discard": {
    id: "candidates.resume.discard",
    method: "POST",
    path: "/v1/resumes/{resumeId}/discard",
    realm: "workforce",
    mutates: true,
  },
  "candidates.resume.list": {
    id: "candidates.resume.list",
    method: "GET",
    path: "/v1/resumes",
    realm: "workforce",
    mutates: false,
  },
  "candidates.resume.read": {
    id: "candidates.resume.read",
    method: "GET",
    path: "/v1/resumes/{resumeId}",
    realm: "workforce",
    mutates: false,
  },
  "candidates.resume.upload": {
    id: "candidates.resume.upload",
    method: "POST",
    path: "/v1/resumes",
    realm: "workforce",
    mutates: true,
  },
  "candidates.saved_search.delete": {
    id: "candidates.saved_search.delete",
    method: "DELETE",
    path: "/v1/saved-searches/{savedSearchId}",
    realm: "workforce",
    mutates: true,
  },
  "candidates.saved_search.list": {
    id: "candidates.saved_search.list",
    method: "GET",
    path: "/v1/saved-searches",
    realm: "workforce",
    mutates: false,
  },
  "candidates.saved_search.save": {
    id: "candidates.saved_search.save",
    method: "PUT",
    path: "/v1/saved-searches",
    realm: "workforce",
    mutates: true,
  },
  "candidates.search.log_impressions": {
    id: "candidates.search.log_impressions",
    method: "POST",
    path: "/v1/search/candidates/impressions",
    realm: "workforce",
    mutates: true,
  },
  "candidates.search.suggest": {
    id: "candidates.search.suggest",
    method: "GET",
    path: "/v1/search/candidates/suggestions",
    realm: "workforce",
    mutates: false,
  },
  "candidates.talent_pool.add_member": {
    id: "candidates.talent_pool.add_member",
    method: "PUT",
    path: "/v1/talent-pools/{poolId}/members/{candidateProfileId}",
    realm: "workforce",
    mutates: true,
  },
  "candidates.talent_pool.archive": {
    id: "candidates.talent_pool.archive",
    method: "POST",
    path: "/v1/talent-pools/{poolId}/archive",
    realm: "workforce",
    mutates: true,
  },
  "candidates.talent_pool.create": {
    id: "candidates.talent_pool.create",
    method: "POST",
    path: "/v1/talent-pools",
    realm: "workforce",
    mutates: true,
  },
  "candidates.talent_pool.list": {
    id: "candidates.talent_pool.list",
    method: "GET",
    path: "/v1/talent-pools",
    realm: "workforce",
    mutates: false,
  },
  "candidates.talent_pool.read": {
    id: "candidates.talent_pool.read",
    method: "GET",
    path: "/v1/talent-pools/{poolId}",
    realm: "workforce",
    mutates: false,
  },
  "candidates.talent_pool.remove_member": {
    id: "candidates.talent_pool.remove_member",
    method: "DELETE",
    path: "/v1/talent-pools/{poolId}/members/{candidateProfileId}",
    realm: "workforce",
    mutates: true,
  },
  "candidates.talent_pool.share": {
    id: "candidates.talent_pool.share",
    method: "PUT",
    path: "/v1/talent-pools/{poolId}/sharing",
    realm: "workforce",
    mutates: true,
  },
  "candidates.talent_pool.sharing": {
    id: "candidates.talent_pool.sharing",
    method: "GET",
    path: "/v1/talent-pools/{poolId}/sharing",
    realm: "workforce",
    mutates: false,
  },
  "clients.client.assign": {
    id: "clients.client.assign",
    method: "POST",
    path: "/v1/clients/{clientId}/assignments",
    realm: "workforce",
    mutates: true,
  },
  "clients.client.create": {
    id: "clients.client.create",
    method: "POST",
    path: "/v1/clients",
    realm: "workforce",
    mutates: true,
  },
  "clients.client.list": {
    id: "clients.client.list",
    method: "GET",
    path: "/v1/clients",
    realm: "workforce",
    mutates: false,
  },
  "clients.client.overview": {
    id: "clients.client.overview",
    method: "GET",
    path: "/v1/clients/overview",
    realm: "workforce",
    mutates: false,
  },
  "clients.client.read": {
    id: "clients.client.read",
    method: "GET",
    path: "/v1/clients/{clientId}",
    realm: "workforce",
    mutates: false,
  },
  "clients.client.set_meeting_service": {
    id: "clients.client.set_meeting_service",
    method: "POST",
    path: "/v1/clients/{clientId}/meeting-service",
    realm: "workforce",
    mutates: true,
  },
  "clients.client.set_owner": {
    id: "clients.client.set_owner",
    method: "POST",
    path: "/v1/clients/{clientId}/owner",
    realm: "workforce",
    mutates: true,
  },
  "clients.client.unassign": {
    id: "clients.client.unassign",
    method: "POST",
    path: "/v1/clients/{clientId}/assignments/{membershipId}/remove",
    realm: "workforce",
    mutates: true,
  },
  "clients.client.update_status": {
    id: "clients.client.update_status",
    method: "POST",
    path: "/v1/clients/{clientId}/status",
    realm: "workforce",
    mutates: true,
  },
  "clients.closure_label.create": {
    id: "clients.closure_label.create",
    method: "POST",
    path: "/v1/closure-labels",
    realm: "workforce",
    mutates: true,
  },
  "clients.closure_label.read": {
    id: "clients.closure_label.read",
    method: "GET",
    path: "/v1/closure-labels",
    realm: "workforce",
    mutates: false,
  },
  "clients.closure_label.update": {
    id: "clients.closure_label.update",
    method: "POST",
    path: "/v1/closure-labels/{labelId}",
    realm: "workforce",
    mutates: true,
  },
  "clients.commercials.read": {
    id: "clients.commercials.read",
    method: "GET",
    path: "/v1/clients/{clientId}/commercials",
    realm: "workforce",
    mutates: false,
  },
  "clients.contact.create": {
    id: "clients.contact.create",
    method: "POST",
    path: "/v1/clients/{clientId}/contacts",
    realm: "workforce",
    mutates: true,
  },
  "clients.contact.remove": {
    id: "clients.contact.remove",
    method: "POST",
    path: "/v1/clients/{clientId}/contacts/{contactId}/remove",
    realm: "workforce",
    mutates: true,
  },
  "clients.contact.set_primary": {
    id: "clients.contact.set_primary",
    method: "POST",
    path: "/v1/clients/{clientId}/contacts/{contactId}/primary",
    realm: "workforce",
    mutates: true,
  },
  "clients.contact.update": {
    id: "clients.contact.update",
    method: "POST",
    path: "/v1/clients/{clientId}/contacts/{contactId}",
    realm: "workforce",
    mutates: true,
  },
  "clients.contract.correct": {
    id: "clients.contract.correct",
    method: "POST",
    path: "/v1/clients/{clientId}/contracts/{contractId}/correct",
    realm: "workforce",
    mutates: true,
  },
  "clients.contract.create": {
    id: "clients.contract.create",
    method: "POST",
    path: "/v1/clients/{clientId}/contracts",
    realm: "workforce",
    mutates: true,
  },
  "clients.contract.list": {
    id: "clients.contract.list",
    method: "GET",
    path: "/v1/clients/{clientId}/contracts",
    realm: "workforce",
    mutates: false,
  },
  "clients.copilot.ask": {
    id: "clients.copilot.ask",
    method: "POST",
    path: "/v1/jobs/{jobId}/ask",
    realm: "workforce",
    mutates: true,
  },
  "clients.cost_template.list": {
    id: "clients.cost_template.list",
    method: "GET",
    path: "/v1/cost-templates",
    realm: "workforce",
    mutates: false,
  },
  "clients.cost_template.save": {
    id: "clients.cost_template.save",
    method: "POST",
    path: "/v1/cost-templates",
    realm: "workforce",
    mutates: true,
  },
  "clients.engagement_contact.create": {
    id: "clients.engagement_contact.create",
    method: "POST",
    path: "/v1/clients/{clientId}/engagement-contacts",
    realm: "workforce",
    mutates: true,
  },
  "clients.engagement_contact.read": {
    id: "clients.engagement_contact.read",
    method: "GET",
    path: "/v1/clients/{clientId}/engagement-contacts",
    realm: "workforce",
    mutates: false,
  },
  "clients.engagement_contact.remove": {
    id: "clients.engagement_contact.remove",
    method: "POST",
    path: "/v1/clients/{clientId}/engagement-contacts/{contactId}/remove",
    realm: "workforce",
    mutates: true,
  },
  "clients.engagement_contact.update": {
    id: "clients.engagement_contact.update",
    method: "POST",
    path: "/v1/clients/{clientId}/engagement-contacts/{contactId}",
    realm: "workforce",
    mutates: true,
  },
  "clients.engagement_terms.set": {
    id: "clients.engagement_terms.set",
    method: "PUT",
    path: "/v1/clients/{clientId}/engagement-terms/{employmentType}",
    realm: "workforce",
    mutates: true,
  },
  "clients.engagement_types.list": {
    id: "clients.engagement_types.list",
    method: "GET",
    path: "/v1/clients/{clientId}/engagement-types",
    realm: "workforce",
    mutates: false,
  },
  "clients.geography.read": {
    id: "clients.geography.read",
    method: "GET",
    path: "/v1/geography",
    realm: "workforce",
    mutates: false,
  },
  "clients.geography.search": {
    id: "clients.geography.search",
    method: "GET",
    path: "/v1/geography/cities",
    realm: "workforce",
    mutates: false,
  },
  "clients.job_assignment.remove": {
    id: "clients.job_assignment.remove",
    method: "POST",
    path: "/v1/jobs/{jobId}/assignments/{membershipId}/remove",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_assignment.set": {
    id: "clients.job_assignment.set",
    method: "POST",
    path: "/v1/jobs/{jobId}/assignments",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_budget.set": {
    id: "clients.job_budget.set",
    method: "POST",
    path: "/v1/jobs/{jobId}/budget",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_commercials.history": {
    id: "clients.job_commercials.history",
    method: "GET",
    path: "/v1/jobs/{jobId}/commercial-history",
    realm: "workforce",
    mutates: false,
  },
  "clients.job_commercials.set": {
    id: "clients.job_commercials.set",
    method: "POST",
    path: "/v1/jobs/{jobId}/commercials",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_document.add": {
    id: "clients.job_document.add",
    method: "POST",
    path: "/v1/jobs/{jobId}/documents",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_document.add_link": {
    id: "clients.job_document.add_link",
    method: "POST",
    path: "/v1/jobs/{jobId}/document-links",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_document.draft": {
    id: "clients.job_document.draft",
    method: "POST",
    path: "/v1/jobs/{jobId}/documents/draft",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_document.library": {
    id: "clients.job_document.library",
    method: "GET",
    path: "/v1/jobs/{jobId}/document-library",
    realm: "workforce",
    mutates: false,
  },
  "clients.job_document.list": {
    id: "clients.job_document.list",
    method: "GET",
    path: "/v1/jobs/{jobId}/documents",
    realm: "workforce",
    mutates: false,
  },
  "clients.job_document.parse": {
    id: "clients.job_document.parse",
    method: "POST",
    path: "/v1/job-documents/{documentId}/parse",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_document.read": {
    id: "clients.job_document.read",
    method: "GET",
    path: "/v1/job-documents/{documentId}",
    realm: "workforce",
    mutates: false,
  },
  "clients.job_document.remove": {
    id: "clients.job_document.remove",
    method: "POST",
    path: "/v1/jobs/{jobId}/document-library/{documentId}/removal",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_document.rename": {
    id: "clients.job_document.rename",
    method: "PATCH",
    path: "/v1/jobs/{jobId}/document-library/{documentId}",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_document.reuse": {
    id: "clients.job_document.reuse",
    method: "POST",
    path: "/v1/jobs/{jobId}/documents/{documentId}",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_document.save_draft": {
    id: "clients.job_document.save_draft",
    method: "POST",
    path: "/v1/jobs/{jobId}/documents/drafted",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_document.upload": {
    id: "clients.job_document.upload",
    method: "POST",
    path: "/v1/job-documents",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_location.list": {
    id: "clients.job_location.list",
    method: "GET",
    path: "/v1/jobs/{jobId}/locations",
    realm: "workforce",
    mutates: false,
  },
  "clients.job_location.set": {
    id: "clients.job_location.set",
    method: "PUT",
    path: "/v1/jobs/{jobId}/locations",
    realm: "workforce",
    mutates: true,
  },
  "clients.job_skill.save": {
    id: "clients.job_skill.save",
    method: "PUT",
    path: "/v1/jobs/{jobId}/skills",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.acknowledge_intake": {
    id: "clients.job.acknowledge_intake",
    method: "POST",
    path: "/v1/jobs/{jobId}/intake/acknowledge",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.analyse_intake": {
    id: "clients.job.analyse_intake",
    method: "POST",
    path: "/v1/jobs/{jobId}/intake",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.artifact_history": {
    id: "clients.job.artifact_history",
    method: "GET",
    path: "/v1/jobs/{jobId}/artifacts/{kind}/versions",
    realm: "workforce",
    mutates: false,
  },
  "clients.job.artifacts": {
    id: "clients.job.artifacts",
    method: "GET",
    path: "/v1/jobs/{jobId}/artifacts",
    realm: "workforce",
    mutates: false,
  },
  "clients.job.bulk_assign": {
    id: "clients.job.bulk_assign",
    method: "POST",
    path: "/v1/job-bulk/assignments",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.bulk_close": {
    id: "clients.job.bulk_close",
    method: "POST",
    path: "/v1/job-bulk/close",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.bulk_set_owner": {
    id: "clients.job.bulk_set_owner",
    method: "POST",
    path: "/v1/job-bulk/owner",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.ceiling": {
    id: "clients.job.ceiling",
    method: "GET",
    path: "/v1/jobs/{jobId}/ceiling",
    realm: "workforce",
    mutates: false,
  },
  "clients.job.create": {
    id: "clients.job.create",
    method: "POST",
    path: "/v1/jobs",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.disposition_flag": {
    id: "clients.job.disposition_flag",
    method: "POST",
    path: "/v1/jobs/{jobId}/intake/flags/{flagId}",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.duplicate": {
    id: "clients.job.duplicate",
    method: "POST",
    path: "/v1/jobs/{jobId}/duplicate",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.edit_artifact": {
    id: "clients.job.edit_artifact",
    method: "POST",
    path: "/v1/jobs/{jobId}/artifacts/{kind}/content",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.field_assist": {
    id: "clients.job.field_assist",
    method: "POST",
    path: "/v1/jobs/{jobId}/field-assist",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.field_history": {
    id: "clients.job.field_history",
    method: "GET",
    path: "/v1/jobs/{jobId}/field-history",
    realm: "workforce",
    mutates: false,
  },
  "clients.job.generate_artifact": {
    id: "clients.job.generate_artifact",
    method: "POST",
    path: "/v1/jobs/{jobId}/artifacts/{kind}",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.generate_boolean_strings": {
    id: "clients.job.generate_boolean_strings",
    method: "POST",
    path: "/v1/jobs/{jobId}/boolean-strings",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.intake_questions": {
    id: "clients.job.intake_questions",
    method: "GET",
    path: "/v1/jobs/{jobId}/intake/questions",
    realm: "workforce",
    mutates: false,
  },
  "clients.job.list": {
    id: "clients.job.list",
    method: "GET",
    path: "/v1/jobs",
    realm: "workforce",
    mutates: false,
  },
  "clients.job.margin": {
    id: "clients.job.margin",
    method: "GET",
    path: "/v1/jobs/{jobId}/margin",
    realm: "workforce",
    mutates: false,
  },
  "clients.job.overview": {
    id: "clients.job.overview",
    method: "GET",
    path: "/v1/jobs/overview",
    realm: "workforce",
    mutates: false,
  },
  "clients.job.preview_posting": {
    id: "clients.job.preview_posting",
    method: "GET",
    path: "/v1/jobs/{jobId}/posting-preview",
    realm: "workforce",
    mutates: false,
  },
  "clients.job.read": {
    id: "clients.job.read",
    method: "GET",
    path: "/v1/jobs/{jobId}",
    realm: "workforce",
    mutates: false,
  },
  "clients.job.refresh_posting": {
    id: "clients.job.refresh_posting",
    method: "POST",
    path: "/v1/jobs/{jobId}/posting-refresh",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.resolve_artifact_proposal": {
    id: "clients.job.resolve_artifact_proposal",
    method: "POST",
    path: "/v1/jobs/{jobId}/artifacts/{kind}/proposal",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.restore_artifact": {
    id: "clients.job.restore_artifact",
    method: "POST",
    path: "/v1/jobs/{jobId}/artifacts/{kind}/restore",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.set_owner": {
    id: "clients.job.set_owner",
    method: "POST",
    path: "/v1/jobs/{jobId}/owner",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.set_publish_clearance": {
    id: "clients.job.set_publish_clearance",
    method: "POST",
    path: "/v1/jobs/{jobId}/publish-clearance",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.update": {
    id: "clients.job.update",
    method: "POST",
    path: "/v1/jobs/{jobId}",
    realm: "workforce",
    mutates: true,
  },
  "clients.job.update_status": {
    id: "clients.job.update_status",
    method: "POST",
    path: "/v1/jobs/{jobId}/status",
    realm: "workforce",
    mutates: true,
  },
  "clients.rate_card.correct": {
    id: "clients.rate_card.correct",
    method: "POST",
    path: "/v1/rate-cards/{rateCardId}/correct",
    realm: "workforce",
    mutates: true,
  },
  "clients.rate_card.create": {
    id: "clients.rate_card.create",
    method: "POST",
    path: "/v1/clients/{clientId}/rate-cards",
    realm: "workforce",
    mutates: true,
  },
  "clients.rate_card.retire": {
    id: "clients.rate_card.retire",
    method: "POST",
    path: "/v1/rate-cards/{rateCardId}/retire",
    realm: "workforce",
    mutates: true,
  },
  "clients.rate_card.update": {
    id: "clients.rate_card.update",
    method: "PATCH",
    path: "/v1/rate-cards/{rateCardId}",
    realm: "workforce",
    mutates: true,
  },
  "clients.sla.list": {
    id: "clients.sla.list",
    method: "GET",
    path: "/v1/slas",
    realm: "workforce",
    mutates: false,
  },
  "clients.sla.set": {
    id: "clients.sla.set",
    method: "PUT",
    path: "/v1/clients/{clientId}/slas/{slaType}",
    realm: "workforce",
    mutates: true,
  },
  "clients.time_to_fill.read": {
    id: "clients.time_to_fill.read",
    method: "GET",
    path: "/v1/insights/time-to-fill",
    realm: "workforce",
    mutates: false,
  },
  "clients.work_location.create": {
    id: "clients.work_location.create",
    method: "POST",
    path: "/v1/work-locations",
    realm: "workforce",
    mutates: true,
  },
  "clients.work_location.list": {
    id: "clients.work_location.list",
    method: "GET",
    path: "/v1/work-locations",
    realm: "workforce",
    mutates: false,
  },
  "clients.work_location.retire": {
    id: "clients.work_location.retire",
    method: "POST",
    path: "/v1/work-locations/{locationId}/retire",
    realm: "workforce",
    mutates: true,
  },
  "copilot.ai_settings.read": {
    id: "copilot.ai_settings.read",
    method: "GET",
    path: "/v1/ai-settings",
    realm: "workforce",
    mutates: false,
  },
  "copilot.ai_settings.update": {
    id: "copilot.ai_settings.update",
    method: "PUT",
    path: "/v1/ai-settings/{featureKey}",
    realm: "workforce",
    mutates: true,
  },
  "copilot.candidate_decision.read": {
    id: "copilot.candidate_decision.read",
    method: "GET",
    path: "/v1/ai-decisions/candidate/{decisionId}",
    realm: "workforce",
    mutates: false,
  },
  "copilot.job_decision.read": {
    id: "copilot.job_decision.read",
    method: "GET",
    path: "/v1/ai-decisions/{decisionId}",
    realm: "workforce",
    mutates: false,
  },
  "copilot.platform_ai_config.read": {
    id: "copilot.platform_ai_config.read",
    method: "GET",
    path: "/v1/platform/ai-config",
    realm: "platform",
    mutates: false,
  },
  "copilot.platform_ai_connection.check": {
    id: "copilot.platform_ai_connection.check",
    method: "POST",
    path: "/v1/platform/ai-connections/{adapter}/check",
    realm: "platform",
    mutates: true,
  },
  "copilot.platform_ai_credential.clear": {
    id: "copilot.platform_ai_credential.clear",
    method: "DELETE",
    path: "/v1/platform/ai-credentials/{provider}",
    realm: "platform",
    mutates: true,
  },
  "copilot.platform_ai_credential.read": {
    id: "copilot.platform_ai_credential.read",
    method: "GET",
    path: "/v1/platform/ai-credentials",
    realm: "platform",
    mutates: false,
  },
  "copilot.platform_ai_credential.save": {
    id: "copilot.platform_ai_credential.save",
    method: "PUT",
    path: "/v1/platform/ai-credentials/{provider}",
    realm: "platform",
    mutates: true,
  },
  "copilot.platform_ai_model.list": {
    id: "copilot.platform_ai_model.list",
    method: "GET",
    path: "/v1/platform/ai-models/{provider}",
    realm: "platform",
    mutates: false,
  },
  "copilot.platform_ai_price.create": {
    id: "copilot.platform_ai_price.create",
    method: "POST",
    path: "/v1/platform/ai-prices",
    realm: "platform",
    mutates: true,
  },
  "copilot.platform_ai_route.upsert": {
    id: "copilot.platform_ai_route.upsert",
    method: "PUT",
    path: "/v1/platform/ai-routes",
    realm: "platform",
    mutates: true,
  },
  "copilot.platform_ai_usage.read": {
    id: "copilot.platform_ai_usage.read",
    method: "GET",
    path: "/v1/platform/tenants/{tenantId}/ai-usage",
    realm: "platform",
    mutates: false,
  },
  "copilot.resume_parse.read": {
    id: "copilot.resume_parse.read",
    method: "GET",
    path: "/v1/resumes/{resumeId}/parse",
    realm: "workforce",
    mutates: false,
  },
  "copilot.resume_parse.record_review": {
    id: "copilot.resume_parse.record_review",
    method: "POST",
    path: "/v1/resume-parses/{resumeId}/review",
    realm: "workforce",
    mutates: true,
  },
  "identity.auth.password": {
    id: "identity.auth.password",
    method: "POST",
    path: "/v1/auth/password",
    realm: "workforce",
    mutates: true,
  },
  "identity.auth.refresh": {
    id: "identity.auth.refresh",
    method: "POST",
    path: "/v1/auth/refresh",
    realm: "workforce",
    mutates: true,
  },
  "identity.auth.reset_confirm": {
    id: "identity.auth.reset_confirm",
    method: "POST",
    path: "/v1/auth/password-reset/confirm",
    realm: "workforce",
    mutates: true,
  },
  "identity.auth.reset_request": {
    id: "identity.auth.reset_request",
    method: "POST",
    path: "/v1/auth/password-reset/request",
    realm: "workforce",
    mutates: true,
  },
  "identity.auth.sign_out": {
    id: "identity.auth.sign_out",
    method: "POST",
    path: "/v1/auth/sign-out",
    realm: "workforce",
    mutates: true,
  },
  "identity.invitation.activate": {
    id: "identity.invitation.activate",
    method: "POST",
    path: "/v1/invitations/{userAccountId}/activation",
    realm: "workforce",
    mutates: true,
  },
  "identity.platform_invitation.list": {
    id: "identity.platform_invitation.list",
    method: "GET",
    path: "/v1/platform/tenants/{tenantId}/invitations",
    realm: "platform",
    mutates: false,
  },
  "identity.portal.applications": {
    id: "identity.portal.applications",
    method: "GET",
    path: "/v1/portal/applications",
    realm: "candidate",
    mutates: false,
  },
  "identity.portal.apply": {
    id: "identity.portal.apply",
    method: "POST",
    path: "/v1/portal/applications",
    realm: "candidate",
    mutates: true,
  },
  "identity.portal.start": {
    id: "identity.portal.start",
    method: "POST",
    path: "/v1/portal/auth/start",
    realm: "candidate",
    mutates: true,
  },
  "identity.portal.verify": {
    id: "identity.portal.verify",
    method: "POST",
    path: "/v1/portal/auth/verify",
    realm: "candidate",
    mutates: true,
  },
  "insight.activity.list": {
    id: "insight.activity.list",
    method: "GET",
    path: "/v1/activity",
    realm: "workforce",
    mutates: false,
  },
  "insight.attention.read": {
    id: "insight.attention.read",
    method: "GET",
    path: "/v1/insights/attention",
    realm: "workforce",
    mutates: false,
  },
  "insight.blocked_by_owner.read": {
    id: "insight.blocked_by_owner.read",
    method: "GET",
    path: "/v1/insights/blocked-by-owner",
    realm: "workforce",
    mutates: false,
  },
  "insight.blocked.read": {
    id: "insight.blocked.read",
    method: "GET",
    path: "/v1/insights/blocked",
    realm: "workforce",
    mutates: false,
  },
  "insight.changes.read": {
    id: "insight.changes.read",
    method: "GET",
    path: "/v1/insights/changes",
    realm: "workforce",
    mutates: false,
  },
  "insight.client_responsiveness.read": {
    id: "insight.client_responsiveness.read",
    method: "GET",
    path: "/v1/insights/client-responsiveness",
    realm: "workforce",
    mutates: false,
  },
  "insight.commitment.create": {
    id: "insight.commitment.create",
    method: "POST",
    path: "/v1/commitments",
    realm: "workforce",
    mutates: true,
  },
  "insight.commitment.delete": {
    id: "insight.commitment.delete",
    method: "DELETE",
    path: "/v1/commitments/{commitmentId}",
    realm: "workforce",
    mutates: true,
  },
  "insight.commitment.list": {
    id: "insight.commitment.list",
    method: "GET",
    path: "/v1/commitments",
    realm: "workforce",
    mutates: false,
  },
  "insight.commitment.roll_forward": {
    id: "insight.commitment.roll_forward",
    method: "POST",
    path: "/v1/commitments/roll-forward",
    realm: "workforce",
    mutates: true,
  },
  "insight.commitment.update": {
    id: "insight.commitment.update",
    method: "PATCH",
    path: "/v1/commitments/{commitmentId}",
    realm: "workforce",
    mutates: true,
  },
  "insight.conversion.read": {
    id: "insight.conversion.read",
    method: "GET",
    path: "/v1/insights/conversion",
    realm: "workforce",
    mutates: false,
  },
  "insight.copilot.ask": {
    id: "insight.copilot.ask",
    method: "POST",
    path: "/v1/desk/ask",
    realm: "workforce",
    mutates: true,
  },
  "insight.copilot.ask_book": {
    id: "insight.copilot.ask_book",
    method: "POST",
    path: "/v1/book/ask",
    realm: "workforce",
    mutates: true,
  },
  "insight.dashboard.read": {
    id: "insight.dashboard.read",
    method: "GET",
    path: "/v1/dashboard/pipeline",
    realm: "workforce",
    mutates: false,
  },
  "insight.digest.read": {
    id: "insight.digest.read",
    method: "GET",
    path: "/v1/insights/digest",
    realm: "workforce",
    mutates: false,
  },
  "insight.effort_board.read": {
    id: "insight.effort_board.read",
    method: "GET",
    path: "/v1/insights/effort",
    realm: "workforce",
    mutates: false,
  },
  "insight.job_board.read": {
    id: "insight.job_board.read",
    method: "GET",
    path: "/v1/dashboard/job-board",
    realm: "workforce",
    mutates: false,
  },
  "insight.job_effort.read": {
    id: "insight.job_effort.read",
    method: "GET",
    path: "/v1/jobs/{jobId}/effort",
    realm: "workforce",
    mutates: false,
  },
  "insight.standup.desk": {
    id: "insight.standup.desk",
    method: "GET",
    path: "/v1/insights/standup/desk",
    realm: "workforce",
    mutates: false,
  },
  "insight.time_leak.read": {
    id: "insight.time_leak.read",
    method: "GET",
    path: "/v1/insights/time-leak",
    realm: "workforce",
    mutates: false,
  },
  "insight.today.read": {
    id: "insight.today.read",
    method: "GET",
    path: "/v1/insights/today",
    realm: "workforce",
    mutates: false,
  },
  "outreach.email_sender.list": {
    id: "outreach.email_sender.list",
    method: "GET",
    path: "/v1/email-senders",
    realm: "workforce",
    mutates: false,
  },
  "outreach.email_sender.save": {
    id: "outreach.email_sender.save",
    method: "POST",
    path: "/v1/email-senders",
    realm: "workforce",
    mutates: true,
  },
  "outreach.email_sender.set_status": {
    id: "outreach.email_sender.set_status",
    method: "POST",
    path: "/v1/email-senders/{senderId}/status",
    realm: "workforce",
    mutates: true,
  },
  "outreach.email_sender.verify": {
    id: "outreach.email_sender.verify",
    method: "POST",
    path: "/v1/email-senders/{senderId}/verify",
    realm: "workforce",
    mutates: true,
  },
  "outreach.interview_invitation.list": {
    id: "outreach.interview_invitation.list",
    method: "GET",
    path: "/v1/interview-occurrences/{occurrenceId}/invitation-deliveries",
    realm: "workforce",
    mutates: false,
  },
  "outreach.message_setting.list": {
    id: "outreach.message_setting.list",
    method: "GET",
    path: "/v1/message-settings",
    realm: "workforce",
    mutates: false,
  },
  "outreach.message_setting.set": {
    id: "outreach.message_setting.set",
    method: "PUT",
    path: "/v1/message-settings/{templateKey}",
    realm: "workforce",
    mutates: true,
  },
  "outreach.message_template.list": {
    id: "outreach.message_template.list",
    method: "GET",
    path: "/v1/platform/message-templates",
    realm: "platform",
    mutates: false,
  },
  "outreach.message_template.read": {
    id: "outreach.message_template.read",
    method: "GET",
    path: "/v1/platform/message-templates/{templateKey}",
    realm: "platform",
    mutates: false,
  },
  "outreach.message_template.revert": {
    id: "outreach.message_template.revert",
    method: "DELETE",
    path: "/v1/platform/message-templates/{templateKey}",
    realm: "platform",
    mutates: true,
  },
  "outreach.message_template.save": {
    id: "outreach.message_template.save",
    method: "PUT",
    path: "/v1/platform/message-templates/{templateKey}",
    realm: "platform",
    mutates: true,
  },
  "outreach.message_template.set_sender": {
    id: "outreach.message_template.set_sender",
    method: "PUT",
    path: "/v1/platform/message-templates/{templateKey}/sender",
    realm: "platform",
    mutates: true,
  },
  "outreach.message_template.test_send": {
    id: "outreach.message_template.test_send",
    method: "POST",
    path: "/v1/platform/message-templates/{templateKey}/test-send",
    realm: "platform",
    mutates: true,
  },
  "outreach.message.draft": {
    id: "outreach.message.draft",
    method: "POST",
    path: "/v1/messages/draft",
    realm: "workforce",
    mutates: true,
  },
  "outreach.message.list": {
    id: "outreach.message.list",
    method: "GET",
    path: "/v1/messages",
    realm: "workforce",
    mutates: false,
  },
  "outreach.message.retry": {
    id: "outreach.message.retry",
    method: "POST",
    path: "/v1/messages/{messageId}/retry",
    realm: "workforce",
    mutates: true,
  },
  "outreach.message.send": {
    id: "outreach.message.send",
    method: "POST",
    path: "/v1/messages",
    realm: "workforce",
    mutates: true,
  },
  "outreach.platform_email_sender.delete": {
    id: "outreach.platform_email_sender.delete",
    method: "DELETE",
    path: "/v1/platform/email-senders/{senderId}",
    realm: "platform",
    mutates: true,
  },
  "outreach.platform_email_sender.read": {
    id: "outreach.platform_email_sender.read",
    method: "GET",
    path: "/v1/platform/email-senders",
    realm: "platform",
    mutates: false,
  },
  "outreach.platform_email_sender.save": {
    id: "outreach.platform_email_sender.save",
    method: "PUT",
    path: "/v1/platform/email-senders",
    realm: "platform",
    mutates: true,
  },
  "outreach.platform_email_sender.verify": {
    id: "outreach.platform_email_sender.verify",
    method: "POST",
    path: "/v1/platform/email-senders/{senderId}/verify",
    realm: "platform",
    mutates: true,
  },
  "outreach.platform_message.read": {
    id: "outreach.platform_message.read",
    method: "GET",
    path: "/v1/platform/tenants/{tenantId}/messages",
    realm: "platform",
    mutates: false,
  },
  "outreach.stage_message.list": {
    id: "outreach.stage_message.list",
    method: "GET",
    path: "/v1/jobs/{jobId}/stage-messages",
    realm: "workforce",
    mutates: false,
  },
  "outreach.stage_message.save": {
    id: "outreach.stage_message.save",
    method: "PUT",
    path: "/v1/jobs/{jobId}/stage-messages/{stepId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.application_answer.list": {
    id: "pipeline.application_answer.list",
    method: "GET",
    path: "/v1/applications/{applicationId}/screening-answers",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.application_answer.save": {
    id: "pipeline.application_answer.save",
    method: "PUT",
    path: "/v1/applications/{applicationId}/screening-answers",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.application.block": {
    id: "pipeline.application.block",
    method: "POST",
    path: "/v1/applications/{applicationId}/block",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.application.bulk_change_stage": {
    id: "pipeline.application.bulk_change_stage",
    method: "POST",
    path: "/v1/applications/bulk-stage",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.application.bulk_close": {
    id: "pipeline.application.bulk_close",
    method: "POST",
    path: "/v1/applications/bulk-close",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.application.change_stage": {
    id: "pipeline.application.change_stage",
    method: "POST",
    path: "/v1/applications/{applicationId}/stage",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.application.close": {
    id: "pipeline.application.close",
    method: "POST",
    path: "/v1/applications/{applicationId}/close",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.application.create": {
    id: "pipeline.application.create",
    method: "POST",
    path: "/v1/applications",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.application.list": {
    id: "pipeline.application.list",
    method: "GET",
    path: "/v1/applications",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.application.read": {
    id: "pipeline.application.read",
    method: "GET",
    path: "/v1/applications/{applicationId}",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.application.revive": {
    id: "pipeline.application.revive",
    method: "POST",
    path: "/v1/applications/{applicationId}/revive",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.application.unblock": {
    id: "pipeline.application.unblock",
    method: "POST",
    path: "/v1/applications/{applicationId}/unblock",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.availability.book": {
    id: "pipeline.availability.book",
    method: "POST",
    path: "/v1/occurrences/{occurrenceId}/book-availability",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.availability.list": {
    id: "pipeline.availability.list",
    method: "GET",
    path: "/v1/jobs/{jobId}/panel/availability",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.availability.record": {
    id: "pipeline.availability.record",
    method: "POST",
    path: "/v1/panel/{panellistId}/availability",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.availability.withdraw": {
    id: "pipeline.availability.withdraw",
    method: "POST",
    path: "/v1/availability/{availabilityId}/withdraw",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.compensation.compute": {
    id: "pipeline.compensation.compute",
    method: "POST",
    path: "/v1/compensation/package",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.copilot.ask": {
    id: "pipeline.copilot.ask",
    method: "POST",
    path: "/v1/applications/{applicationId}/ask",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.document_clause.read": {
    id: "pipeline.document_clause.read",
    method: "GET",
    path: "/v1/document-clauses",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.document_clause.save": {
    id: "pipeline.document_clause.save",
    method: "PUT",
    path: "/v1/document-clauses/{key}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.document_link.open": {
    id: "pipeline.document_link.open",
    method: "POST",
    path: "/v1/document-links/open",
    realm: "candidate",
    mutates: true,
  },
  "pipeline.document_link.submit": {
    id: "pipeline.document_link.submit",
    method: "POST",
    path: "/v1/document-links/submissions",
    realm: "candidate",
    mutates: true,
  },
  "pipeline.document_request.create": {
    id: "pipeline.document_request.create",
    method: "POST",
    path: "/v1/applications/{applicationId}/document-request",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.document_request.read": {
    id: "pipeline.document_request.read",
    method: "GET",
    path: "/v1/applications/{applicationId}/document-request",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.document_request.revoke": {
    id: "pipeline.document_request.revoke",
    method: "POST",
    path: "/v1/document-requests/{requestId}/revocation",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.document_set.read": {
    id: "pipeline.document_set.read",
    method: "GET",
    path: "/v1/document-sets",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.document_set.save": {
    id: "pipeline.document_set.save",
    method: "PUT",
    path: "/v1/document-sets/{setId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.document_submission.review": {
    id: "pipeline.document_submission.review",
    method: "PATCH",
    path: "/v1/document-submissions/{submissionId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.document_template.read": {
    id: "pipeline.document_template.read",
    method: "GET",
    path: "/v1/document-templates",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.document_template.save": {
    id: "pipeline.document_template.save",
    method: "PUT",
    path: "/v1/document-templates/{templateId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview_note.add": {
    id: "pipeline.interview_note.add",
    method: "POST",
    path: "/v1/interviews/{interviewId}/notes",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview_note.list": {
    id: "pipeline.interview_note.list",
    method: "GET",
    path: "/v1/interviews/{interviewId}/notes",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.interview_plan.read": {
    id: "pipeline.interview_plan.read",
    method: "GET",
    path: "/v1/interview-plans",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.interview_plan.reset_job": {
    id: "pipeline.interview_plan.reset_job",
    method: "DELETE",
    path: "/v1/jobs/{jobId}/interview-plan",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview_plan.save_client": {
    id: "pipeline.interview_plan.save_client",
    method: "PUT",
    path: "/v1/clients/{clientId}/interview-plan",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview_plan.save_default": {
    id: "pipeline.interview_plan.save_default",
    method: "PUT",
    path: "/v1/interview-plans/default",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview_plan.save_job": {
    id: "pipeline.interview_plan.save_job",
    method: "PUT",
    path: "/v1/jobs/{jobId}/interview-plan",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview.clashes": {
    id: "pipeline.interview.clashes",
    method: "GET",
    path: "/v1/interview-clashes",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.interview.client_view": {
    id: "pipeline.interview.client_view",
    method: "GET",
    path: "/v1/client-views/interviews",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.interview.history": {
    id: "pipeline.interview.history",
    method: "GET",
    path: "/v1/interviews/{interviewId}/history",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.interview.list": {
    id: "pipeline.interview.list",
    method: "GET",
    path: "/v1/interviews",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.interview.provision_meeting": {
    id: "pipeline.interview.provision_meeting",
    method: "POST",
    path: "/v1/interview-occurrences/{occurrenceId}/meeting",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview.read": {
    id: "pipeline.interview.read",
    method: "GET",
    path: "/v1/interviews/{interviewId}",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.interview.record_confirmation": {
    id: "pipeline.interview.record_confirmation",
    method: "POST",
    path: "/v1/interview-occurrences/{occurrenceId}/confirmations",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview.record_past": {
    id: "pipeline.interview.record_past",
    method: "POST",
    path: "/v1/applications/{applicationId}/interviews/record-past",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview.remove": {
    id: "pipeline.interview.remove",
    method: "DELETE",
    path: "/v1/interviews/{interviewId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview.schedule": {
    id: "pipeline.interview.schedule",
    method: "POST",
    path: "/v1/applications/{applicationId}/interviews",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview.seed_from_plan": {
    id: "pipeline.interview.seed_from_plan",
    method: "POST",
    path: "/v1/applications/{applicationId}/interviews/from-plan",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview.send_invitation": {
    id: "pipeline.interview.send_invitation",
    method: "POST",
    path: "/v1/interview-occurrences/{occurrenceId}/invitations",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview.set_outcome": {
    id: "pipeline.interview.set_outcome",
    method: "POST",
    path: "/v1/interviews/{interviewId}/outcome",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview.set_owner": {
    id: "pipeline.interview.set_owner",
    method: "POST",
    path: "/v1/interviews/{interviewId}/owner",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.interview.update": {
    id: "pipeline.interview.update",
    method: "PATCH",
    path: "/v1/interviews/{interviewId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.job_panel.add": {
    id: "pipeline.job_panel.add",
    method: "POST",
    path: "/v1/jobs/{jobId}/panel",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.job_panel.list": {
    id: "pipeline.job_panel.list",
    method: "GET",
    path: "/v1/jobs/{jobId}/panel",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.job_panel.remove": {
    id: "pipeline.job_panel.remove",
    method: "POST",
    path: "/v1/jobs/{jobId}/panel/{panellistId}/remove",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.job_question.list": {
    id: "pipeline.job_question.list",
    method: "GET",
    path: "/v1/jobs/{jobId}/screening-questions",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.job_question.save": {
    id: "pipeline.job_question.save",
    method: "PUT",
    path: "/v1/jobs/{jobId}/screening-questions",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.joining_touchpoint.record": {
    id: "pipeline.joining_touchpoint.record",
    method: "PATCH",
    path: "/v1/joining-touchpoints/{touchpointId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.joining_touchpoint.schedule": {
    id: "pipeline.joining_touchpoint.schedule",
    method: "POST",
    path: "/v1/joinings/{joiningId}/touchpoints",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.joining.dropped": {
    id: "pipeline.joining.dropped",
    method: "POST",
    path: "/v1/joinings/{joiningId}/dropped",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.joining.joined": {
    id: "pipeline.joining.joined",
    method: "POST",
    path: "/v1/joinings/{joiningId}/joined",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.joining.list": {
    id: "pipeline.joining.list",
    method: "GET",
    path: "/v1/joinings",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.joining.read": {
    id: "pipeline.joining.read",
    method: "GET",
    path: "/v1/applications/{applicationId}/joining",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.joining.update": {
    id: "pipeline.joining.update",
    method: "PATCH",
    path: "/v1/joinings/{joiningId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.match_settings.read": {
    id: "pipeline.match_settings.read",
    method: "GET",
    path: "/v1/match-settings",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.match_settings.save": {
    id: "pipeline.match_settings.save",
    method: "POST",
    path: "/v1/match-settings",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.meeting_application.remove": {
    id: "pipeline.meeting_application.remove",
    method: "POST",
    path: "/v1/meeting-applications/{provider}/remove",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.meeting_application.save": {
    id: "pipeline.meeting_application.save",
    method: "POST",
    path: "/v1/meeting-applications",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.meeting_connection.disconnect": {
    id: "pipeline.meeting_connection.disconnect",
    method: "POST",
    path: "/v1/meeting-connections/{connectionId}/disconnect",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.meeting_connection.finish": {
    id: "pipeline.meeting_connection.finish",
    method: "POST",
    path: "/v1/meeting-connections/consent/completion",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.meeting_connection.list": {
    id: "pipeline.meeting_connection.list",
    method: "GET",
    path: "/v1/meeting-connections",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.meeting_connection.start": {
    id: "pipeline.meeting_connection.start",
    method: "POST",
    path: "/v1/meeting-connections/consent",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.meeting_connection.verify": {
    id: "pipeline.meeting_connection.verify",
    method: "POST",
    path: "/v1/meeting-connections/{connectionId}/verify",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer_document.generate": {
    id: "pipeline.offer_document.generate",
    method: "POST",
    path: "/v1/offers/{offerId}/documents",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer_document.issue": {
    id: "pipeline.offer_document.issue",
    method: "POST",
    path: "/v1/offer-documents/{documentId}/issue",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer_document.read": {
    id: "pipeline.offer_document.read",
    method: "GET",
    path: "/v1/offers/{offerId}/documents",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.offer_document.sign": {
    id: "pipeline.offer_document.sign",
    method: "POST",
    path: "/v1/offer-documents/{documentId}/signature",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer_scenario.counter": {
    id: "pipeline.offer_scenario.counter",
    method: "POST",
    path: "/v1/offer-scenarios/{scenarioId}/counter",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer_scenario.create": {
    id: "pipeline.offer_scenario.create",
    method: "POST",
    path: "/v1/applications/{applicationId}/scenarios",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer_scenario.promote": {
    id: "pipeline.offer_scenario.promote",
    method: "POST",
    path: "/v1/offer-scenarios/{scenarioId}/promotion",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer_scenario.read": {
    id: "pipeline.offer_scenario.read",
    method: "GET",
    path: "/v1/applications/{applicationId}/scenarios",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.offer_scenario.share": {
    id: "pipeline.offer_scenario.share",
    method: "POST",
    path: "/v1/offer-scenarios/{scenarioId}/share",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer_scenario.update": {
    id: "pipeline.offer_scenario.update",
    method: "PATCH",
    path: "/v1/offer-scenarios/{scenarioId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer_settings.components": {
    id: "pipeline.offer_settings.components",
    method: "PUT",
    path: "/v1/offer-settings/components",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer_settings.read": {
    id: "pipeline.offer_settings.read",
    method: "GET",
    path: "/v1/offer-settings",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.offer_settings.update": {
    id: "pipeline.offer_settings.update",
    method: "PUT",
    path: "/v1/offer-settings/policy",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer.approve": {
    id: "pipeline.offer.approve",
    method: "POST",
    path: "/v1/offers/{offerId}/approval",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer.create": {
    id: "pipeline.offer.create",
    method: "POST",
    path: "/v1/applications/{applicationId}/offers",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer.joining": {
    id: "pipeline.offer.joining",
    method: "POST",
    path: "/v1/offers/{offerId}/joining",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer.list": {
    id: "pipeline.offer.list",
    method: "GET",
    path: "/v1/offers",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.offer.read": {
    id: "pipeline.offer.read",
    method: "GET",
    path: "/v1/applications/{applicationId}/offers",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.offer.release": {
    id: "pipeline.offer.release",
    method: "POST",
    path: "/v1/offers/{offerId}/release",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer.respond": {
    id: "pipeline.offer.respond",
    method: "POST",
    path: "/v1/offers/{offerId}/response",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer.update": {
    id: "pipeline.offer.update",
    method: "PATCH",
    path: "/v1/offers/{offerId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.offer.withdraw": {
    id: "pipeline.offer.withdraw",
    method: "POST",
    path: "/v1/offers/{offerId}/withdrawal",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.setup.create_defaults": {
    id: "pipeline.setup.create_defaults",
    method: "POST",
    path: "/v1/pipeline-setup",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.stage_template.read_default": {
    id: "pipeline.stage_template.read_default",
    method: "GET",
    path: "/v1/stage-templates/default",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.submission.create": {
    id: "pipeline.submission.create",
    method: "POST",
    path: "/v1/applications/{applicationId}/submissions",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.submission.list": {
    id: "pipeline.submission.list",
    method: "GET",
    path: "/v1/submissions",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.submission.record_response": {
    id: "pipeline.submission.record_response",
    method: "PATCH",
    path: "/v1/submissions/{submissionId}",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.taxonomy.builder_read": {
    id: "pipeline.taxonomy.builder_read",
    method: "GET",
    path: "/v1/taxonomy/builder",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.taxonomy.create": {
    id: "pipeline.taxonomy.create",
    method: "POST",
    path: "/v1/taxonomy",
    realm: "workforce",
    mutates: true,
  },
  "pipeline.taxonomy.list": {
    id: "pipeline.taxonomy.list",
    method: "GET",
    path: "/v1/taxonomy",
    realm: "workforce",
    mutates: false,
  },
  "pipeline.taxonomy.update": {
    id: "pipeline.taxonomy.update",
    method: "PATCH",
    path: "/v1/taxonomy/{entryId}",
    realm: "workforce",
    mutates: true,
  },
} as const satisfies Record<OperationId, {
  readonly id: OperationId;
  readonly method: string;
  readonly path: string;
  readonly realm: string;
  readonly mutates: boolean;
}>;

/** Binds each operation id to its request and response shapes, for the typed client. */
export interface OperationTypes {
  "access.agency_profile.update": { request: AccessAgencyProfileUpdateRequest; response: AccessAgencyProfileUpdateResponse };
  "access.group.create": { request: AccessGroupCreateRequest; response: AccessGroupCreateResponse };
  "access.group.list": { request: AccessGroupListRequest; response: AccessGroupListResponse };
  "access.group.read": { request: AccessGroupReadRequest; response: AccessGroupReadResponse };
  "access.group.remove_member": { request: AccessGroupRemoveMemberRequest; response: AccessGroupRemoveMemberResponse };
  "access.group.set_member": { request: AccessGroupSetMemberRequest; response: AccessGroupSetMemberResponse };
  "access.group.update": { request: AccessGroupUpdateRequest; response: AccessGroupUpdateResponse };
  "access.me.read": { request: AccessMeReadRequest; response: AccessMeReadResponse };
  "access.member.deactivate": { request: AccessMemberDeactivateRequest; response: AccessMemberDeactivateResponse };
  "access.member.invite": { request: AccessMemberInviteRequest; response: AccessMemberInviteResponse };
  "access.member.list": { request: AccessMemberListRequest; response: AccessMemberListResponse };
  "access.member.reactivate": { request: AccessMemberReactivateRequest; response: AccessMemberReactivateResponse };
  "access.member.update_role": { request: AccessMemberUpdateRoleRequest; response: AccessMemberUpdateRoleResponse };
  "access.platform_operator.read": { request: AccessPlatformOperatorReadRequest; response: AccessPlatformOperatorReadResponse };
  "access.platform_tenant.close": { request: AccessPlatformTenantCloseRequest; response: AccessPlatformTenantCloseResponse };
  "access.platform_tenant.create": { request: AccessPlatformTenantCreateRequest; response: AccessPlatformTenantCreateResponse };
  "access.platform_tenant.list": { request: AccessPlatformTenantListRequest; response: AccessPlatformTenantListResponse };
  "access.platform_tenant.reactivate": { request: AccessPlatformTenantReactivateRequest; response: AccessPlatformTenantReactivateResponse };
  "access.platform_tenant.read": { request: AccessPlatformTenantReadRequest; response: AccessPlatformTenantReadResponse };
  "access.platform_tenant.set_entitlements": { request: AccessPlatformTenantSetEntitlementsRequest; response: AccessPlatformTenantSetEntitlementsResponse };
  "access.platform_tenant.set_term": { request: AccessPlatformTenantSetTermRequest; response: AccessPlatformTenantSetTermResponse };
  "access.platform_tenant.suspend": { request: AccessPlatformTenantSuspendRequest; response: AccessPlatformTenantSuspendResponse };
  "access.preference.clear": { request: AccessPreferenceClearRequest; response: AccessPreferenceClearResponse };
  "access.preference.list": { request: AccessPreferenceListRequest; response: AccessPreferenceListResponse };
  "access.preference.save": { request: AccessPreferenceSaveRequest; response: AccessPreferenceSaveResponse };
  "access.saved_view.delete": { request: AccessSavedViewDeleteRequest; response: AccessSavedViewDeleteResponse };
  "access.saved_view.list": { request: AccessSavedViewListRequest; response: AccessSavedViewListResponse };
  "access.saved_view.save": { request: AccessSavedViewSaveRequest; response: AccessSavedViewSaveResponse };
  "candidates.candidate.create": { request: CandidatesCandidateCreateRequest; response: CandidatesCandidateCreateResponse };
  "candidates.candidate.history": { request: CandidatesCandidateHistoryRequest; response: CandidatesCandidateHistoryResponse };
  "candidates.candidate.list": { request: CandidatesCandidateListRequest; response: CandidatesCandidateListResponse };
  "candidates.candidate.overview": { request: CandidatesCandidateOverviewRequest; response: CandidatesCandidateOverviewResponse };
  "candidates.candidate.read": { request: CandidatesCandidateReadRequest; response: CandidatesCandidateReadResponse };
  "candidates.candidate.search": { request: CandidatesCandidateSearchRequest; response: CandidatesCandidateSearchResponse };
  "candidates.candidate.set_photo": { request: CandidatesCandidateSetPhotoRequest; response: CandidatesCandidateSetPhotoResponse };
  "candidates.candidate.set_reachability": { request: CandidatesCandidateSetReachabilityRequest; response: CandidatesCandidateSetReachabilityResponse };
  "candidates.candidate.update": { request: CandidatesCandidateUpdateRequest; response: CandidatesCandidateUpdateResponse };
  "candidates.candidate.update_status": { request: CandidatesCandidateUpdateStatusRequest; response: CandidatesCandidateUpdateStatusResponse };
  "candidates.copilot.ask": { request: CandidatesCopilotAskRequest; response: CandidatesCopilotAskResponse };
  "candidates.credit.balance": { request: CandidatesCreditBalanceRequest; response: CandidatesCreditBalanceResponse };
  "candidates.identity_number.read": { request: CandidatesIdentityNumberReadRequest; response: CandidatesIdentityNumberReadResponse };
  "candidates.identity_number.reveal": { request: CandidatesIdentityNumberRevealRequest; response: CandidatesIdentityNumberRevealResponse };
  "candidates.identity_number.save": { request: CandidatesIdentityNumberSaveRequest; response: CandidatesIdentityNumberSaveResponse };
  "candidates.identity_reveal.read": { request: CandidatesIdentityRevealReadRequest; response: CandidatesIdentityRevealReadResponse };
  "candidates.intelligence.generate": { request: CandidatesIntelligenceGenerateRequest; response: CandidatesIntelligenceGenerateResponse };
  "candidates.intelligence.read": { request: CandidatesIntelligenceReadRequest; response: CandidatesIntelligenceReadResponse };
  "candidates.merge_review.decide_many": { request: CandidatesMergeReviewDecideManyRequest; response: CandidatesMergeReviewDecideManyResponse };
  "candidates.merge_review.list": { request: CandidatesMergeReviewListRequest; response: CandidatesMergeReviewListResponse };
  "candidates.merge_review.merge": { request: CandidatesMergeReviewMergeRequest; response: CandidatesMergeReviewMergeResponse };
  "candidates.merge_review.reject": { request: CandidatesMergeReviewRejectRequest; response: CandidatesMergeReviewRejectResponse };
  "candidates.note.add": { request: CandidatesNoteAddRequest; response: CandidatesNoteAddResponse };
  "candidates.note.delete": { request: CandidatesNoteDeleteRequest; response: CandidatesNoteDeleteResponse };
  "candidates.note.list": { request: CandidatesNoteListRequest; response: CandidatesNoteListResponse };
  "candidates.note.update": { request: CandidatesNoteUpdateRequest; response: CandidatesNoteUpdateResponse };
  "candidates.resume.accept": { request: CandidatesResumeAcceptRequest; response: CandidatesResumeAcceptResponse };
  "candidates.resume.check_duplicate": { request: CandidatesResumeCheckDuplicateRequest; response: CandidatesResumeCheckDuplicateResponse };
  "candidates.resume.check_held": { request: CandidatesResumeCheckHeldRequest; response: CandidatesResumeCheckHeldResponse };
  "candidates.resume.discard": { request: CandidatesResumeDiscardRequest; response: CandidatesResumeDiscardResponse };
  "candidates.resume.list": { request: CandidatesResumeListRequest; response: CandidatesResumeListResponse };
  "candidates.resume.read": { request: CandidatesResumeReadRequest; response: CandidatesResumeReadResponse };
  "candidates.resume.upload": { request: CandidatesResumeUploadRequest; response: CandidatesResumeUploadResponse };
  "candidates.saved_search.delete": { request: CandidatesSavedSearchDeleteRequest; response: CandidatesSavedSearchDeleteResponse };
  "candidates.saved_search.list": { request: CandidatesSavedSearchListRequest; response: CandidatesSavedSearchListResponse };
  "candidates.saved_search.save": { request: CandidatesSavedSearchSaveRequest; response: CandidatesSavedSearchSaveResponse };
  "candidates.search.log_impressions": { request: CandidatesSearchLogImpressionsRequest; response: CandidatesSearchLogImpressionsResponse };
  "candidates.search.suggest": { request: CandidatesSearchSuggestRequest; response: CandidatesSearchSuggestResponse };
  "candidates.talent_pool.add_member": { request: CandidatesTalentPoolAddMemberRequest; response: CandidatesTalentPoolAddMemberResponse };
  "candidates.talent_pool.archive": { request: CandidatesTalentPoolArchiveRequest; response: CandidatesTalentPoolArchiveResponse };
  "candidates.talent_pool.create": { request: CandidatesTalentPoolCreateRequest; response: CandidatesTalentPoolCreateResponse };
  "candidates.talent_pool.list": { request: CandidatesTalentPoolListRequest; response: CandidatesTalentPoolListResponse };
  "candidates.talent_pool.read": { request: CandidatesTalentPoolReadRequest; response: CandidatesTalentPoolReadResponse };
  "candidates.talent_pool.remove_member": { request: CandidatesTalentPoolRemoveMemberRequest; response: CandidatesTalentPoolRemoveMemberResponse };
  "candidates.talent_pool.share": { request: CandidatesTalentPoolShareRequest; response: CandidatesTalentPoolShareResponse };
  "candidates.talent_pool.sharing": { request: CandidatesTalentPoolSharingRequest; response: CandidatesTalentPoolSharingResponse };
  "clients.client.assign": { request: ClientsClientAssignRequest; response: ClientsClientAssignResponse };
  "clients.client.create": { request: ClientsClientCreateRequest; response: ClientsClientCreateResponse };
  "clients.client.list": { request: ClientsClientListRequest; response: ClientsClientListResponse };
  "clients.client.overview": { request: ClientsClientOverviewRequest; response: ClientsClientOverviewResponse };
  "clients.client.read": { request: ClientsClientReadRequest; response: ClientsClientReadResponse };
  "clients.client.set_meeting_service": { request: ClientsClientSetMeetingServiceRequest; response: ClientsClientSetMeetingServiceResponse };
  "clients.client.set_owner": { request: ClientsClientSetOwnerRequest; response: ClientsClientSetOwnerResponse };
  "clients.client.unassign": { request: ClientsClientUnassignRequest; response: ClientsClientUnassignResponse };
  "clients.client.update_status": { request: ClientsClientUpdateStatusRequest; response: ClientsClientUpdateStatusResponse };
  "clients.closure_label.create": { request: ClientsClosureLabelCreateRequest; response: ClientsClosureLabelCreateResponse };
  "clients.closure_label.read": { request: ClientsClosureLabelReadRequest; response: ClientsClosureLabelReadResponse };
  "clients.closure_label.update": { request: ClientsClosureLabelUpdateRequest; response: ClientsClosureLabelUpdateResponse };
  "clients.commercials.read": { request: ClientsCommercialsReadRequest; response: ClientsCommercialsReadResponse };
  "clients.contact.create": { request: ClientsContactCreateRequest; response: ClientsContactCreateResponse };
  "clients.contact.remove": { request: ClientsContactRemoveRequest; response: ClientsContactRemoveResponse };
  "clients.contact.set_primary": { request: ClientsContactSetPrimaryRequest; response: ClientsContactSetPrimaryResponse };
  "clients.contact.update": { request: ClientsContactUpdateRequest; response: ClientsContactUpdateResponse };
  "clients.contract.correct": { request: ClientsContractCorrectRequest; response: ClientsContractCorrectResponse };
  "clients.contract.create": { request: ClientsContractCreateRequest; response: ClientsContractCreateResponse };
  "clients.contract.list": { request: ClientsContractListRequest; response: ClientsContractListResponse };
  "clients.copilot.ask": { request: ClientsCopilotAskRequest; response: ClientsCopilotAskResponse };
  "clients.cost_template.list": { request: ClientsCostTemplateListRequest; response: ClientsCostTemplateListResponse };
  "clients.cost_template.save": { request: ClientsCostTemplateSaveRequest; response: ClientsCostTemplateSaveResponse };
  "clients.engagement_contact.create": { request: ClientsEngagementContactCreateRequest; response: ClientsEngagementContactCreateResponse };
  "clients.engagement_contact.read": { request: ClientsEngagementContactReadRequest; response: ClientsEngagementContactReadResponse };
  "clients.engagement_contact.remove": { request: ClientsEngagementContactRemoveRequest; response: ClientsEngagementContactRemoveResponse };
  "clients.engagement_contact.update": { request: ClientsEngagementContactUpdateRequest; response: ClientsEngagementContactUpdateResponse };
  "clients.engagement_terms.set": { request: ClientsEngagementTermsSetRequest; response: ClientsEngagementTermsSetResponse };
  "clients.engagement_types.list": { request: ClientsEngagementTypesListRequest; response: ClientsEngagementTypesListResponse };
  "clients.geography.read": { request: ClientsGeographyReadRequest; response: ClientsGeographyReadResponse };
  "clients.geography.search": { request: ClientsGeographySearchRequest; response: ClientsGeographySearchResponse };
  "clients.job_assignment.remove": { request: ClientsJobAssignmentRemoveRequest; response: ClientsJobAssignmentRemoveResponse };
  "clients.job_assignment.set": { request: ClientsJobAssignmentSetRequest; response: ClientsJobAssignmentSetResponse };
  "clients.job_budget.set": { request: ClientsJobBudgetSetRequest; response: ClientsJobBudgetSetResponse };
  "clients.job_commercials.history": { request: ClientsJobCommercialsHistoryRequest; response: ClientsJobCommercialsHistoryResponse };
  "clients.job_commercials.set": { request: ClientsJobCommercialsSetRequest; response: ClientsJobCommercialsSetResponse };
  "clients.job_document.add": { request: ClientsJobDocumentAddRequest; response: ClientsJobDocumentAddResponse };
  "clients.job_document.add_link": { request: ClientsJobDocumentAddLinkRequest; response: ClientsJobDocumentAddLinkResponse };
  "clients.job_document.draft": { request: ClientsJobDocumentDraftRequest; response: ClientsJobDocumentDraftResponse };
  "clients.job_document.library": { request: ClientsJobDocumentLibraryRequest; response: ClientsJobDocumentLibraryResponse };
  "clients.job_document.list": { request: ClientsJobDocumentListRequest; response: ClientsJobDocumentListResponse };
  "clients.job_document.parse": { request: ClientsJobDocumentParseRequest; response: ClientsJobDocumentParseResponse };
  "clients.job_document.read": { request: ClientsJobDocumentReadRequest; response: ClientsJobDocumentReadResponse };
  "clients.job_document.remove": { request: ClientsJobDocumentRemoveRequest; response: ClientsJobDocumentRemoveResponse };
  "clients.job_document.rename": { request: ClientsJobDocumentRenameRequest; response: ClientsJobDocumentRenameResponse };
  "clients.job_document.reuse": { request: ClientsJobDocumentReuseRequest; response: ClientsJobDocumentReuseResponse };
  "clients.job_document.save_draft": { request: ClientsJobDocumentSaveDraftRequest; response: ClientsJobDocumentSaveDraftResponse };
  "clients.job_document.upload": { request: ClientsJobDocumentUploadRequest; response: ClientsJobDocumentUploadResponse };
  "clients.job_location.list": { request: ClientsJobLocationListRequest; response: ClientsJobLocationListResponse };
  "clients.job_location.set": { request: ClientsJobLocationSetRequest; response: ClientsJobLocationSetResponse };
  "clients.job_skill.save": { request: ClientsJobSkillSaveRequest; response: ClientsJobSkillSaveResponse };
  "clients.job.acknowledge_intake": { request: ClientsJobAcknowledgeIntakeRequest; response: ClientsJobAcknowledgeIntakeResponse };
  "clients.job.analyse_intake": { request: ClientsJobAnalyseIntakeRequest; response: ClientsJobAnalyseIntakeResponse };
  "clients.job.artifact_history": { request: ClientsJobArtifactHistoryRequest; response: ClientsJobArtifactHistoryResponse };
  "clients.job.artifacts": { request: ClientsJobArtifactsRequest; response: ClientsJobArtifactsResponse };
  "clients.job.bulk_assign": { request: ClientsJobBulkAssignRequest; response: ClientsJobBulkAssignResponse };
  "clients.job.bulk_close": { request: ClientsJobBulkCloseRequest; response: ClientsJobBulkCloseResponse };
  "clients.job.bulk_set_owner": { request: ClientsJobBulkSetOwnerRequest; response: ClientsJobBulkSetOwnerResponse };
  "clients.job.ceiling": { request: ClientsJobCeilingRequest; response: ClientsJobCeilingResponse };
  "clients.job.create": { request: ClientsJobCreateRequest; response: ClientsJobCreateResponse };
  "clients.job.disposition_flag": { request: ClientsJobDispositionFlagRequest; response: ClientsJobDispositionFlagResponse };
  "clients.job.duplicate": { request: ClientsJobDuplicateRequest; response: ClientsJobDuplicateResponse };
  "clients.job.edit_artifact": { request: ClientsJobEditArtifactRequest; response: ClientsJobEditArtifactResponse };
  "clients.job.field_assist": { request: ClientsJobFieldAssistRequest; response: ClientsJobFieldAssistResponse };
  "clients.job.field_history": { request: ClientsJobFieldHistoryRequest; response: ClientsJobFieldHistoryResponse };
  "clients.job.generate_artifact": { request: ClientsJobGenerateArtifactRequest; response: ClientsJobGenerateArtifactResponse };
  "clients.job.generate_boolean_strings": { request: ClientsJobGenerateBooleanStringsRequest; response: ClientsJobGenerateBooleanStringsResponse };
  "clients.job.intake_questions": { request: ClientsJobIntakeQuestionsRequest; response: ClientsJobIntakeQuestionsResponse };
  "clients.job.list": { request: ClientsJobListRequest; response: ClientsJobListResponse };
  "clients.job.margin": { request: ClientsJobMarginRequest; response: ClientsJobMarginResponse };
  "clients.job.overview": { request: ClientsJobOverviewRequest; response: ClientsJobOverviewResponse };
  "clients.job.preview_posting": { request: ClientsJobPreviewPostingRequest; response: ClientsJobPreviewPostingResponse };
  "clients.job.read": { request: ClientsJobReadRequest; response: ClientsJobReadResponse };
  "clients.job.refresh_posting": { request: ClientsJobRefreshPostingRequest; response: ClientsJobRefreshPostingResponse };
  "clients.job.resolve_artifact_proposal": { request: ClientsJobResolveArtifactProposalRequest; response: ClientsJobResolveArtifactProposalResponse };
  "clients.job.restore_artifact": { request: ClientsJobRestoreArtifactRequest; response: ClientsJobRestoreArtifactResponse };
  "clients.job.set_owner": { request: ClientsJobSetOwnerRequest; response: ClientsJobSetOwnerResponse };
  "clients.job.set_publish_clearance": { request: ClientsJobSetPublishClearanceRequest; response: ClientsJobSetPublishClearanceResponse };
  "clients.job.update": { request: ClientsJobUpdateRequest; response: ClientsJobUpdateResponse };
  "clients.job.update_status": { request: ClientsJobUpdateStatusRequest; response: ClientsJobUpdateStatusResponse };
  "clients.rate_card.correct": { request: ClientsRateCardCorrectRequest; response: ClientsRateCardCorrectResponse };
  "clients.rate_card.create": { request: ClientsRateCardCreateRequest; response: ClientsRateCardCreateResponse };
  "clients.rate_card.retire": { request: ClientsRateCardRetireRequest; response: ClientsRateCardRetireResponse };
  "clients.rate_card.update": { request: ClientsRateCardUpdateRequest; response: ClientsRateCardUpdateResponse };
  "clients.sla.list": { request: ClientsSlaListRequest; response: ClientsSlaListResponse };
  "clients.sla.set": { request: ClientsSlaSetRequest; response: ClientsSlaSetResponse };
  "clients.time_to_fill.read": { request: ClientsTimeToFillReadRequest; response: ClientsTimeToFillReadResponse };
  "clients.work_location.create": { request: ClientsWorkLocationCreateRequest; response: ClientsWorkLocationCreateResponse };
  "clients.work_location.list": { request: ClientsWorkLocationListRequest; response: ClientsWorkLocationListResponse };
  "clients.work_location.retire": { request: ClientsWorkLocationRetireRequest; response: ClientsWorkLocationRetireResponse };
  "copilot.ai_settings.read": { request: CopilotAiSettingsReadRequest; response: CopilotAiSettingsReadResponse };
  "copilot.ai_settings.update": { request: CopilotAiSettingsUpdateRequest; response: CopilotAiSettingsUpdateResponse };
  "copilot.candidate_decision.read": { request: CopilotCandidateDecisionReadRequest; response: CopilotCandidateDecisionReadResponse };
  "copilot.job_decision.read": { request: CopilotJobDecisionReadRequest; response: CopilotJobDecisionReadResponse };
  "copilot.platform_ai_config.read": { request: CopilotPlatformAiConfigReadRequest; response: CopilotPlatformAiConfigReadResponse };
  "copilot.platform_ai_connection.check": { request: CopilotPlatformAiConnectionCheckRequest; response: CopilotPlatformAiConnectionCheckResponse };
  "copilot.platform_ai_credential.clear": { request: CopilotPlatformAiCredentialClearRequest; response: CopilotPlatformAiCredentialClearResponse };
  "copilot.platform_ai_credential.read": { request: CopilotPlatformAiCredentialReadRequest; response: CopilotPlatformAiCredentialReadResponse };
  "copilot.platform_ai_credential.save": { request: CopilotPlatformAiCredentialSaveRequest; response: CopilotPlatformAiCredentialSaveResponse };
  "copilot.platform_ai_model.list": { request: CopilotPlatformAiModelListRequest; response: CopilotPlatformAiModelListResponse };
  "copilot.platform_ai_price.create": { request: CopilotPlatformAiPriceCreateRequest; response: CopilotPlatformAiPriceCreateResponse };
  "copilot.platform_ai_route.upsert": { request: CopilotPlatformAiRouteUpsertRequest; response: CopilotPlatformAiRouteUpsertResponse };
  "copilot.platform_ai_usage.read": { request: CopilotPlatformAiUsageReadRequest; response: CopilotPlatformAiUsageReadResponse };
  "copilot.resume_parse.read": { request: CopilotResumeParseReadRequest; response: CopilotResumeParseReadResponse };
  "copilot.resume_parse.record_review": { request: CopilotResumeParseRecordReviewRequest; response: CopilotResumeParseRecordReviewResponse };
  "identity.auth.password": { request: IdentityAuthPasswordRequest; response: IdentityAuthPasswordResponse };
  "identity.auth.refresh": { request: IdentityAuthRefreshRequest; response: IdentityAuthRefreshResponse };
  "identity.auth.reset_confirm": { request: IdentityAuthResetConfirmRequest; response: IdentityAuthResetConfirmResponse };
  "identity.auth.reset_request": { request: IdentityAuthResetRequestRequest; response: IdentityAuthResetRequestResponse };
  "identity.auth.sign_out": { request: IdentityAuthSignOutRequest; response: IdentityAuthSignOutResponse };
  "identity.invitation.activate": { request: IdentityInvitationActivateRequest; response: IdentityInvitationActivateResponse };
  "identity.platform_invitation.list": { request: IdentityPlatformInvitationListRequest; response: IdentityPlatformInvitationListResponse };
  "identity.portal.applications": { request: IdentityPortalApplicationsRequest; response: IdentityPortalApplicationsResponse };
  "identity.portal.apply": { request: IdentityPortalApplyRequest; response: IdentityPortalApplyResponse };
  "identity.portal.start": { request: IdentityPortalStartRequest; response: IdentityPortalStartResponse };
  "identity.portal.verify": { request: IdentityPortalVerifyRequest; response: IdentityPortalVerifyResponse };
  "insight.activity.list": { request: InsightActivityListRequest; response: InsightActivityListResponse };
  "insight.attention.read": { request: InsightAttentionReadRequest; response: InsightAttentionReadResponse };
  "insight.blocked_by_owner.read": { request: InsightBlockedByOwnerReadRequest; response: InsightBlockedByOwnerReadResponse };
  "insight.blocked.read": { request: InsightBlockedReadRequest; response: InsightBlockedReadResponse };
  "insight.changes.read": { request: InsightChangesReadRequest; response: InsightChangesReadResponse };
  "insight.client_responsiveness.read": { request: InsightClientResponsivenessReadRequest; response: InsightClientResponsivenessReadResponse };
  "insight.commitment.create": { request: InsightCommitmentCreateRequest; response: InsightCommitmentCreateResponse };
  "insight.commitment.delete": { request: InsightCommitmentDeleteRequest; response: InsightCommitmentDeleteResponse };
  "insight.commitment.list": { request: InsightCommitmentListRequest; response: InsightCommitmentListResponse };
  "insight.commitment.roll_forward": { request: InsightCommitmentRollForwardRequest; response: InsightCommitmentRollForwardResponse };
  "insight.commitment.update": { request: InsightCommitmentUpdateRequest; response: InsightCommitmentUpdateResponse };
  "insight.conversion.read": { request: InsightConversionReadRequest; response: InsightConversionReadResponse };
  "insight.copilot.ask": { request: InsightCopilotAskRequest; response: InsightCopilotAskResponse };
  "insight.copilot.ask_book": { request: InsightCopilotAskBookRequest; response: InsightCopilotAskBookResponse };
  "insight.dashboard.read": { request: InsightDashboardReadRequest; response: InsightDashboardReadResponse };
  "insight.digest.read": { request: InsightDigestReadRequest; response: InsightDigestReadResponse };
  "insight.effort_board.read": { request: InsightEffortBoardReadRequest; response: InsightEffortBoardReadResponse };
  "insight.job_board.read": { request: InsightJobBoardReadRequest; response: InsightJobBoardReadResponse };
  "insight.job_effort.read": { request: InsightJobEffortReadRequest; response: InsightJobEffortReadResponse };
  "insight.standup.desk": { request: InsightStandupDeskRequest; response: InsightStandupDeskResponse };
  "insight.time_leak.read": { request: InsightTimeLeakReadRequest; response: InsightTimeLeakReadResponse };
  "insight.today.read": { request: InsightTodayReadRequest; response: InsightTodayReadResponse };
  "outreach.email_sender.list": { request: OutreachEmailSenderListRequest; response: OutreachEmailSenderListResponse };
  "outreach.email_sender.save": { request: OutreachEmailSenderSaveRequest; response: OutreachEmailSenderSaveResponse };
  "outreach.email_sender.set_status": { request: OutreachEmailSenderSetStatusRequest; response: OutreachEmailSenderSetStatusResponse };
  "outreach.email_sender.verify": { request: OutreachEmailSenderVerifyRequest; response: OutreachEmailSenderVerifyResponse };
  "outreach.interview_invitation.list": { request: OutreachInterviewInvitationListRequest; response: OutreachInterviewInvitationListResponse };
  "outreach.message_setting.list": { request: OutreachMessageSettingListRequest; response: OutreachMessageSettingListResponse };
  "outreach.message_setting.set": { request: OutreachMessageSettingSetRequest; response: OutreachMessageSettingSetResponse };
  "outreach.message_template.list": { request: OutreachMessageTemplateListRequest; response: OutreachMessageTemplateListResponse };
  "outreach.message_template.read": { request: OutreachMessageTemplateReadRequest; response: OutreachMessageTemplateReadResponse };
  "outreach.message_template.revert": { request: OutreachMessageTemplateRevertRequest; response: OutreachMessageTemplateRevertResponse };
  "outreach.message_template.save": { request: OutreachMessageTemplateSaveRequest; response: OutreachMessageTemplateSaveResponse };
  "outreach.message_template.set_sender": { request: OutreachMessageTemplateSetSenderRequest; response: OutreachMessageTemplateSetSenderResponse };
  "outreach.message_template.test_send": { request: OutreachMessageTemplateTestSendRequest; response: OutreachMessageTemplateTestSendResponse };
  "outreach.message.draft": { request: OutreachMessageDraftRequest; response: OutreachMessageDraftResponse };
  "outreach.message.list": { request: OutreachMessageListRequest; response: OutreachMessageListResponse };
  "outreach.message.retry": { request: OutreachMessageRetryRequest; response: OutreachMessageRetryResponse };
  "outreach.message.send": { request: OutreachMessageSendRequest; response: OutreachMessageSendResponse };
  "outreach.platform_email_sender.delete": { request: OutreachPlatformEmailSenderDeleteRequest; response: OutreachPlatformEmailSenderDeleteResponse };
  "outreach.platform_email_sender.read": { request: OutreachPlatformEmailSenderReadRequest; response: OutreachPlatformEmailSenderReadResponse };
  "outreach.platform_email_sender.save": { request: OutreachPlatformEmailSenderSaveRequest; response: OutreachPlatformEmailSenderSaveResponse };
  "outreach.platform_email_sender.verify": { request: OutreachPlatformEmailSenderVerifyRequest; response: OutreachPlatformEmailSenderVerifyResponse };
  "outreach.platform_message.read": { request: OutreachPlatformMessageReadRequest; response: OutreachPlatformMessageReadResponse };
  "outreach.stage_message.list": { request: OutreachStageMessageListRequest; response: OutreachStageMessageListResponse };
  "outreach.stage_message.save": { request: OutreachStageMessageSaveRequest; response: OutreachStageMessageSaveResponse };
  "pipeline.application_answer.list": { request: PipelineApplicationAnswerListRequest; response: PipelineApplicationAnswerListResponse };
  "pipeline.application_answer.save": { request: PipelineApplicationAnswerSaveRequest; response: PipelineApplicationAnswerSaveResponse };
  "pipeline.application.block": { request: PipelineApplicationBlockRequest; response: PipelineApplicationBlockResponse };
  "pipeline.application.bulk_change_stage": { request: PipelineApplicationBulkChangeStageRequest; response: PipelineApplicationBulkChangeStageResponse };
  "pipeline.application.bulk_close": { request: PipelineApplicationBulkCloseRequest; response: PipelineApplicationBulkCloseResponse };
  "pipeline.application.change_stage": { request: PipelineApplicationChangeStageRequest; response: PipelineApplicationChangeStageResponse };
  "pipeline.application.close": { request: PipelineApplicationCloseRequest; response: PipelineApplicationCloseResponse };
  "pipeline.application.create": { request: PipelineApplicationCreateRequest; response: PipelineApplicationCreateResponse };
  "pipeline.application.list": { request: PipelineApplicationListRequest; response: PipelineApplicationListResponse };
  "pipeline.application.read": { request: PipelineApplicationReadRequest; response: PipelineApplicationReadResponse };
  "pipeline.application.revive": { request: PipelineApplicationReviveRequest; response: PipelineApplicationReviveResponse };
  "pipeline.application.unblock": { request: PipelineApplicationUnblockRequest; response: PipelineApplicationUnblockResponse };
  "pipeline.availability.book": { request: PipelineAvailabilityBookRequest; response: PipelineAvailabilityBookResponse };
  "pipeline.availability.list": { request: PipelineAvailabilityListRequest; response: PipelineAvailabilityListResponse };
  "pipeline.availability.record": { request: PipelineAvailabilityRecordRequest; response: PipelineAvailabilityRecordResponse };
  "pipeline.availability.withdraw": { request: PipelineAvailabilityWithdrawRequest; response: PipelineAvailabilityWithdrawResponse };
  "pipeline.compensation.compute": { request: PipelineCompensationComputeRequest; response: PipelineCompensationComputeResponse };
  "pipeline.copilot.ask": { request: PipelineCopilotAskRequest; response: PipelineCopilotAskResponse };
  "pipeline.document_clause.read": { request: PipelineDocumentClauseReadRequest; response: PipelineDocumentClauseReadResponse };
  "pipeline.document_clause.save": { request: PipelineDocumentClauseSaveRequest; response: PipelineDocumentClauseSaveResponse };
  "pipeline.document_link.open": { request: PipelineDocumentLinkOpenRequest; response: PipelineDocumentLinkOpenResponse };
  "pipeline.document_link.submit": { request: PipelineDocumentLinkSubmitRequest; response: PipelineDocumentLinkSubmitResponse };
  "pipeline.document_request.create": { request: PipelineDocumentRequestCreateRequest; response: PipelineDocumentRequestCreateResponse };
  "pipeline.document_request.read": { request: PipelineDocumentRequestReadRequest; response: PipelineDocumentRequestReadResponse };
  "pipeline.document_request.revoke": { request: PipelineDocumentRequestRevokeRequest; response: PipelineDocumentRequestRevokeResponse };
  "pipeline.document_set.read": { request: PipelineDocumentSetReadRequest; response: PipelineDocumentSetReadResponse };
  "pipeline.document_set.save": { request: PipelineDocumentSetSaveRequest; response: PipelineDocumentSetSaveResponse };
  "pipeline.document_submission.review": { request: PipelineDocumentSubmissionReviewRequest; response: PipelineDocumentSubmissionReviewResponse };
  "pipeline.document_template.read": { request: PipelineDocumentTemplateReadRequest; response: PipelineDocumentTemplateReadResponse };
  "pipeline.document_template.save": { request: PipelineDocumentTemplateSaveRequest; response: PipelineDocumentTemplateSaveResponse };
  "pipeline.interview_note.add": { request: PipelineInterviewNoteAddRequest; response: PipelineInterviewNoteAddResponse };
  "pipeline.interview_note.list": { request: PipelineInterviewNoteListRequest; response: PipelineInterviewNoteListResponse };
  "pipeline.interview_plan.read": { request: PipelineInterviewPlanReadRequest; response: PipelineInterviewPlanReadResponse };
  "pipeline.interview_plan.reset_job": { request: PipelineInterviewPlanResetJobRequest; response: PipelineInterviewPlanResetJobResponse };
  "pipeline.interview_plan.save_client": { request: PipelineInterviewPlanSaveClientRequest; response: PipelineInterviewPlanSaveClientResponse };
  "pipeline.interview_plan.save_default": { request: PipelineInterviewPlanSaveDefaultRequest; response: PipelineInterviewPlanSaveDefaultResponse };
  "pipeline.interview_plan.save_job": { request: PipelineInterviewPlanSaveJobRequest; response: PipelineInterviewPlanSaveJobResponse };
  "pipeline.interview.clashes": { request: PipelineInterviewClashesRequest; response: PipelineInterviewClashesResponse };
  "pipeline.interview.client_view": { request: PipelineInterviewClientViewRequest; response: PipelineInterviewClientViewResponse };
  "pipeline.interview.history": { request: PipelineInterviewHistoryRequest; response: PipelineInterviewHistoryResponse };
  "pipeline.interview.list": { request: PipelineInterviewListRequest; response: PipelineInterviewListResponse };
  "pipeline.interview.provision_meeting": { request: PipelineInterviewProvisionMeetingRequest; response: PipelineInterviewProvisionMeetingResponse };
  "pipeline.interview.read": { request: PipelineInterviewReadRequest; response: PipelineInterviewReadResponse };
  "pipeline.interview.record_confirmation": { request: PipelineInterviewRecordConfirmationRequest; response: PipelineInterviewRecordConfirmationResponse };
  "pipeline.interview.record_past": { request: PipelineInterviewRecordPastRequest; response: PipelineInterviewRecordPastResponse };
  "pipeline.interview.remove": { request: PipelineInterviewRemoveRequest; response: PipelineInterviewRemoveResponse };
  "pipeline.interview.schedule": { request: PipelineInterviewScheduleRequest; response: PipelineInterviewScheduleResponse };
  "pipeline.interview.seed_from_plan": { request: PipelineInterviewSeedFromPlanRequest; response: PipelineInterviewSeedFromPlanResponse };
  "pipeline.interview.send_invitation": { request: PipelineInterviewSendInvitationRequest; response: PipelineInterviewSendInvitationResponse };
  "pipeline.interview.set_outcome": { request: PipelineInterviewSetOutcomeRequest; response: PipelineInterviewSetOutcomeResponse };
  "pipeline.interview.set_owner": { request: PipelineInterviewSetOwnerRequest; response: PipelineInterviewSetOwnerResponse };
  "pipeline.interview.update": { request: PipelineInterviewUpdateRequest; response: PipelineInterviewUpdateResponse };
  "pipeline.job_panel.add": { request: PipelineJobPanelAddRequest; response: PipelineJobPanelAddResponse };
  "pipeline.job_panel.list": { request: PipelineJobPanelListRequest; response: PipelineJobPanelListResponse };
  "pipeline.job_panel.remove": { request: PipelineJobPanelRemoveRequest; response: PipelineJobPanelRemoveResponse };
  "pipeline.job_question.list": { request: PipelineJobQuestionListRequest; response: PipelineJobQuestionListResponse };
  "pipeline.job_question.save": { request: PipelineJobQuestionSaveRequest; response: PipelineJobQuestionSaveResponse };
  "pipeline.joining_touchpoint.record": { request: PipelineJoiningTouchpointRecordRequest; response: PipelineJoiningTouchpointRecordResponse };
  "pipeline.joining_touchpoint.schedule": { request: PipelineJoiningTouchpointScheduleRequest; response: PipelineJoiningTouchpointScheduleResponse };
  "pipeline.joining.dropped": { request: PipelineJoiningDroppedRequest; response: PipelineJoiningDroppedResponse };
  "pipeline.joining.joined": { request: PipelineJoiningJoinedRequest; response: PipelineJoiningJoinedResponse };
  "pipeline.joining.list": { request: PipelineJoiningListRequest; response: PipelineJoiningListResponse };
  "pipeline.joining.read": { request: PipelineJoiningReadRequest; response: PipelineJoiningReadResponse };
  "pipeline.joining.update": { request: PipelineJoiningUpdateRequest; response: PipelineJoiningUpdateResponse };
  "pipeline.match_settings.read": { request: PipelineMatchSettingsReadRequest; response: PipelineMatchSettingsReadResponse };
  "pipeline.match_settings.save": { request: PipelineMatchSettingsSaveRequest; response: PipelineMatchSettingsSaveResponse };
  "pipeline.meeting_application.remove": { request: PipelineMeetingApplicationRemoveRequest; response: PipelineMeetingApplicationRemoveResponse };
  "pipeline.meeting_application.save": { request: PipelineMeetingApplicationSaveRequest; response: PipelineMeetingApplicationSaveResponse };
  "pipeline.meeting_connection.disconnect": { request: PipelineMeetingConnectionDisconnectRequest; response: PipelineMeetingConnectionDisconnectResponse };
  "pipeline.meeting_connection.finish": { request: PipelineMeetingConnectionFinishRequest; response: PipelineMeetingConnectionFinishResponse };
  "pipeline.meeting_connection.list": { request: PipelineMeetingConnectionListRequest; response: PipelineMeetingConnectionListResponse };
  "pipeline.meeting_connection.start": { request: PipelineMeetingConnectionStartRequest; response: PipelineMeetingConnectionStartResponse };
  "pipeline.meeting_connection.verify": { request: PipelineMeetingConnectionVerifyRequest; response: PipelineMeetingConnectionVerifyResponse };
  "pipeline.offer_document.generate": { request: PipelineOfferDocumentGenerateRequest; response: PipelineOfferDocumentGenerateResponse };
  "pipeline.offer_document.issue": { request: PipelineOfferDocumentIssueRequest; response: PipelineOfferDocumentIssueResponse };
  "pipeline.offer_document.read": { request: PipelineOfferDocumentReadRequest; response: PipelineOfferDocumentReadResponse };
  "pipeline.offer_document.sign": { request: PipelineOfferDocumentSignRequest; response: PipelineOfferDocumentSignResponse };
  "pipeline.offer_scenario.counter": { request: PipelineOfferScenarioCounterRequest; response: PipelineOfferScenarioCounterResponse };
  "pipeline.offer_scenario.create": { request: PipelineOfferScenarioCreateRequest; response: PipelineOfferScenarioCreateResponse };
  "pipeline.offer_scenario.promote": { request: PipelineOfferScenarioPromoteRequest; response: PipelineOfferScenarioPromoteResponse };
  "pipeline.offer_scenario.read": { request: PipelineOfferScenarioReadRequest; response: PipelineOfferScenarioReadResponse };
  "pipeline.offer_scenario.share": { request: PipelineOfferScenarioShareRequest; response: PipelineOfferScenarioShareResponse };
  "pipeline.offer_scenario.update": { request: PipelineOfferScenarioUpdateRequest; response: PipelineOfferScenarioUpdateResponse };
  "pipeline.offer_settings.components": { request: PipelineOfferSettingsComponentsRequest; response: PipelineOfferSettingsComponentsResponse };
  "pipeline.offer_settings.read": { request: PipelineOfferSettingsReadRequest; response: PipelineOfferSettingsReadResponse };
  "pipeline.offer_settings.update": { request: PipelineOfferSettingsUpdateRequest; response: PipelineOfferSettingsUpdateResponse };
  "pipeline.offer.approve": { request: PipelineOfferApproveRequest; response: PipelineOfferApproveResponse };
  "pipeline.offer.create": { request: PipelineOfferCreateRequest; response: PipelineOfferCreateResponse };
  "pipeline.offer.joining": { request: PipelineOfferJoiningRequest; response: PipelineOfferJoiningResponse };
  "pipeline.offer.list": { request: PipelineOfferListRequest; response: PipelineOfferListResponse };
  "pipeline.offer.read": { request: PipelineOfferReadRequest; response: PipelineOfferReadResponse };
  "pipeline.offer.release": { request: PipelineOfferReleaseRequest; response: PipelineOfferReleaseResponse };
  "pipeline.offer.respond": { request: PipelineOfferRespondRequest; response: PipelineOfferRespondResponse };
  "pipeline.offer.update": { request: PipelineOfferUpdateRequest; response: PipelineOfferUpdateResponse };
  "pipeline.offer.withdraw": { request: PipelineOfferWithdrawRequest; response: PipelineOfferWithdrawResponse };
  "pipeline.setup.create_defaults": { request: PipelineSetupCreateDefaultsRequest; response: PipelineSetupCreateDefaultsResponse };
  "pipeline.stage_template.read_default": { request: PipelineStageTemplateReadDefaultRequest; response: PipelineStageTemplateReadDefaultResponse };
  "pipeline.submission.create": { request: PipelineSubmissionCreateRequest; response: PipelineSubmissionCreateResponse };
  "pipeline.submission.list": { request: PipelineSubmissionListRequest; response: PipelineSubmissionListResponse };
  "pipeline.submission.record_response": { request: PipelineSubmissionRecordResponseRequest; response: PipelineSubmissionRecordResponseResponse };
  "pipeline.taxonomy.builder_read": { request: PipelineTaxonomyBuilderReadRequest; response: PipelineTaxonomyBuilderReadResponse };
  "pipeline.taxonomy.create": { request: PipelineTaxonomyCreateRequest; response: PipelineTaxonomyCreateResponse };
  "pipeline.taxonomy.list": { request: PipelineTaxonomyListRequest; response: PipelineTaxonomyListResponse };
  "pipeline.taxonomy.update": { request: PipelineTaxonomyUpdateRequest; response: PipelineTaxonomyUpdateResponse };
}
